// lib/model-registry.ts
import { isModelInFamilies, isWithheldAsUnrunnable, unrunnableModelFamilies } from '#lib/access-suspension';
import { MAI_IMAGE_MODELS } from '#lib/maiImage';
import { STUDIO_MODEL_FAMILIES, studioModelFamilyOf } from '#lib/studioModelFamilies';
import type { LatestVideoParams, ProjectType } from '#types/types';
import { GPT_IMAGE_25_CANVAS_REFERENCE_MAX, getGptImage25QualityCredits } from '#lib/gpt-image-25';
import { resolveGrokImagine2Resolution } from '#lib/grok-imagine-2';
import { MUSE_IMAGE_MAX_INPUT_IMAGES } from '#lib/muse-image';
import { estimateLatestVideoCredits, LATEST_VIDEO_MODEL_IDS, LATEST_VIDEO_MODELS, type LatestVideoModelId } from '#lib/latest-video-models';
import { FLUX_3_IMAGE_MAX_INPUT_IMAGES, FLUX_3_IMAGE_MAX_INPUT_PIXELS, FLUX_3_IMAGE_RESOLUTION_CREDITS, resolveFlux3ImageResolution } from '#lib/flux-3-image';
import { FLUX_3_KLEIN_MAX_INPUT_IMAGES, FLUX_3_KLEIN_MAX_INPUT_PIXELS, FLUX_3_KLEIN_RESOLUTION_CREDITS, resolveFlux3KleinResolution } from '#lib/flux-3-klein';
import { resolveQwenImage3Resolution } from '#lib/qwen-image-3';
import { normalizeSeedVR2OutputMegaPixels } from '#lib/seedvr2';
import { QWEN_IMAGE_21_MAX_REFERENCES, QWEN_IMAGE_21_REFERENCE_MAX_PIXELS } from '#lib/qwen-image-21';
import { IDEOGRAM_VECTOR_QUALITY_CREDITS, arrowReservedCredits, normalizeIdeogramVectorQuality } from '#lib/vector-models';
import { CHEAP_TEXT_MODEL, MAIN_TEXT_MODEL } from '#lib/gptBackend';
export type StudioModelCategory = 'Generate' | 'Edit' | 'Video';
export type StudioModelSubCategory =
  | 'Basic'
  | 'Controlled'
  | 'Special'
  | 'Masked'
  | 'Full Area'
  | 'Generate'
  | 'Edit';

type StudioTaskRoute = 'diffuse' | 'film';
export type UserAccessScopeLike = 'ALL_MODELS' | 'CHEAP_MODELS_ONLY' | null | undefined;
export type ModelVisibilityPreference = 0 | 1 | 2 | undefined;

type BillingEstimatorId =
  | 'flat'
  | 'nano-banana-pro-image-size'
  | 'nano-banana-2-image-size'
  | 'nano-banana-2.1-image-size'
  | 'grok-imagine-2-resolution'
  | 'qwen-image-3-resolution'
  | 'flux-3-image-resolution'
  | 'flux-3-klein-resolution'
  | 'kling-3'
  | 'kling-3-omni'
  | 'seedance-2.0'
  | 'gemini-omni-flash-preview'
  | 'topaz-video-upscale'
  | 'mirelo-sfx-1.6'
  | 'magnific-video-upscale'
  | 'wan-2.2-fast'
  | 'wan-2.2-i2v'
  | 'hailuo-02-fast'
  | 'seedance-2.0-fast'
  | 'seedance-2.5'
  | 'flux-3'
  | 'flux-3-draft'
  | 'veo-3.1-fast'
  | 'kling-2.5-turbo'
  | 'ltx-2.3-fast'
  | 'luma-ray-2-flash'
  | 'minimax-h3-max'
  | 'minimax-h3-max-turbo'
  | 'wan-3.0-prime'
  | 'grok-imagine-video-1.5'
  | 'kling-3-turbo'
  | 'flux-3-max'
  | 'wan-3.0'
  | 'ltx-2.5-fast'
  | 'luma-ray-3.2'
  | 'gpt-image-2.5-quality'
  | 'ideogram-vector-1-quality'
  | 'seedvr2-output-megapixels';

type StudioModelComparisonGrade = 'better' | 'similar' | 'worse' | 'unknown';
type StudioModelRelativeSpeed = 'faster' | 'similar' | 'slower' | 'unknown';
type StudioModelReferenceSupport = 'none' | 'limited' | 'full' | 'unknown';
type StudioModelLifecycle = 'flagship' | 'active' | 'legacy' | 'experimental';

interface StudioModelComparisonProfile {
  lifecycle: StudioModelLifecycle;
  flagshipId?: string;
  positioning: string;
  instructionFidelityVsFlagship?: StudioModelComparisonGrade;
  visualQualityVsFlagship?: StudioModelComparisonGrade;
  speedVsFlagship?: StudioModelRelativeSpeed;
  costVsFlagship?: StudioModelComparisonGrade;
  additionalReferencesVsFlagship?: StudioModelReferenceSupport;
  webReferenceSearch?: 'yes' | 'no' | 'optional' | 'unknown';
  bestFor?: string[];
  avoidFor?: string[];
  knownLimitations?: string[];
}

export const FLAGSHIP_STUDIO_MODEL_ID = 'nano-banana-2';

interface ModelBillingDefinition {
  estimator: BillingEstimatorId;
  defaultCredits: number;
  notes?: string;
}

interface StudioModelDefinition {
  id: string;
  name: string;
  provider: string;
  hosting?: 'fal';
  taskRoute: StudioTaskRoute;
  supportedProjectTypes?: readonly ProjectType[];
  /** The provider returns SVG, which lands as a vector layer, never a raster. */
  outputFormat?: 'svg';
  uiCategory?: StudioModelCategory;
  uiSubCategory?: StudioModelSubCategory;
  showInModelSelector: boolean;
  defaultVisible: boolean;
  cheapEligible: boolean;
  instructionModel: boolean;
  powerHint?: string;
  capabilities: {
    supportsPrompt: boolean;
    requiresPrompt: boolean;
    supportsNegativePrompt: boolean;
    supportsSeed: boolean;
    supportsMask: boolean;
    supportsAspectRatio: boolean;
    supportsMainImageInput: boolean;
    supportsReferenceImages: boolean;
    referenceImagesMin: number;
    referenceImagesMax: number;
    // Each reference is fitted into it before upload; absent where the provider documents no per-image limit.
    referenceImageBudget?: { maxPixels: number; minSide?: number };
    supportsAnnotationGuide: boolean;
    supportsEndFrame: boolean;
    supportsVideoInput: boolean;
  };
  minimumGenerations?: number;
  maximumGenerations?: number;
  averageGenerationSeconds?: number;
  comparisonProfile?: StudioModelComparisonProfile;
  billing: ModelBillingDefinition;
  notes?: string;
}

export interface AgentModelDefinition {
  id: string;
  name: string;
  description: string;
  provider: 'openai' | 'gemini' | 'anthropic' | 'moonshot' | 'meta';
  modelType: 'gemini' | 'gpt-azure' | 'anthropic' | 'kimi' | 'openrouter';
  availableInUi: boolean;
  cheapEligible: boolean;
  faterOrgOnly?: boolean;
  billing: ModelBillingDefinition;
  fallback?: string;
}

type StudioModelInput = Omit<StudioModelDefinition, 'id'>;
const defineStudioModels = <T extends Record<string, StudioModelInput>>(
  models: T,
): Record<keyof T, StudioModelInput> => models;

const readNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const readString = (value: unknown): string | null => {
  return typeof value === 'string' ? value : null;
};

type CostEstimatorContext = {
  videoDurationSeconds?: number;
  organisationId?: string | null;
  /** A video run that animates a start image. Only per-clip models price on it (Luma Ray 3.2):
   *  unknown, they quote the text-to-video price, the higher one. */
  startsFromImage?: boolean;
};

export const resolveNanoBanana2ImageSize = (value: unknown) => {
  const size = typeof value === 'string' ? value.trim().toUpperCase() : value;
  switch (size) {
    case undefined:
    case null:
    case '':
      return '2K';
    case '1K':
    case '2K':
    case '4K':
      return size;
    default:
      return null;
  }
};

const estimateCostFromEstimator = (
  estimator: BillingEstimatorId,
  params: Record<string, unknown> | null | undefined,
  context: CostEstimatorContext | undefined,
  fallback: number,
): number | null => {
  switch (estimator) {
    case 'flat':
      return fallback;
    case 'nano-banana-pro-image-size': {
      const imageSize = readString(params?.imageSize);
      return imageSize === '4K' ? 4 : 2;
    }
    case 'nano-banana-2-image-size':
    case 'nano-banana-2.1-image-size': {
      const imageSize = resolveNanoBanana2ImageSize(params?.imageSize);
      if (!context?.organisationId || !imageSize) return null;
      if (imageSize === '2K') return estimator === 'nano-banana-2.1-image-size' ? 1.4 : 1.5;
      return imageSize === '1K' ? 1 : 2;
    }
    case 'grok-imagine-2-resolution': {
      const resolution = resolveGrokImagine2Resolution(params?.resolution);
      if (!context?.organisationId || !resolution) return null;
      return resolution === '1K' ? 1 : 1.5;
    }
    case 'qwen-image-3-resolution': {
      const resolution = resolveQwenImage3Resolution(params?.resolution);
      if (!context?.organisationId || !resolution) return null;
      return resolution === '1K' ? 1 : 1.5;
    }
    case 'flux-3-image-resolution': {
      const resolution = resolveFlux3ImageResolution(params?.resolution);
      if (!context?.organisationId || !resolution) return null;
      return FLUX_3_IMAGE_RESOLUTION_CREDITS[resolution];
    }
    case 'flux-3-klein-resolution': {
      const resolution = resolveFlux3KleinResolution(params?.resolution);
      if (!context?.organisationId || !resolution) return null;
      return FLUX_3_KLEIN_RESOLUTION_CREDITS[resolution];
    }
    case 'seedvr2-output-megapixels': {
      const outputMegaPixels = normalizeSeedVR2OutputMegaPixels(params?.outputMegaPixels);
      return 1 + Math.max(0, outputMegaPixels - 16) / 4 * 0.25;
    }
    case 'gpt-image-2.5-quality':
      return getGptImage25QualityCredits(params?.quality);
    case 'ideogram-vector-1-quality':
      return IDEOGRAM_VECTOR_QUALITY_CREDITS[normalizeIdeogramVectorQuality(params?.quality)];
    case 'kling-3': {
      const duration = readNumber(params?.duration) ?? 5;
      const resolution = readString(params?.resolution) ?? '1080p';
      const generateAudio = params?.generate_audio === true;
      const baseDurationCost = Math.max(1, Math.ceil(duration));
      let calculatedCost = baseDurationCost;
      if (resolution === '1080p') calculatedCost *= 2;
      if (generateAudio) calculatedCost *= 2;
      return calculatedCost;
    }
    case 'kling-3-omni': {
      const duration = readNumber(params?.duration) ?? 5;
      const resolution = readString(params?.resolution) ?? '1080p';
      const generateAudio = params?.generate_audio === true;
      const baseDurationCost = Math.max(1, Math.ceil(duration));
      let calculatedCost = baseDurationCost;
      if (resolution === '1080p') calculatedCost *= 2;
      if (generateAudio) calculatedCost *= 2;
      return calculatedCost;
    }
    case 'seedance-2.0': {
      const requestedDuration = readNumber(params?.duration);
      const duration = requestedDuration === -1 ? 5 : (requestedDuration ?? 5);
      const clampedDuration = Math.max(1, Math.min(15, duration));
      const resolution = readString(params?.resolution) ?? '720p';
      // Seedance API cost is ~3-6x other video models per second; charge 4 credits/sec base
      // so the 720p/5s default lands at 20 credits (~86% margin at €0.50/credit).
      // fal's '4k' tier bills 2x the 1080p rate, so 4k = base * 4 (1080p is base * 2).
      const baseDurationCost = 4 * Math.max(1, Math.ceil(clampedDuration));
      let calculatedCost = baseDurationCost;
      if (resolution === '4k') {
        calculatedCost *= 4;
      } else if (resolution === '1080p') {
        calculatedCost *= 2;
      } else if (resolution === '480p') {
        calculatedCost = Math.max(1, Math.round(calculatedCost * 0.75));
      }
      return calculatedCost;
    }
    case 'gemini-omni-flash-preview': {
      const duration = readNumber(params?.duration) ?? 5;
      const clampedDuration = Math.max(3, Math.min(10, duration));
      return Math.max(1, Math.ceil(clampedDuration));
    }
    case 'mirelo-sfx-1.6': {
      const startMs = readNumber(params?.video_input_start_ms) ?? 0;
      const endMs = readNumber(params?.video_input_end_ms);
      const explicitRangeDuration = typeof endMs === 'number' && endMs > startMs
        ? (endMs - startMs) / 1000
        : null;
      const selectedDuration = context?.videoDurationSeconds ?? explicitRangeDuration ?? 10;
      const clampedDuration = Math.max(1, Math.min(60, selectedDuration));
      // fal currently charges $0.01 per generated second. One Fater credit per
      // started second keeps billing deterministic for timeline-selected spans.
      return Math.max(1, Math.ceil(clampedDuration));
    }
    case 'topaz-video-upscale': {
      const upscaleFactor = readNumber(params?.upscale_factor) ?? 2;
      const targetFps = readNumber(params?.target_fps);
      let calculatedCost = 2;
      if (upscaleFactor > 2.5) calculatedCost += 2;
      else if (upscaleFactor > 2) calculatedCost += 1;
      if (typeof targetFps === 'number' && targetFps > 30) calculatedCost += 1;
      return calculatedCost;
    }
    case 'magnific-video-upscale': {
      // Premium creative upscale (~$0.30/s est.), and heavy long jobs (avg ~180s).
      // Scale with the selected source-video span at 4 credits/sec, floor 20 so short
      // clips are unchanged while long clips stay >~85% margin at €0.50/credit.
      const startMs = readNumber(params?.video_input_start_ms) ?? 0;
      const endMs = readNumber(params?.video_input_end_ms);
      const durationSec = typeof endMs === 'number' && endMs > startMs ? (endMs - startMs) / 1000 : 5;
      const clampedDuration = Math.max(1, Math.min(60, durationSec));
      return Math.max(20, Math.ceil(4 * clampedDuration));
    }
    case 'wan-2.2-fast': {
      // fal fast-wan t2v: $0.0125-0.025/video. 1 credit already ~20x margin at
      // €0.50/credit; charge 2 at 720p to nudge validation toward lower res.
      const resolution = readString(params?.resolution) ?? '720p';
      return resolution === '720p' ? 2 : 1;
    }
    case 'wan-2.2-i2v': {
      // fal Wan a14b i2v: $0.04-0.08/sec. Charge ~1 credit/sec at 720p (0.7x below),
      // floored at 2, far under Seedance's 20, keeps iteration cheap.
      const duration = Math.max(2, Math.min(8, readNumber(params?.duration) ?? 5));
      const resolution = readString(params?.resolution) ?? '720p';
      const perSecond = resolution === '720p' ? 1 : 0.7;
      return Math.max(2, Math.ceil(perSecond * duration));
    }
    case 'hailuo-02-fast': {
      // fal Hailuo-02-fast i2v: $0.017/sec (~$0.10/6s, ~$0.17/10s). Flat-ish tier.
      const duration = readNumber(params?.duration) ?? 6;
      return duration >= 10 ? 3 : 2;
    }
    case 'minimax-h3-max':
    case 'minimax-h3-max-turbo':
    case 'wan-3.0-prime':
    case 'grok-imagine-video-1.5':
    case 'kling-3-turbo':
    case 'flux-3-max':
    case 'wan-3.0':
    case 'ltx-2.5-fast':
    case 'luma-ray-3.2':
      return estimateLatestVideoCredits(estimator, params as Partial<LatestVideoParams> | null, { startsFromImage: context?.startsFromImage });
    case 'veo-3.1-fast': {
      // Google Veo 3.1 Fast: ~$0.10-0.15/s @720p/1080p, $0.30-0.35/s @4K. Charge
      // ~4 cr/s @720p, +factors for higher res/audio; a premium tier.
      const duration = Math.max(4, Math.min(8, readNumber(params?.duration) ?? 8));
      const resolution = readString(params?.resolution) ?? '720p';
      const audio = params?.generate_audio === true;
      let per = 4;
      if (resolution === '1080p') per = 5;
      else if (resolution === '4k') per = 12;
      if (audio) per += 1;
      return Math.max(4, Math.ceil(per * duration));
    }
    case 'kling-2.5-turbo': {
      // Kling 2.5 Turbo Pro: $0.35/5s + $0.07/s. Charge ~2.5 cr/s.
      const duration = readNumber(params?.duration) ?? 5;
      return Math.max(6, Math.ceil(2.5 * Math.max(5, Math.min(10, duration))));
    }
    case 'ltx-2.3-fast': {
      // LTX-2.3 Fast: $0.06/s @1080p, $0.12/s @1440p, $0.24/s @2160p. Charge ~2 cr/s @1080p.
      const duration = Math.max(6, Math.min(20, readNumber(params?.duration) ?? 6));
      const resolution = readString(params?.resolution) ?? '1080p';
      const per = resolution === '2160p' ? 6 : resolution === '1440p' ? 3 : 2;
      return Math.max(4, Math.ceil(per * duration));
    }
    case 'luma-ray-2-flash': {
      // Luma Ray 2 Flash: $0.20/5s @540p; 720p x2, 1080p x4, 9s x2. Charge ~1 cr/s @540p base.
      const duration = (readNumber(params?.duration) ?? 5) >= 9 ? 9 : 5;
      const resolution = readString(params?.resolution) ?? '540p';
      const resMul = resolution === '1080p' ? 4 : resolution === '720p' ? 2 : 1;
      return Math.max(3, Math.ceil((duration / 5) * 3 * resMul));
    }
    case 'seedance-2.0-fast': {
      // ByteDance Seedance fast i2v (~$0.24/s @720p), cheaper/faster than full
      // Seedance's 4 cr/s. Charge 3 cr/s @720p (0.6x @480p), well under the 20-cr flagship.
      const requestedDuration = readNumber(params?.duration);
      const duration = requestedDuration === -1 ? 5 : (requestedDuration ?? 5);
      const clampedDuration = Math.max(1, Math.min(15, duration));
      const resolution = readString(params?.resolution) ?? '720p';
      const base = 3 * Math.max(1, Math.ceil(clampedDuration));
      return resolution === '480p' ? Math.max(2, Math.round(base * 0.6)) : base;
    }
    case 'flux-3': {
      // FLUX.3 sur fal : 0,17 $/s en 720p, 0,29 $/s en 1080p. A l'ancre maison
      // (seedance-2.0-fast, 0,2419 $/s pour 3 cr/s), cela donne 2 cr/s en 720p et 4 en 1080p.
      // C'est le premium le moins cher du catalogue, et c'est voulu.
      const duration = Math.max(5, Math.min(20, readNumber(params?.duration) ?? 5));
      const resolution = readString(params?.resolution) ?? '720p';
      const per = resolution === '1080p' ? 4 : 2;
      return Math.max(4, Math.ceil(per * duration));
    }
    case 'flux-3-draft': {
      // Palier brouillon : 0,06 $/s, 720p uniquement. 1 cr/s, plancher a 3 comme les autres
      // validateurs bon marche pour que la facturation reste lisible sur un clip court.
      const duration = Math.max(5, Math.min(20, readNumber(params?.duration) ?? 5));
      return Math.max(3, Math.ceil(duration));
    }
    case 'seedance-2.5': {
      // Seedance 2.5 (fal: $0.4730/s @720p, $0.2205/s @480p) is ~1.6x the 2.0 rate,
      // so 6 cr/s @720p holds the same margin as 2.0's 4 cr/s. 480p bills roughly
      // half of 720p on fal, so halve the credits too. Clips run 4-30s.
      const requestedDuration = readNumber(params?.duration);
      const duration = requestedDuration === -1 ? 5 : (requestedDuration ?? 5);
      const clampedDuration = Math.max(1, Math.min(30, duration));
      const resolution = readString(params?.resolution) ?? '720p';
      const base = 6 * Math.max(1, Math.ceil(clampedDuration));
      return resolution === '480p' ? Math.max(3, Math.round(base * 0.5)) : base;
    }
    default:
      return fallback;
  }
};

export interface FrontendModelAccessState {
  modelId: string;
  hasAccess: boolean;
  isVisibleByDefault: boolean;
  userVisibilityPreference: ModelVisibilityPreference;
  isInActiveList: boolean;
  isCheapModel: boolean;
  estimatedCostCredits: number | null;
}

type DirectorModelDocSection = 'inpainting' | 'maskless' | 'video';

export interface DirectorModelDocumentationChapters {
  inpainting: string;
  maskless: string;
  video: string;
}

const CAPS_EDIT_BASE = {
  supportsPrompt: true,
  requiresPrompt: true,
  supportsNegativePrompt: false,
  supportsSeed: true,
  supportsMask: false,
  supportsAspectRatio: false,
  supportsMainImageInput: true,
  supportsReferenceImages: false,
  referenceImagesMin: 0,
  referenceImagesMax: 0,
  supportsAnnotationGuide: false,
  supportsEndFrame: false,
  supportsVideoInput: false,
} as const;

// The Studio Vector models (lib/vector-models.ts): fal-hosted, SVG out, offered in a Vector project only.
const VECTOR_MODEL_BASE = {
  provider: 'fal',
  taskRoute: 'diffuse',
  outputFormat: 'svg',
  supportedProjectTypes: ['VECTOR'],
  uiCategory: 'Generate',
  uiSubCategory: 'Basic',
  showInModelSelector: true,
  defaultVisible: true,
  instructionModel: false,
} as const;
const CAPS_VECTOR_BASE = { ...CAPS_EDIT_BASE, supportsSeed: false, supportsMainImageInput: false } as const;

const CAPS_VIDEO_BASE = {
  supportsPrompt: true,
  requiresPrompt: true,
  supportsNegativePrompt: false,
  supportsSeed: false,
  supportsMask: false,
  supportsAspectRatio: false,
  supportsMainImageInput: true,
  supportsReferenceImages: false,
  referenceImagesMin: 0,
  referenceImagesMax: 0,
  supportsAnnotationGuide: false,
  supportsEndFrame: false,
  supportsVideoInput: false,
} as const;

const maiImageDefinition = (id: keyof typeof MAI_IMAGE_MODELS): StudioModelInput => ({
  name: MAI_IMAGE_MODELS[id].name,
  provider: 'azure-openai',
  taskRoute: 'diffuse',
  uiCategory: 'Edit',
  uiSubCategory: 'Full Area',
  showInModelSelector: true,
  defaultVisible: true,
  cheapEligible: false,
  instructionModel: true,
  powerHint: `${MAI_IMAGE_MODELS[id].purpose} Public preview.`,
  capabilities: { ...CAPS_EDIT_BASE, supportsSeed: false },
  billing: { estimator: 'flat', defaultCredits: 1 },
  notes: 'Text-to-image or single-image edits. PNG, at most 1,048,576 pixels, minimum 768px per side. No masks, extra references or private persona workflows. Public preview without an SLA.',
});

export const STUDIO_MODEL_REGISTRY = defineStudioModels({
  'mai-2.5-pro': maiImageDefinition('mai-2.5-pro'),
  'mai-2.6-flash': maiImageDefinition('mai-2.6-flash'),
  'mai-2.6': maiImageDefinition('mai-2.6'),
  'flux-2-pro': {
    name: 'Flux 2 Pro',
    provider: 'bfl',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: false,    cheapEligible: false,
    instructionModel: false,
    powerHint: 'High-control full-frame',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsAspectRatio: true,
      supportsReferenceImages: true,
      referenceImagesMax: 8,
      supportsAnnotationGuide: true,
    },
    averageGenerationSeconds: 29.93,
    billing: { estimator: 'flat', defaultCredits: 1 },
  },
  'seedvr2': {
    name: 'SeedVR2',
    provider: 'fal',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: true,
    instructionModel: true,
    powerHint: 'High-resolution image upscale',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsPrompt: false,
      requiresPrompt: false,
      supportsReferenceImages: false,
      supportsAnnotationGuide: false,
    },
    // Outputs barely differ across seeds, so extra generations are wasted credits.
    maximumGenerations: 1,
    averageGenerationSeconds: 180,
    billing: { estimator: 'seedvr2-output-megapixels', defaultCredits: 1, notes: '1 credit up to 16 Mpx, then +0.25 per additional 4 Mpx (2 credits at 32 Mpx, 2.5 at 40).' },
    notes: 'fal.ai by default; local ComfyUI exclusively when COMFYUI_SEEDVR2_ENABLED=true, never falls back.',
  },
  'qwen-image-2.1': {
    name: 'Qwen Image 2.1',
    provider: 'qwen',
    taskRoute: 'diffuse',
    supportedProjectTypes: ['FREEFORM', 'STORE_SWAP'],
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: true,
    instructionModel: true,
    powerHint: 'Instruction edits with up to 9 references.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsReferenceImages: true,
      referenceImagesMax: QWEN_IMAGE_21_MAX_REFERENCES,
      referenceImageBudget: { maxPixels: QWEN_IMAGE_21_REFERENCE_MAX_PIXELS },
      supportsAnnotationGuide: true,
    },
    billing: { estimator: 'flat', defaultCredits: 1 },
    notes: 'Local ComfyUI when COMFYUI_QWEN21_ENABLED=true, else our pinned Replicate cog fater-ai/region-lock-edit running the same workflow; neither falls back on the other.',
  },
  'flux-2-klein-4b': {
    name: 'Flux 2 Klein 4B',
    provider: 'bfl',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: false,    cheapEligible: true,
    instructionModel: true,
    powerHint: 'Ultra-fast + low-cost, with medium quality.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsAspectRatio: true,
      supportsReferenceImages: true,
      referenceImagesMax: 8,
      supportsAnnotationGuide: true,
    },
    averageGenerationSeconds: 3.35,
    comparisonProfile: {
      lifecycle: 'active',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Ultra-fast and cheaper than Grok/NB2, but lower final quality.',
      instructionFidelityVsFlagship: 'worse',
      visualQualityVsFlagship: 'worse',
      speedVsFlagship: 'faster',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'full',
      webReferenceSearch: 'no',
      bestFor: ['Fast cheap iteration loops', 'Utility passes with references'],
      avoidFor: ['High-fidelity final outputs'],
      knownLimitations: ['Quality can be visibly average vs NB2.'],
    },
    billing: { estimator: 'flat', defaultCredits: 0.5 },
  },
  'fater-enhance': {
    name: 'Fater Enhance',
    provider: 'fater',
    taskRoute: 'diffuse',
    showInModelSelector: false,
    defaultVisible: false,    cheapEligible: true,
    instructionModel: false,
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsPrompt: false,
      requiresPrompt: false,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
    },
    averageGenerationSeconds: 2.99,
    billing: { estimator: 'flat', defaultCredits: 0.5 },
    notes: 'Internal quick power alias over flux-2-klein-4b.',
  },
  'fater-upscale': {
    name: 'Fater Upscale',
    provider: 'fater',
    taskRoute: 'diffuse',
    showInModelSelector: false,
    defaultVisible: false,    cheapEligible: true,
    instructionModel: false,
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsPrompt: false,
      requiresPrompt: false,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
    },
    averageGenerationSeconds: 3.21,
    billing: { estimator: 'flat', defaultCredits: 0.5 },
    notes: 'Internal quick power alias over flux-2-klein-4b.',
  },
  'nano-banana': {
    name: 'Nano Banana',
    provider: 'gemini',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: false,    cheapEligible: true,
    instructionModel: true,
    powerHint: 'Fast direct edits',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsAspectRatio: true,
      supportsReferenceImages: true,
      referenceImagesMax: 5,
      supportsAnnotationGuide: true,
    },
    averageGenerationSeconds: 12.42,
    billing: { estimator: 'flat', defaultCredits: 1 },
  },
  'nano-banana-pro': {
    name: 'Nano Banana Pro',
    provider: 'gemini',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: false,    cheapEligible: false,
    instructionModel: true,
    powerHint: 'Legacy model kept for backward compatibility.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsAspectRatio: true,
      supportsReferenceImages: true,
      referenceImagesMax: 5,
      referenceImageBudget: { maxPixels: 16_000_000 },
      supportsAnnotationGuide: true,
    },
    averageGenerationSeconds: 36.64,
    comparisonProfile: {
      lifecycle: 'legacy',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Legacy access only; superseded by Nano Banana 2.',
      instructionFidelityVsFlagship: 'unknown',
      visualQualityVsFlagship: 'unknown',
      speedVsFlagship: 'unknown',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'full',
      webReferenceSearch: 'optional',
      bestFor: ['Backward compatibility workflows'],
      avoidFor: ['New default workflows'],
      knownLimitations: ['Superseded by Nano Banana 2.'],
    },
    billing: { estimator: 'nano-banana-pro-image-size', defaultCredits: 2, notes: '2 credits (1K/2K), 4 credits at 4K.' },
  },
  'nano-banana-2': {
    name: 'Nano Banana 2',
    provider: 'gemini',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: true,
    instructionModel: true,
    powerHint: 'Flagship baseline: strong instruction following + quality.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsAspectRatio: true,
      supportsReferenceImages: true,
      referenceImagesMax: 14,
      // fal decodes each input under 89.5 MP: a 16K layer reached it at 98.5 MP (2026-10-22). 16 MP covers the 4K tier.
      referenceImageBudget: { maxPixels: 16_000_000 },
      supportsAnnotationGuide: true,
    },
    averageGenerationSeconds: 48.82,
    comparisonProfile: {
      lifecycle: 'flagship',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Current flagship baseline for instruction reliability + quality.',
      instructionFidelityVsFlagship: 'similar',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'similar',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'full',
      webReferenceSearch: 'optional',
      bestFor: ['Default production edits', 'Reference-heavy local editing'],
      avoidFor: ['Ultra-fast low-cost iteration cycles'],
      knownLimitations: ['A bit slow versus fast models.'],
    },
    billing: { estimator: 'nano-banana-2-image-size', defaultCredits: 1.5, notes: '1 credit at 1K, 1.5 at 2K (default), 2 at 4K.' },
  },
  'nano-banana-2.1': {
    name: 'Nano Banana 2.1',
    provider: 'gemini',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: true,
    instructionModel: true,
    powerHint: 'Newer Nano Banana 2: sharper output and in-image text.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsAspectRatio: true,
      supportsReferenceImages: true,
      referenceImagesMax: 14,
      referenceImageBudget: { maxPixels: 16_000_000 },
      supportsAnnotationGuide: true,
    },
    // Nano Banana 2's figure until a 2.1 run is measured.
    averageGenerationSeconds: 48.82,
    comparisonProfile: {
      lifecycle: 'active',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Newer Nano Banana 2: sharper output and in-image text; thinking is always on, so expect slower runs.',
      instructionFidelityVsFlagship: 'unknown',
      visualQualityVsFlagship: 'unknown',
      speedVsFlagship: 'unknown',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'full',
      webReferenceSearch: 'optional',
      bestFor: ['In-image text', 'Reference-heavy edits'],
      avoidFor: ['Ultra-fast low-cost iteration cycles'],
      knownLimitations: ['A bit slow versus fast models.'],
    },
    billing: { estimator: 'nano-banana-2.1-image-size', defaultCredits: 1.4, notes: '1 credit at 1K, 1.4 at 2K (default), 2 at 4K.' },
  },
  'seedream4.5': {
    name: 'SeeDream 4.5',
    provider: 'seed',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: true,
    instructionModel: true,
    powerHint: 'Experimental fallback (test model).',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsReferenceImages: true,
      referenceImagesMax: 10,
      // BytePlus ModelArk image API, behind Replicate: each image at most 6000x6000 px in total, each side > 14 px.
      referenceImageBudget: { maxPixels: 36_000_000, minSide: 15 },
      supportsAnnotationGuide: true,
    },
    averageGenerationSeconds: 33.32,
    comparisonProfile: {
      lifecycle: 'experimental',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Experimental fallback when NB2/Grok fail.',
      instructionFidelityVsFlagship: 'unknown',
      visualQualityVsFlagship: 'unknown',
      speedVsFlagship: 'unknown',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'full',
      webReferenceSearch: 'no',
      bestFor: ['Model experimentation', 'Fallback trials'],
      avoidFor: ['Primary production baseline'],
      knownLimitations: ['Behavior is less predictable for primary workflows.'],
    },
    billing: { estimator: 'flat', defaultCredits: 1 },
  },
  'gpt-image-2': {
    name: 'GPT-Image-2',
    provider: 'azure-openai',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: false,    cheapEligible: false,
    instructionModel: true,
    powerHint: 'Very high quality, but very slow and weak on localized edits.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsSeed: false,
      supportsMainImageInput: true,
      supportsReferenceImages: false,
      supportsAnnotationGuide: false,
    },
    averageGenerationSeconds: 146.91,
    comparisonProfile: {
      lifecycle: 'active',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Best raw quality, but extremely slow and poor at localized geometry-preserving edits.',
      instructionFidelityVsFlagship: 'worse',
      visualQualityVsFlagship: 'better',
      speedVsFlagship: 'slower',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['High-quality global restyles'],
      avoidFor: ['Localized edits requiring strict geometry preservation'],
      knownLimitations: ['Can heavily deform images during localized edits.'],
    },
    billing: { estimator: 'flat', defaultCredits: 1 },
  },
  'gpt-image-2.5': {
    name: 'GPT-Image-2.5',
    provider: 'openai',
    hosting: 'fal',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: false,
    instructionModel: true,
    powerHint: 'High-fidelity image generation and full-area editing.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsSeed: false,
      supportsMask: false,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
      supportsReferenceImages: true,
      referenceImagesMin: 0,
      referenceImagesMax: GPT_IMAGE_25_CANVAS_REFERENCE_MAX,
    },
    averageGenerationSeconds: 120,
    billing: { estimator: 'gpt-image-2.5-quality', defaultCredits: 1.5, notes: '1 credit at low quality, 1.5 at medium (default), 2 at high, 2.25 at xhigh, 2.5 at max.' },
    notes: 'fal-hosted GPT-Image-2.5. Compact controls only: sunburst/flare mode and low/medium/high/xhigh/max quality. Geometry and input preparation are app-owned.',
  },
  'mai-2.5-flash': {
    name: 'MAI 2.5 Flash',
    provider: 'azure-openai',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: false,
    instructionModel: true,
    powerHint: 'Azure MAI 2.5 Flash image edit endpoint.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsSeed: false,
      supportsMainImageInput: true,
      supportsReferenceImages: false,
      supportsAnnotationGuide: false,
    },
    averageGenerationSeconds: 15,
    comparisonProfile: {
      lifecycle: 'active',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Azure-deployed MAI 2.5 Flash model for fast image edits.',
      instructionFidelityVsFlagship: 'unknown',
      visualQualityVsFlagship: 'unknown',
      speedVsFlagship: 'faster',
      costVsFlagship: 'unknown',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Fast Azure image edits'],
      avoidFor: ['TODO'],
      knownLimitations: ['Output is always PNG. Max pixel count 1,048,576. Dimensions min 768px.'],
    },
    billing: { estimator: 'flat', defaultCredits: 1 },
  },
  'ideogram-4-edit': {
    name: 'Ideogram 4',
    provider: 'ideogram',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: true,
    powerHint: 'Ideogram 4.0 generation/remix, strong typography and visual consistency.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsSeed: false,
      supportsNegativePrompt: false,
      supportsMainImageInput: true,
      supportsReferenceImages: false,
      supportsAnnotationGuide: false,
    },
    averageGenerationSeconds: 20,
    comparisonProfile: {
      lifecycle: 'active',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Ideogram 4.0 prompt-based generation or image remix with strong text rendering.',
      instructionFidelityVsFlagship: 'similar',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'faster',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Typography-heavy generation', 'Prompt-driven restyles'],
      avoidFor: ['Mask-precise localized inpainting'],
      knownLimitations: ['Maskless: edits are prompt-driven, not region-precise.'],
    },
    billing: { estimator: 'flat', defaultCredits: 1 },
  },
  'ideogram-inpaint': {
    name: 'Ideogram Inpaint',
    provider: 'ideogram',
    taskRoute: 'diffuse',
    supportedProjectTypes: ['FREEFORM', 'STORE_SWAP'],
    uiCategory: 'Edit',
    uiSubCategory: 'Masked',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: true,
    powerHint: 'Ideogram masked inpainting, paint a region with the Mask tool and regenerate just that area.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsSeed: true,
      supportsMainImageInput: true,
      supportsReferenceImages: false,
      supportsAnnotationGuide: false,
    },
    averageGenerationSeconds: 20,
    comparisonProfile: {
      lifecycle: 'active',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Ideogram masked inpainting for region-precise edits.',
      instructionFidelityVsFlagship: 'similar',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'faster',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Region-precise localized edits', 'Removing/replacing a painted area'],
      avoidFor: ['Full-frame restyles (use Remix instead)'],
      knownLimitations: ['Requires a painted mask; edits are confined to the masked region.'],
    },
    billing: { estimator: 'flat', defaultCredits: 1 },
  },
  'ideogram-3': {
    name: 'Ideogram 3',
    provider: 'ideogram',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: true,
    powerHint: 'Ideogram 3 text-to-image, generate an image from a prompt with strong typography.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsSeed: true,
      supportsNegativePrompt: true,
      supportsAspectRatio: true,
      supportsMainImageInput: false,
    },
    averageGenerationSeconds: 20,
    comparisonProfile: {
      lifecycle: 'active',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Ideogram 3 prompt-to-image generation with strong text rendering.',
      instructionFidelityVsFlagship: 'similar',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'faster',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Typography-heavy image generation', 'Text-to-image creation from scratch'],
      avoidFor: ['Image-to-image editing (use Ideogram Edit)', 'Masked inpainting (use Ideogram Inpaint)'],
      knownLimitations: ['Text-to-image only; generates a fresh image from the prompt.'],
    },
    billing: { estimator: 'flat', defaultCredits: 1 },
  },
  'qwen-image-2-pro': {
    name: 'Qwen Image 2 Pro',
    provider: 'qwen',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: false,    cheapEligible: true,
    instructionModel: true,
    powerHint: 'Superseded by Qwen Image 3 Pro, kept for comparison runs.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsNegativePrompt: true,
      supportsReferenceImages: true,
      referenceImagesMax: 1,
      supportsAnnotationGuide: true,
    },
    averageGenerationSeconds: 19.26,
    comparisonProfile: {
      lifecycle: 'legacy',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Superseded by qwen-image-3-pro: better text and layout rendering, a resolution tier and two extra reference images.',
      instructionFidelityVsFlagship: 'unknown',
      visualQualityVsFlagship: 'unknown',
      speedVsFlagship: 'unknown',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Comparison runs against qwen-image-3-pro'],
      avoidFor: ['New work: qwen-image-3-pro covers the same ground'],
      knownLimitations: ['Superseded; still selectable for users who pinned it.'],
    },
    billing: { estimator: 'flat', defaultCredits: 1 },
  },
  'qwen-image-3-pro': {
    name: 'Qwen Image 3 Pro',
    provider: 'qwen',
    hosting: 'fal',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: true,
    instructionModel: true,
    powerHint: 'Text & layout accuracy',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsNegativePrompt: true,
      supportsReferenceImages: true,
      referenceImagesMax: 2,
      supportsAnnotationGuide: true,
    },
    // Single verified run on 2026-09-22: 7.5s for a 720x720 text-to-image. Refine as usage accrues.
    averageGenerationSeconds: 8,
    comparisonProfile: {
      lifecycle: 'experimental',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Qwen flagship for accurate in-image text and dense layouts; composite benchmarks place it above nano-banana-2.',
      instructionFidelityVsFlagship: 'unknown',
      visualQualityVsFlagship: 'unknown',
      speedVsFlagship: 'unknown',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Accurate in-image text and signage', 'Complex multi-element layouts', 'Full-frame instruction edits'],
      avoidFor: ['Mask-precise inpainting', 'Multi-reference persona work'],
      knownLimitations: ['At most 3 input images in total, the edit source included.', 'No mask support.', 'Each input side must sit between 384 and 2048 px.'],
    },
    billing: { estimator: 'qwen-image-3-resolution', defaultCredits: 1.5, notes: '1 credit at 1K, 1.5 at 2K (default).' },
  },
  'qwen-edit-multiangle': {
    name: 'Qwen Edit Multiangle',
    provider: 'qwen',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: false,    cheapEligible: true,
    instructionModel: true,
    powerHint: 'Multi-view consistency',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsNegativePrompt: true,
      supportsAnnotationGuide: true,
    },
    averageGenerationSeconds: 33.36,
    billing: { estimator: 'flat', defaultCredits: 1 },
  },
  'grok-imagine-image': {
    name: 'Grok Imagine',
    provider: 'xai',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: false,    cheapEligible: true,
    instructionModel: true,
    powerHint: 'Fast + strong quality at lower cost, but no extra reference images.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsAspectRatio: true,
    },
    averageGenerationSeconds: 13.86,
    comparisonProfile: {
      lifecycle: 'active',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Fast, good-quality, cheaper alternative to NB2.',
      instructionFidelityVsFlagship: 'unknown',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'faster',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Fast quality edits when no extra refs are needed'],
      avoidFor: ['Reference-heavy workflows needing additional images'],
      knownLimitations: ['No additional reference image support.'],
    },
    billing: { estimator: 'flat', defaultCredits: 0.8 },
  },
  'grok-imagine-2': {
    name: 'Grok Imagine 2',
    provider: 'xai',
    hosting: 'fal',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: false,
    instructionModel: true,
    powerHint: 'Same credit price as the flagship, capped at 2 extra reference images.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsAspectRatio: true,
      supportsReferenceImages: true,
      referenceImagesMax: 2,
    },
    // Estimate anchored on the 13.86 s measured for Grok Imagine 1; no Grok 2 run was timed.
    averageGenerationSeconds: 14,
    comparisonProfile: {
      lifecycle: 'active',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Costs the same credits as the flagship at 1K and 2K, but takes at most 2 extra reference images against 14. Quality and speed against the flagship are not measured.',
      instructionFidelityVsFlagship: 'unknown',
      visualQualityVsFlagship: 'unknown',
      speedVsFlagship: 'unknown',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Instruction edits needing at most two extra references'],
      avoidFor: ['Reference-heavy persona or archetype work', '4K output'],
      knownLimitations: ['At most 3 input images in total, source included.', 'No mask support.', 'No 4K tier.'],
    },
    billing: { estimator: 'grok-imagine-2-resolution', defaultCredits: 1.5, notes: '1 credit at 1K, 1.5 at 2K (default).' },
  },
  'muse-image': {
    name: 'Muse Image',
    provider: 'meta',
    hosting: 'fal',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: true,
    instructionModel: true,
    powerHint: 'One credit per image, up to 9 extra reference images.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsSeed: false,
      supportsAspectRatio: true,
      supportsReferenceImages: true,
      referenceImagesMax: MUSE_IMAGE_MAX_INPUT_IMAGES - 1,
    },
    // 29.5 s measured on one edit (1568x1568, 2026-10-01).
    averageGenerationSeconds: 30,
    comparisonProfile: {
      lifecycle: 'active',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Costs 1 credit per image, less than the flagship at its default resolution, and takes fewer extra reference images (up to 9). Quality and speed against the flagship are not measured.',
      instructionFidelityVsFlagship: 'unknown',
      visualQualityVsFlagship: 'unknown',
      speedVsFlagship: 'unknown',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'unknown',
      bestFor: ['Instruction edits and generations needing up to nine extra references'],
      avoidFor: ['Mask-precise inpainting', '4K output'],
      knownLimitations: ['No mask support.', 'No seed.', "No resolution tier: the output is about 2.5 Mpx per fal's documentation.", 'At most 10 input images in total, source included.', 'About 30 s per image, measured on a single edit.'],
    },
    billing: { estimator: 'flat', defaultCredits: 1 },
  },
  'flux-3-image': {
    name: 'Flux 3 Image',
    provider: 'bfl',
    hosting: 'fal',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: false,
    instructionModel: true,
    powerHint: 'Five resolution tiers up to 4K, up to 9 extra reference images.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsSeed: false,
      supportsAspectRatio: true,
      supportsReferenceImages: true,
      referenceImagesMax: FLUX_3_IMAGE_MAX_INPUT_IMAGES - 1,
      // fal schema: each image_urls entry at least 256 px per side.
      referenceImageBudget: { maxPixels: FLUX_3_IMAGE_MAX_INPUT_PIXELS, minSide: 256 },
    },
    // 117 s measured on one 2k edit (2000x2000, 2026-10-02).
    averageGenerationSeconds: 120,
    comparisonProfile: {
      lifecycle: 'active',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'Five resolution tiers up to 4K, and fewer extra reference images than the flagship (up to 9). Quality and speed against the flagship are not measured.',
      instructionFidelityVsFlagship: 'unknown',
      visualQualityVsFlagship: 'unknown',
      speedVsFlagship: 'unknown',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'unknown',
      bestFor: ['Prompt-driven generations and maskless edits needing up to nine extra references'],
      avoidFor: ['Mask-precise inpainting'],
      knownLimitations: ['No mask support.', 'No seed.', 'No negative prompt.', 'At most 10 input images in total, source included.', "About 2 minutes at 2k, measured on a single edit; 4k can take several minutes per fal's documentation."],
    },
    billing: { estimator: 'flux-3-image-resolution', defaultCredits: 1.5, notes: '1 credit at 512sq, 768sq and 1k, 1.5 at 2k (default), 2.5 at 4k.' },
  },
  'flux-3-klein': {
    name: 'Flux 3 Klein',
    provider: 'bfl',
    hosting: 'fal',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: false,
    instructionModel: true,
    powerHint: 'Open-weights FLUX 3, output sized to the frame up to 4K, up to 9 extra reference images.',
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsSeed: false,
      supportsAspectRatio: true,
      supportsReferenceImages: true,
      referenceImagesMax: FLUX_3_KLEIN_MAX_INPUT_IMAGES - 1,
      referenceImageBudget: { maxPixels: FLUX_3_KLEIN_MAX_INPUT_PIXELS, minSide: 256 },
    },
    // 22 s measured for three parallel 2k edits through the Studio agent (2048x2048, 2026-10-16).
    averageGenerationSeconds: 22,
    comparisonProfile: {
      lifecycle: 'active',
      flagshipId: FLAGSHIP_STUDIO_MODEL_ID,
      positioning: 'FLUX 3 open-weights model: output sized to the frame at 1K, 2K or 4K and up to 9 extra references. On 83 URW Concept Studio edits its closeness to the approved result averaged 54 against 63 for Nano Banana 2.1.',
      instructionFidelityVsFlagship: 'worse',
      visualQualityVsFlagship: 'worse',
      speedVsFlagship: 'unknown',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'unknown',
      bestFor: ['Shopfront material and colour edits', 'Generations sized exactly to the frame'],
      avoidFor: ['Mask-precise inpainting', 'Development-project scenes where fidelity to the brief matters most'],
      knownLimitations: ['No mask support.', 'No seed.', 'No negative prompt.', 'At most 10 input images in total, source included.', 'Its edits can change content outside the requested region (BFL model card).'],
    },
    billing: { estimator: 'flux-3-klein-resolution', defaultCredits: 1.5, notes: '1 credit at 1k, 1.5 at 2k (default), 2.5 at 4k.' },
  },
  'kling-3': {
    name: 'Kling 3',
    provider: 'kling',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsNegativePrompt: true,
      supportsEndFrame: true,
    },
    averageGenerationSeconds: 208.6,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Reliable cinematic video generation with strong motion quality.',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'similar',
      costVsFlagship: 'unknown',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Prompt-first video generation', 'Cinematic sequences'],
      avoidFor: ['Reference-video editing workflows'],
      knownLimitations: ['No reference video input support.'],
    },
    billing: { estimator: 'kling-3', defaultCredits: 3, notes: '1 credit per started second, doubled at 1080p and doubled again with audio (5s at 1080p without audio = 10 credits).' },
  },
  'kling-3-omni': {
    name: 'Kling 3 Omni',
    provider: 'kling',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsNegativePrompt: true,
      supportsEndFrame: true,
      supportsVideoInput: true,
    },
    averageGenerationSeconds: 246.41,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Reference-video aware generation/editing with stronger continuity controls.',
      visualQualityVsFlagship: 'better',
      speedVsFlagship: 'slower',
      costVsFlagship: 'unknown',
      additionalReferencesVsFlagship: 'full',
      webReferenceSearch: 'no',
      bestFor: ['Video-to-video guided edits', 'Continuity with source footage'],
      avoidFor: ['Ultra-fast iteration loops'],
      knownLimitations: ['Input duration/resolution constraints are strict.'],
    },
    billing: { estimator: 'kling-3-omni', defaultCredits: 3, notes: '1 credit per started second, doubled at 1080p and doubled again with audio (5s at 1080p without audio = 10 credits).' },
  },
  'seedance-2.0': {
    name: 'Seedance 2.0',
    provider: 'bytedance',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: true,
      supportsReferenceImages: true,
      referenceImagesMax: 9,
      supportsEndFrame: true,
      supportsVideoInput: true,
    },
    averageGenerationSeconds: 370.4,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Balanced reference-to-video model across quality and speed.',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'faster',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'full',
      webReferenceSearch: 'no',
      bestFor: ['Mixed image/video reference workflows', 'Faster turnaround video generation'],
      avoidFor: ['Strict cinematic stylization requiring heavier motion control'],
      knownLimitations: ['Output quality varies more with prompt/reference quality.'],
    },
    billing: { estimator: 'seedance-2.0', defaultCredits: 20, notes: '4 credits per started second at 720p (20 credits for the 5s default, and for a model-chosen duration -1), x0.75 at 480p, x2 at 1080p, x4 at 4K.' },
  },
  'seedance-2.0-fast': {
    name: 'Seedance Fast',
    provider: 'bytedance',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: true,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: true,
      supportsAspectRatio: true,
      supportsReferenceImages: true,
      referenceImagesMax: 9,
      supportsEndFrame: true,
      supportsMainImageInput: true,
    },
    averageGenerationSeconds: 200,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'ByteDance Seedance 2.0 FAST tier, the flagship Seedance model at a fraction of the time/cost (480p/720p). State-of-the-art quality, fast enough to iterate.',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'faster',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'full',
      webReferenceSearch: 'no',
      bestFor: ['Fast high-quality video previews and finals at 480p/720p', 'Iterating a Seedance direction without the full-tier cost/latency'],
      avoidFor: ['1080p final renders (use the full seedance-2.0)'],
      knownLimitations: ['Capped at 720p, escalate to seedance-2.0 for 1080p.'],
    },
    billing: { estimator: 'seedance-2.0-fast', defaultCredits: 15, notes: '3 credits per started second at 720p (15 credits for the 5s default, and for a model-chosen duration -1), 60% of that at 480p rounded to the nearest credit.' },
  },
  'seedance-2.5': {
    name: 'Seedance 2.5',
    provider: 'bytedance',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsAspectRatio: true,
      supportsReferenceImages: true,
      referenceImagesMax: 30,
      supportsVideoInput: true,
    },
    averageGenerationSeconds: 350,
    comparisonProfile: {
      lifecycle: 'flagship',
      positioning: 'ByteDance Seedance 2.5, the newest Seedance. Holds a character, set and palette across a single 30-second take, and takes up to 50 multimodal references (images, videos, audio). Capped at 720p.',
      visualQualityVsFlagship: 'better',
      speedVsFlagship: 'similar',
      costVsFlagship: 'worse',
      additionalReferencesVsFlagship: 'full',
      webReferenceSearch: 'no',
      bestFor: ['Long single takes up to 30s without stitching', 'Character/set consistency across many references', 'Native synchronized audio and lip-sync'],
      avoidFor: ['1080p or 4K deliverables (use seedance-2.0)', 'Cheap exploratory iterations (use seedance-2.0-fast)'],
      knownLimitations: ['Capped at 480p/720p.', 'No seed control, so runs are not exactly reproducible.', 'No end-frame keyframe.'],
    },
    billing: { estimator: 'seedance-2.5', defaultCredits: 30, notes: '6 credits per started second at 720p (30 credits for the 5s default, and for a model-chosen duration -1), half that at 480p.' },
  },
  'flux-3': {
    name: 'FLUX 3',
    provider: 'bfl',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
      supportsEndFrame: true,
    },
    averageGenerationSeconds: 120,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Black Forest Labs FLUX.3, the cheapest premium video tier in the catalogue: native synchronized audio, up to 20 seconds, 720p or 1080p, and an optional end frame. Image-to-video from the current frame.',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'faster',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Cheap premium video with native audio', 'Clips up to 20s from one frame', 'First-to-last-frame transitions'],
      avoidFor: ['Multi-reference character consistency (use seedance-2.5)', '4K deliverables (use veo-3.1-fast or ltx-2.3-fast)'],
      knownLimitations: ['No reference images beyond the start and end frame.', 'No seed control.', 'Capped at 1080p.'],
    },
    billing: { estimator: 'flux-3', defaultCredits: 10, notes: '2 credits per second at 720p (10 credits for the 5s default), 4 per second at 1080p, rounded up; clips of 5 to 20 seconds.' },
  },
  'flux-3-draft': {
    name: 'FLUX 3 Draft',
    provider: 'bfl',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: true,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
      supportsEndFrame: true,
    },
    averageGenerationSeconds: 40,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'FLUX.3 draft tier, a fast low-cost preview of the same model at 720p. Use it to prove a motion reads before paying for the full render.',
      visualQualityVsFlagship: 'worse',
      speedVsFlagship: 'faster',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Proving a motion or direction cheaply', 'Fast iteration inside the video self-validation loop'],
      avoidFor: ['Any final deliverable, escalate to flux-3'],
      knownLimitations: ['Draft quality, 720p only.', 'No seed control.'],
    },
    billing: { estimator: 'flux-3-draft', defaultCredits: 5, notes: '1 credit per started second, for clips of 5 to 20 seconds (5 credits for the 5s default).' },
  },
  'veo-3.1-fast': {
    name: 'Veo 3.1 Fast',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: true,
      supportsNegativePrompt: true,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
    },
    averageGenerationSeconds: 360,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Google Veo 3.1 Fast, arguably the best overall video quality, fast, with native audio and up to 4K. Image-to-video from the current frame.',
      visualQualityVsFlagship: 'better',
      speedVsFlagship: 'faster',
      costVsFlagship: 'worse',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Best-quality fast video from a frame', 'Cinematic motion with native audio, up to 4K'],
      avoidFor: ['Ultra-cheap iteration (use wan/hailuo/ltx)'],
      knownLimitations: ['First-frame only on this endpoint (no last-frame keyframe).'],
    },
    billing: { estimator: 'veo-3.1-fast', defaultCredits: 32, notes: '4 credits per second at 720p, 5 at 1080p, 12 at 4K, plus 1 more per second when generate_audio is set, rounded up; durations 4, 6 or 8 seconds (8s at 720p without audio = 32 credits).' },
  },
  'minimax-h3-max': {
    name: 'MiniMax H3 Max',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: LATEST_VIDEO_MODELS['minimax-h3-max'].supportsSeed,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
    },
    averageGenerationSeconds: 240,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'MiniMax H3 Max, the flagship of the Hailuo line (2026-08): strong physical motion and faces, text- or image-to-video up to 15s at 480p, 768p or 1080p (1080p is a latent refinement of a 768p native pass).',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'similar',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Realistic people and physical motion from a frame or from text', 'Long single takes up to 15s at a moderate price'],
      avoidFor: ['Native audio (none on this endpoint; use wan-3.0-prime or veo-3.1-fast)'],
      knownLimitations: ['No audio track.', 'The output can run up to about 0.7s longer than requested.'],
    },
    billing: { estimator: 'minimax-h3-max', defaultCredits: 13, notes: '1.5 credits per second at 480p, 2.5 at 768p (default), 5 at 1080p, rounded up; 2-15s (5s at 768p = 13 credits).' },
  },
  'minimax-h3-max-turbo': {
    name: 'MiniMax H3 Max Turbo',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: LATEST_VIDEO_MODELS['minimax-h3-max-turbo'].supportsSeed,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
    },
    averageGenerationSeconds: 120,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'MiniMax H3 Max Turbo (2026-09), the fast and cheap tier of H3 Max: same inputs, roughly half the price, for drafts and volume.',
      visualQualityVsFlagship: 'worse',
      speedVsFlagship: 'faster',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Cheap, fast drafts and batches of motion tests', 'Iterating on a prompt before a final on minimax-h3-max'],
      avoidFor: ['Final hero shots (use minimax-h3-max, kling-3 or veo-3.1-fast)'],
      knownLimitations: ['No audio track.'],
    },
    billing: { estimator: 'minimax-h3-max-turbo', defaultCredits: 8, notes: '1 credit per second at 480p, 1.5 at 768p (default), 2.5 at 1080p, rounded up; 2-15s (5s at 768p = 8 credits).' },
  },
  'wan-3.0-prime': {
    name: 'Wan 3.0 Prime',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: LATEST_VIDEO_MODELS['wan-3.0-prime'].supportsSeed,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
      supportsEndFrame: true,
      supportsReferenceImages: true,
      referenceImagesMax: LATEST_VIDEO_MODELS['wan-3.0-prime'].maxReferenceImages ?? 10,
    },
    averageGenerationSeconds: 240,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Alibaba Wan 3.0 Prime (2026-08): text- or image-to-video up to 30s with native generated audio, start and end frames, 480p to 1080p.',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'slower',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Longer takes (up to 30s) with native sound', 'First-to-last frame transitions'],
      avoidFor: ['Quick drafts (prompt expansion adds 20-60s)'],
      knownLimitations: ['Prompt expansion is on and adds 20-60s of latency.'],
    },
    billing: { estimator: 'wan-3.0-prime', defaultCredits: 20, notes: '2 credits per second at 480p, 4 at 720p (default), 8 at 1080p, rounded up, sound included; 2-30s (5s at 720p = 20 credits).' },
  },
  'grok-imagine-video-1.5': {
    name: 'Grok Imagine Video 1.5',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: LATEST_VIDEO_MODELS['grok-imagine-video-1.5'].supportsSeed,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
    },
    averageGenerationSeconds: 120,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'xAI Grok Imagine Video 1.5 (2026-07): text- or image-to-video up to 15s at 480p, 720p or 1080p, with the widest set of text-to-video aspect ratios (3:2 and 2:3 included).',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'faster',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Stylised or playful motion', 'Unusual aspect ratios from text (3:2, 2:3)'],
      avoidFor: ['Exact reproduction of a person (identity drift observed on this family)'],
      knownLimitations: ['No seed: runs are not reproducible.'],
    },
    billing: { estimator: 'grok-imagine-video-1.5', defaultCredits: 24, notes: '2.5 credits per second at 480p, 4 at 720p (default), 7.5 at 1080p, rounded up; 1-15s (6s at 720p = 24 credits).' },
  },
  'kling-3-turbo': {
    name: 'Kling 3 Turbo',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: LATEST_VIDEO_MODELS['kling-3-turbo'].supportsSeed,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
    },
    averageGenerationSeconds: 150,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Kling 3 Turbo Pro (2026-06): the faster Kling 3, text- or image-to-video at 1080p, 3-15s, same motion quality family as kling-3.',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'faster',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Kling-quality people and motion, faster than kling-3', 'Animating a List output of a person (refused by Seedance)'],
      avoidFor: ['Reference-heavy consistency (use kling-3-omni)'],
      knownLimitations: ['No end frame, no seed, no audio on this endpoint.'],
    },
    billing: { estimator: 'kling-3-turbo', defaultCredits: 20, notes: '4 credits per second, rounded up; 3-15s (5s = 20 credits).' },
  },
  'flux-3-max': {
    name: 'FLUX 3 Max',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: LATEST_VIDEO_MODELS['flux-3-max'].supportsSeed,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
      supportsEndFrame: true,
    },
    averageGenerationSeconds: 60,
    comparisonProfile: {
      lifecycle: 'experimental',
      positioning: 'Black Forest Labs FLUX 3 Max (2026-10-19): the top of the FLUX video line, text- or image-to-video with an optional end frame, native audio, 5-20s at 720p or 1080p; ranked in the top three on both text- and image-to-video leaderboards and fast (about 12s for a 10s clip).',
      visualQualityVsFlagship: 'better',
      speedVsFlagship: 'faster',
      costVsFlagship: 'similar',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Premium finals from text or from a frame, fast', 'First-to-last frame transitions with sound'],
      avoidFor: ['Multi-reference character consistency (use seedance-2.5 or wan-3.0 with references)', '4K deliverables (use ltx-2.5-fast or veo-3.1-fast)'],
      knownLimitations: ['1080p is an upscale of the native pass.', 'No reference images beyond start and end frames.'],
    },
    billing: { estimator: 'flux-3-max', defaultCredits: 25, notes: '5 credits per second at 720p (default), 8.5 at 1080p, rounded up, sound included; 5-20s (5s at 720p = 25 credits).' },
  },
  'wan-3.0': {
    name: 'Wan 3.0',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: true,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: LATEST_VIDEO_MODELS['wan-3.0'].supportsSeed,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
      supportsEndFrame: true,
      supportsReferenceImages: true,
      referenceImagesMax: LATEST_VIDEO_MODELS['wan-3.0'].maxReferenceImages ?? 10,
    },
    averageGenerationSeconds: 180,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Alibaba Wan 3.0 Standard (2026-09): the same family that tops the text-to-video leaderboard, about a third cheaper than Prime. Text, image (with end frame) or up to 10 reference images, native audio, 2-30s at 480p, 720p or 1080p.',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'slower',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'full',
      webReferenceSearch: 'no',
      bestFor: ['Cheap drafts at 480p, from text or a frame', 'Keeping a character or product consistent from up to 10 reference images, at a fraction of seedance-2.5'],
      avoidFor: ['Quick turnarounds (prompt expansion adds 20-60s)'],
      knownLimitations: ['Prompt expansion is on and adds 20-60s of latency.', 'With reference images, a start image becomes the first reference rather than the exact first frame.'],
    },
    billing: { estimator: 'wan-3.0', defaultCredits: 15, notes: '1.5 credits per second at 480p, 3 at 720p (default), 6 at 1080p, rounded up, sound included, the same with reference images; 2-30s (5s at 720p = 15 credits).' },
  },
  'ltx-2.5-fast': {
    name: 'LTX-2.5 Fast',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: LATEST_VIDEO_MODELS['ltx-2.5-fast'].supportsSeed,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
      supportsEndFrame: true,
    },
    averageGenerationSeconds: 60,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Lightricks LTX-2.5 Fast (2026-09, open weights): text- or image-to-video with an optional end frame, native audio, 720p up to 4K, even durations 6-20s (10s max at 1440p and 4K). About 110 points above LTX-2.3 on the text-to-video leaderboard.',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'faster',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['4K deliverables at a moderate price', 'Fast finals with sound and an end frame'],
      avoidFor: ['Character consistency from references'],
      knownLimitations: ['Landscape 16:9 or portrait 9:16 only.', 'No seed.', '1440p and 4K stop at 10 seconds.'],
    },
    billing: { estimator: 'ltx-2.5-fast', defaultCredits: 24, notes: '2.5 credits per second at 720p, 4 at 1080p (default), 5.5 at 1440p, 9 at 2160p, rounded up, sound included; even durations 6-20s, 6-10s above 1080p (6s at 1080p = 24 credits).' },
  },
  'luma-ray-3.2': {
    name: 'Luma Ray 3.2',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,
    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: LATEST_VIDEO_MODELS['luma-ray-3.2'].supportsSeed,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
      supportsEndFrame: true,
    },
    averageGenerationSeconds: 90,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Luma Ray 3.2 (2026-09): seamless loops and first-to-last frame morphs, text- or image-to-video at 540p, 720p or 1080p. From an image it is one of the cheapest clips in the catalogue; from text it is priced per clip and costs more. Replaces Ray 2 Flash, which fal retires on 2026-10-25.',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'similar',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Seamless marketing loops from a frame', 'Morphs from a start frame to an end frame'],
      avoidFor: ['Long takes (5s from an image, 10s at most from text)', 'Generating from text when price matters (3x the image price)'],
      knownLimitations: ['From an image, 5 seconds only.', 'A loop and an end frame cannot be combined.', 'No audio, no seed.'],
    },
    billing: { estimator: 'luma-ray-3.2', defaultCredits: 30, notes: 'Per clip. From an image (5s): 4.5 credits at 540p, 9 at 720p (default), 36 at 1080p. From text: 15 / 30 / 60 credits for 5s at 540p / 720p / 1080p, twice that for 10s. Rounded up.' },
  },
  'kling-2.5-turbo': {
    name: 'Kling 2.5 Turbo',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    // Retired 2026-10-22: superseded by kling-3-turbo (same lab, 1080p, text or image, 3-15s).
    // The id, estimator and film branch stay so old logs resolve and in-flight runs finish.
    showInModelSelector: false,
    defaultVisible: false,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsNegativePrompt: true,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
      supportsEndFrame: true,
    },
    averageGenerationSeconds: 100,
    comparisonProfile: {
      lifecycle: 'legacy',
      positioning: 'Kling 2.5 Turbo Pro (retired 2026-10-22, use kling-3-turbo): 1080p image-to-video with a tail-frame keyframe.',
      visualQualityVsFlagship: 'better',
      speedVsFlagship: 'faster',
      costVsFlagship: 'worse',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Top-quality motion at 1080p', 'First-to-last-frame transitions via end_frame'],
      avoidFor: ['Cheap iteration'],
      knownLimitations: ['No audio.'],
    },
    billing: { estimator: 'kling-2.5-turbo', defaultCredits: 13, notes: '2.5 credits per second, rounded up: 13 credits at 5s and 25 at 10s, the only two durations.' },
  },
  'ltx-2.3-fast': {
    name: 'LTX-2.3 Fast',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: true,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: true,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
      supportsEndFrame: true,
    },
    averageGenerationSeconds: 55,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Lightricks LTX-2.3 Fast, cheapest premium tier (open-weights), native audio, end-frame keyframe, up to 4K. Great value.',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'faster',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Cheap high-res video with audio', 'First-to-last-frame transitions'],
      avoidFor: [],
      knownLimitations: ['Durations >10s require 25fps + 1080p.'],
    },
    billing: { estimator: 'ltx-2.3-fast', defaultCredits: 12, notes: '2 credits per second at 1080p (12 credits for the 6s default), 3 at 1440p, 6 at 2160p, rounded up; clips of 6 to 20 seconds.' },
  },
  'luma-ray-2-flash': {
    name: 'Luma Ray 2 Flash',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    // Retired 2026-10-22: fal removes Ray 2 Flash on 2026-10-25; luma-ray-3.2 replaces it.
    // The id, estimator and film branch stay so old logs resolve and in-flight runs finish.
    showInModelSelector: false,
    defaultVisible: false,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
      supportsEndFrame: true,
    },
    averageGenerationSeconds: 60,
    comparisonProfile: {
      lifecycle: 'legacy',
      positioning: 'Luma Ray 2 Flash (retired 2026-10-22, use luma-ray-3.2; fal removes it on 2026-10-25): first+last keyframes and seamless loops.',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'faster',
      costVsFlagship: 'worse',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Seamless looping clips', 'First-to-last-frame morph transitions'],
      avoidFor: ['Cheap iteration'],
      knownLimitations: ['No audio.'],
    },
    billing: { estimator: 'luma-ray-2-flash', defaultCredits: 9, notes: '3 credits for a 5s clip at 540p and 5.4 at 9s before rounding (any longer clip bills as 9s), x2 at 720p, x4 at 1080p, the total rounded up (9s at 1080p = 22).' },
  },
  'gemini-omni-flash-preview': {
    name: 'Gemini Omni Flash 1.1',
    provider: 'gemini',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    showInModelSelector: false, // Google switched off: no other provider
    defaultVisible: false,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsAspectRatio: true,
      supportsReferenceImages: true,
      referenceImagesMax: 3,
      supportsVideoInput: true,
    },
    averageGenerationSeconds: 90,
    comparisonProfile: {
      lifecycle: 'experimental',
      positioning: 'Fast Gemini-native short video generation and conversational-style editing.',
      visualQualityVsFlagship: 'unknown',
      speedVsFlagship: 'faster',
      costVsFlagship: 'unknown',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Short 720p prompt-to-video loops', 'Fast image/video/audio guided iterations'],
      avoidFor: ['Long-form or high-resolution cinematic output'],
      knownLimitations: ['Preview model; output controls and schemas may change.', 'Short clips only.'],
    },
    billing: { estimator: 'gemini-omni-flash-preview', defaultCredits: 5, notes: '1 credit per started second of video (3 to 10 seconds).' },
  },
  'hailuo-02-fast': {
    name: 'Hailuo 02 Fast',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    // Accessible on the full/prod fal key (confirmed via a direct fal access probe).
    // The earlier 403 was the LIMITED PREVIEW key, which can't reach any fal partner
    // model (even the core seedance-2.0 403s on it). See memory: fal-model-access.
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: true,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsMainImageInput: true,
    },
    averageGenerationSeconds: 120,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Cheapest, fastest image-to-video motion validator (512p). MiniMax Hailuo 02 distilled fast tier.',
      visualQualityVsFlagship: 'worse',
      speedVsFlagship: 'faster',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Fast/cheap motion + direction validation before the final render', 'Quick previews of "does the movement work"'],
      avoidFor: ['Final deliverable quality', 'High-resolution output (capped at 512p)'],
      knownLimitations: ['512p only.', 'Preview-grade quality, a validator, not a finisher.'],
    },
    billing: { estimator: 'hailuo-02-fast', defaultCredits: 2, notes: '2 credits, 3 credits from 10 seconds.' },
  },
  'wan-2.2-i2v': {
    name: 'Wan 2.2 Video',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    // Retired 2026-10-22: superseded by wan-3.0 at 480p (cheaper per second, text or image, end frame).
    // The id, estimator and film branch stay so old logs resolve and in-flight runs finish.
    showInModelSelector: false,
    defaultVisible: false,    cheapEligible: true,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: true,
      supportsNegativePrompt: true,
      supportsAspectRatio: true,
      supportsMainImageInput: true,
      supportsEndFrame: true,
    },
    averageGenerationSeconds: 180,
    comparisonProfile: {
      lifecycle: 'legacy',
      positioning: 'Wan 2.2 A14B image-to-video (retired 2026-10-22, use wan-3.0 at 480p): cheap 720p with last-frame keyframing.',
      visualQualityVsFlagship: 'worse',
      speedVsFlagship: 'faster',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Higher-quality cheap motion previews (720p)', 'First-frame to last-frame transitions via end_frame'],
      avoidFor: ['Cinematic final renders (use Seedance/Kling)'],
      knownLimitations: ['Preview-grade quality.', 'Short clips.'],
    },
    billing: { estimator: 'wan-2.2-i2v', defaultCredits: 5, notes: '1 credit per second at 720p (5 credits for the 5s default), 0.7 per second at 480p or 580p, rounded up; clips of 2 to 8 seconds.' },
  },
  'wan-2.2-fast': {
    name: 'Wan 2.2 Fast',
    provider: 'fal',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Generate',
    // Retired 2026-10-22: superseded by wan-3.0 at 480p and minimax-h3-max-turbo at 480p.
    // The id, estimator and film branch stay so old logs resolve and in-flight runs finish.
    showInModelSelector: false,
    defaultVisible: false,    cheapEligible: true,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsSeed: true,
      supportsNegativePrompt: true,
      supportsAspectRatio: true,
      supportsMainImageInput: false,
    },
    averageGenerationSeconds: 90,
    comparisonProfile: {
      lifecycle: 'legacy',
      positioning: 'Wan 2.2 5B "fast-wan" text-to-video (retired 2026-10-22, use wan-3.0 or minimax-h3-max-turbo at 480p): the old cheapest sketch tier.',
      visualQualityVsFlagship: 'worse',
      speedVsFlagship: 'faster',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Fast/cheap text-to-video direction sketches', 'Exploring motion ideas with no source image'],
      avoidFor: ['Final deliverable quality', 'Image-to-video (use Hailuo Fast or Wan 2.2 Video)'],
      knownLimitations: ['Preview-grade quality.', 'Short clips (<=5s).', 'Text-to-video only, ignores the workspace frame.'],
    },
    billing: { estimator: 'wan-2.2-fast', defaultCredits: 1, notes: '2 credits at 720p, 1 credit at 480p or 580p.' },
  },
  'topaz-video-upscale': {
    name: 'Topaz Video Upscale',
    provider: 'fal',
    // List has no source-video path to feed it: offering it there was a dead end.
    supportedProjectTypes: ['FREEFORM', 'STORE_SWAP'],
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Edit',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsPrompt: false,
      requiresPrompt: false,
      supportsMainImageInput: false,
      supportsVideoInput: true,
    },
    averageGenerationSeconds: 76.25,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Post-process enhancement/upscale pass for existing videos.',
      visualQualityVsFlagship: 'better',
      speedVsFlagship: 'faster',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Sharpening and upscaling final renders', 'Increasing target playback quality'],
      avoidFor: ['Net-new video generation'],
      knownLimitations: ['Requires source video input and does not generate new scenes.'],
    },
    billing: { estimator: 'topaz-video-upscale', defaultCredits: 2, notes: '2 credits, +1 above 2x upscale (+2 above 2.5x), +1 when the target fps exceeds 30.' },
  },
  'mirelo-sfx-1.6': {
    name: 'Mirelo SFX 1.6',
    provider: 'mirelo',
    // List has no source-video path to feed it: offering it there was a dead end.
    supportedProjectTypes: ['FREEFORM', 'STORE_SWAP'],
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Edit',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      requiresPrompt: false,
      supportsSeed: true,
      supportsMainImageInput: false,
      supportsVideoInput: true,
    },
    averageGenerationSeconds: 90,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Timeline-native synchronized Foley, ambience, impacts, and scene sound generation for existing video.',
      visualQualityVsFlagship: 'similar',
      speedVsFlagship: 'similar',
      costVsFlagship: 'better',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Frame-synchronized sound effects for silent footage', 'Foley and ambience tracks for existing edits'],
      avoidFor: ['Dialogue replacement', 'Music composition', 'Clips shorter than 1 second or longer than 60 seconds'],
      knownLimitations: ['Produces a video carrying the generated soundtrack; Fater extracts it into an audio timeline track.', 'Text guidance is optional and should stay sound-only.'],
    },
    billing: {
      estimator: 'mirelo-sfx-1.6',
      defaultCredits: 10,
      notes: '1 credit per started second of the selected timeline span, up to 60 seconds, 10 credits when no span is resolved (fal price verified at $0.01 per generated second).',
    },
  },
  'magnific-video-upscale': {
    name: 'Magnific Video Upscale',
    provider: 'magnific',
    taskRoute: 'film',
    uiCategory: 'Video',
    uiSubCategory: 'Edit',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_VIDEO_BASE,
      supportsPrompt: false,
      requiresPrompt: false,
      supportsMainImageInput: false,
      supportsVideoInput: true,
    },
    averageGenerationSeconds: 900,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'Creative AI video upscaling with resolution, creativity, sharpness, grain, FPS boost, and style controls.',
      visualQualityVsFlagship: 'better',
      speedVsFlagship: 'slower',
      costVsFlagship: 'worse',
      additionalReferencesVsFlagship: 'limited',
      webReferenceSearch: 'no',
      bestFor: ['Creative enhancement of existing AI videos', 'Upscaling final clips to 2K or 4K', 'Adding detail and texture to low-resolution footage'],
      avoidFor: ['Faithful archival restoration where new synthesized detail is unwanted', 'Net-new video generation'],
      knownLimitations: ['Requires source video input and may creatively reinterpret details when creativity is raised.'],
    },
    billing: { estimator: 'magnific-video-upscale', defaultCredits: 20, notes: '4 credits per second of the selected source span, rounded up, minimum 20 credits, span capped at 60 seconds.' },
  },
  'topaz-image-enhance': {
    name: 'Topaz Image Upscale',
    provider: 'fal',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsPrompt: false,
      requiresPrompt: false,
      supportsSeed: false,
      supportsMainImageInput: true,
      supportsReferenceImages: false,
      referenceImagesMin: 0,
      referenceImagesMax: 0,
      supportsAnnotationGuide: false,
    },
    averageGenerationSeconds: 7.5,
    billing: { estimator: 'flat', defaultCredits: 1 },
    notes: 'Runs through fal.ai Topaz image upscale endpoint as an enhancement pass on the current generation area.',
  },
  'magnific-image-upscale': {
    name: 'Magnific Image Upscale',
    provider: 'magnific',
    taskRoute: 'diffuse',
    uiCategory: 'Edit',
    uiSubCategory: 'Full Area',
    showInModelSelector: true,
    defaultVisible: true,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsPrompt: false,
      requiresPrompt: false,
      supportsSeed: false,
      supportsMainImageInput: true,
      supportsReferenceImages: false,
      referenceImagesMin: 0,
      referenceImagesMax: 0,
      supportsAnnotationGuide: false,
    },
    // Measured 271s end to end on 2026-08-13 (GenerationLog durationMs 270780). At 120 the
    // UI called a perfectly healthy run "longer than usual" halfway through it.
    averageGenerationSeconds: 271,
    comparisonProfile: {
      lifecycle: 'active',
      positioning: 'High-detail Magnific Precision V2 image upscaling with scale, flavor, sharpen, grain, and ultra-detail controls.',
      visualQualityVsFlagship: 'better',
      speedVsFlagship: 'slower',
      costVsFlagship: 'worse',
      additionalReferencesVsFlagship: 'none',
      webReferenceSearch: 'no',
      bestFor: ['Final image upscaling', 'Print/display prep', 'Adding crisp texture and detail to existing visuals'],
      avoidFor: ['Instruction-driven scene edits', 'Masked/local edits', 'Cases where synthesized detail must be avoided'],
      knownLimitations: ['Requires a source image and may synthesize extra fine detail, especially at higher ultra-detail settings.'],
    },
    billing: { estimator: 'flat', defaultCredits: 4 },
    notes: 'Runs through Magnific Upscaler Precision V2 as a quality pass on the current generation area.',
  },
  // Arrow 2 is billed on the tokens it used: the reservation is the most one call can cost under its
  // output bound, the settlement what Quiver reports (lib/vector-models arrowCreditsForUsage).
  'arrow-2': {
    ...VECTOR_MODEL_BASE,
    provider: 'quiver',
    name: 'Arrow 2',
    cheapEligible: false,
    powerHint: 'Arrow 2, the best-ranked text-to-SVG model: a logo, icon or illustration as clean editable vectors.',
    capabilities: CAPS_VECTOR_BASE,
    averageGenerationSeconds: 40,
    billing: { estimator: 'flat', defaultCredits: arrowReservedCredits('arrow-2'), notes: `Reserved at ${arrowReservedCredits('arrow-2')} credits, the most one call can use; charged on the tokens it actually used, usually far less.` },
    notes: 'Text to SVG on api.quiver.ai/v1/svgs/generations, model arrow-2. The artboard is sent as the viewBox.',
  },
  'arrow-2-telos': {
    ...VECTOR_MODEL_BASE,
    provider: 'quiver',
    name: 'Arrow 2 Telos',
    cheapEligible: false,
    powerHint: 'Arrow 2 Telos, the top SVG model for complex artwork: more detail, longer to run.',
    capabilities: CAPS_VECTOR_BASE,
    averageGenerationSeconds: 90,
    billing: { estimator: 'flat', defaultCredits: arrowReservedCredits('arrow-2-telos'), notes: `Reserved at ${arrowReservedCredits('arrow-2-telos')} credits, the most one call can use; charged on the tokens it actually used, usually far less.` },
    notes: 'Text to SVG on api.quiver.ai/v1/svgs/generations, model arrow-2-telos.',
  },
  'arrow-2-vectorize': {
    name: 'Arrow 2 Vectorize',
    provider: 'quiver',
    taskRoute: 'diffuse',
    outputFormat: 'svg',
    supportedProjectTypes: ['VECTOR'],
    uiCategory: 'Edit',
    uiSubCategory: 'Special',
    showInModelSelector: false,
    defaultVisible: false,
    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsPrompt: false,
      requiresPrompt: false,
      supportsSeed: false,
      supportsMainImageInput: true,
    },
    averageGenerationSeconds: 40,
    billing: { estimator: 'flat', defaultCredits: arrowReservedCredits('arrow-2-vectorize'), notes: `Reserved at ${arrowReservedCredits('arrow-2-vectorize')} credits, the most one call can use; charged on the tokens it actually used, usually far less.` },
    notes: 'The trace of a Vector project: api.quiver.ai/v1/svgs/vectorizations, model arrow-2.',
  },
  'ideogram-vector-1': {
    ...VECTOR_MODEL_BASE,
    provider: 'ideogram',
    name: 'Ideogram Vector',
    cheapEligible: false,
    powerHint: 'Ideogram Vector, an editable SVG from a prompt with smooth gradients and sharp lettering.',
    capabilities: CAPS_VECTOR_BASE,
    averageGenerationSeconds: 20,
    billing: { estimator: 'ideogram-vector-1-quality', defaultCredits: IDEOGRAM_VECTOR_QUALITY_CREDITS.medium, notes: `${IDEOGRAM_VECTOR_QUALITY_CREDITS.low} credits at low quality, ${IDEOGRAM_VECTOR_QUALITY_CREDITS.medium} at medium (default), ${IDEOGRAM_VECTOR_QUALITY_CREDITS.high} at high.` },
    notes: 'Text to SVG on api.ideogram.ai/v2/svg/generate/ideogram-vector-1, polled on /v2/generations. The artboard is sent as its nearest Ideogram aspect ratio.',
  },
  'ideogram-vector-1-vectorize': {
    name: 'Ideogram Vectorize',
    provider: 'ideogram',
    taskRoute: 'diffuse',
    outputFormat: 'svg',
    supportedProjectTypes: ['VECTOR'],
    uiCategory: 'Edit',
    uiSubCategory: 'Special',
    showInModelSelector: false,
    defaultVisible: false,
    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsPrompt: false,
      requiresPrompt: false,
      supportsSeed: false,
      supportsMainImageInput: true,
    },
    averageGenerationSeconds: 15,
    billing: { estimator: 'flat', defaultCredits: 2 },
    notes: 'The trace of a Vector project: api.ideogram.ai/v2/svg/vectorize/ideogram-vector-1, keeping gradients. Ideogram bills $0.10.',
  },
  'recraft-v4-1-vector': {
    ...VECTOR_MODEL_BASE,
    name: 'Recraft V4.1 Vector',
    cheapEligible: true,
    powerHint: 'Recraft V4.1 Vector, a logo, icon or illustration as editable SVG from a prompt, with brand colours.',
    capabilities: CAPS_VECTOR_BASE,
    averageGenerationSeconds: 15,
    // Provisional until the owner sets the grid.
    billing: { estimator: 'flat', defaultCredits: 1 },
    notes: 'Text to vector on fal-ai/recraft/v4.1/text-to-vector. Output is one SVG sized to the artboard.',
  },
  'recraft-v4-1-vector-pro': {
    ...VECTOR_MODEL_BASE,
    name: 'Recraft V4.1 Vector Pro',
    cheapEligible: false,
    powerHint: 'Recraft V4.1 Vector Pro, the same vector output with more geometric detail and finer paths.',
    capabilities: CAPS_VECTOR_BASE,
    averageGenerationSeconds: 20,
    // Provisional until the owner sets the grid.
    billing: { estimator: 'flat', defaultCredits: 2 },
    notes: 'Text to vector on fal-ai/recraft/v4.1/pro/text-to-vector. Output is one SVG sized to the artboard.',
  },
  'recraft-vectorize': {
    name: 'Recraft Vectorize',
    provider: 'fal',
    taskRoute: 'diffuse',
    outputFormat: 'svg',
    uiCategory: 'Edit',
    uiSubCategory: 'Special',
    showInModelSelector: false,
    defaultVisible: false,    cheapEligible: false,
    instructionModel: false,
    capabilities: {
      ...CAPS_EDIT_BASE,
      supportsPrompt: false,
      requiresPrompt: false,
      supportsSeed: false,
      supportsMainImageInput: true,
      supportsReferenceImages: false,
      referenceImagesMin: 0,
      referenceImagesMax: 0,
      supportsAnnotationGuide: false,
    },
    averageGenerationSeconds: 12,
    billing: { estimator: 'flat', defaultCredits: 1 },
    notes: 'Internal layer action that converts a raster layer to SVG through fal.ai Recraft Vectorize.',
  },
} as const);

export type StudioModelId = keyof typeof STUDIO_MODEL_REGISTRY;

export const AGENT_MODEL_REGISTRY = {
  // The only two text models any user is offered (owner, 2026-10-18); every other entry stays for the logs it billed.
  [MAIN_TEXT_MODEL]: {
    id: MAIN_TEXT_MODEL,
    name: 'GPT-6.1 Sol',
    description: 'The main agent model: Studio, Analyst, Source, Slides and every task that reasons.',
    provider: 'openai',
    modelType: 'gpt-azure',
    availableInUi: true,
    cheapEligible: true,
    billing: { estimator: 'flat', defaultCredits: 1 },
  },
  [CHEAP_TEXT_MODEL]: {
    id: CHEAP_TEXT_MODEL,
    name: 'GPT-6 Luna',
    description: 'The cheap model for short secondary tasks: titles, names, triage, judges.',
    provider: 'openai',
    modelType: 'gpt-azure',
    availableInUi: true,
    cheapEligible: true,
    billing: { estimator: 'flat', defaultCredits: 1 },
  },
  'muse-wonder': {
    id: 'muse-wonder',
    name: 'Muse Wonder',
    description: 'Meta\'s first frontier model (Meta Superintelligence Labs, 2026-10-12), built for agents: plans multi-step work, reads images, PDFs and video, 1M-token context. Served through OpenRouter. Requires OPENROUTER_API_KEY; falls back to GPT-5.6 Sol.',
    provider: 'meta',
    modelType: 'openrouter',
    availableInUi: false,
    cheapEligible: false,
    billing: { estimator: 'flat', defaultCredits: 1 },
    fallback: 'gpt-5.6-sol',
  },
  'kimi-k3': {
    id: 'kimi-k3',
    name: 'Kimi K3',
    description: 'Moonshot flagship for complex, long-horizon agent work. Always reasons at max effort, supports a 1M-token context and vision, and is used by Studio for multi-step orchestration. Requires MOONSHOT_API_KEY; falls back to GPT-5.6 Sol.',
    provider: 'moonshot',
    modelType: 'kimi',
    availableInUi: false,
    cheapEligible: false,
    billing: { estimator: 'flat', defaultCredits: 1 },
    fallback: 'gpt-5.6-sol',
  },
  'gpt-6-astra': {
    id: 'gpt-6-astra',
    name: 'GPT-6 Astra',
    description: 'Azure GPT-6 deployment that drives Source: reads the asset, sets the parameters, launches and refines runs under the user\'s mandate. Requires an Azure deployment named "gpt-6-astra"; falls back to GPT-5.6 Sol.',
    provider: 'openai',
    modelType: 'gpt-azure',
    availableInUi: false,
    cheapEligible: false,
    faterOrgOnly: true,
    billing: { estimator: 'flat', defaultCredits: 1 },
    fallback: 'gpt-5.6-sol',
  },
  'gpt-5.6-sol': {
    id: 'gpt-5.6-sol',
    name: 'GPT-5.6 Sol',
    description: 'Azure GPT deployment for high-capacity agent reasoning. Requires an Azure deployment named "gpt-5.6-sol".',
    provider: 'openai',
    modelType: 'gpt-azure',
    availableInUi: false,
    cheapEligible: false,
    faterOrgOnly: true,
    billing: { estimator: 'flat', defaultCredits: 1 },
    fallback: 'gpt-5.5',
  },
  // The Daimon's voice (Azure OpenAI Realtime). Never selectable: it is the Daimon, not a Studio
  // model. Billed on measured audio/text/image tokens (lib/daimon/pricing.ts), so the flat price
  // here is only the placeholder every log is provisioned with before the answer is measured.
  'gpt-realtime-1.5': {
    id: 'gpt-realtime-1.5',
    name: 'Daimon voice',
    description: 'Azure OpenAI Realtime deployment behind the Daimon. Requires an Azure deployment named "gpt-realtime-1.5".',
    provider: 'openai',
    modelType: 'gpt-azure',
    availableInUi: false,
    cheapEligible: false,
    faterOrgOnly: true,
    billing: { estimator: 'flat', defaultCredits: 0.1 },
  },
  'gpt-realtime-2.1': {
    id: 'gpt-realtime-2.1',
    name: 'Daimon voice 2.1',
    description: 'Azure OpenAI Realtime deployment behind the Daimon, billed on measured usage.',
    provider: 'openai',
    modelType: 'gpt-azure',
    availableInUi: false,
    cheapEligible: false,
    faterOrgOnly: true,
    billing: { estimator: 'flat', defaultCredits: 0.1 },
  },
  'claude-opus-4-8': {
    id: 'claude-opus-4-8',
    name: 'Claude Opus 4.8',
    description: 'Anthropic frontier model, default for the Analyst (spreadsheet / financial-model reasoning). Requires ANTHROPIC_API_KEY; falls back to gpt-5.6-sol if unavailable.',
    provider: 'anthropic',
    modelType: 'anthropic',
    availableInUi: false,
    cheapEligible: false,
    billing: { estimator: 'flat', defaultCredits: 1 },
    fallback: 'gpt-5.6-sol',
  },
  'gemini-3.1-pro-preview': {
    id: 'gemini-3.1-pro-preview',
    name: 'Gemini 3.1 Pro',
    description: 'Latest Gemini Pro. NB: GCP provider is policy-blocked until 2026-07-23.',
    provider: 'gemini',
    modelType: 'gemini',
    availableInUi: false, // Google switched off (lib/llmClient.ts isGoogleSwitchedOff): served by its Azure fallback
    cheapEligible: false,
    billing: { estimator: 'flat', defaultCredits: 1 },
    fallback: 'gpt-5.6-sol',
  },
  'gemini-3.8-flash': {
    id: 'gemini-3.8-flash',
    name: 'Gemini 3.8 Flash',
    description: 'Fast multimodal Gemini agent model.',
    provider: 'gemini',
    modelType: 'gemini',
    availableInUi: false, // Google switched off (lib/llmClient.ts isGoogleSwitchedOff): served by its Azure fallback
    cheapEligible: true,
    billing: { estimator: 'flat', defaultCredits: 1 },
    fallback: 'gpt-5.4-mini',
  },
  'gpt-5.5': {
    id: 'gpt-5.5',
    name: 'GPT-5.5',
    description: 'Previous Azure GPT frontier for formulas / financial modelling. Requires an Azure deployment named "gpt-5.5".',
    provider: 'openai',
    modelType: 'gpt-azure',
    availableInUi: false,
    cheapEligible: false,
    billing: { estimator: 'flat', defaultCredits: 1 },
    fallback: 'gpt-5.1',
  },
  'gpt-5.4-mini': {
    id: 'gpt-5.4-mini',
    name: 'GPT-5.4 Mini',
    description: 'Fast Azure GPT variant.',
    provider: 'openai',
    modelType: 'gpt-azure',
    availableInUi: false,
    cheapEligible: true,
    billing: { estimator: 'flat', defaultCredits: 1 },
    fallback: 'gpt-5.1',
  },
  'gpt-5.1': {
    id: 'gpt-5.1',
    name: 'GPT-5.1',
    description: 'Fallback model (not exposed in selector).',
    provider: 'openai',
    modelType: 'gpt-azure',
    availableInUi: false,
    cheapEligible: true,
    billing: { estimator: 'flat', defaultCredits: 1 },
  },

  'gemini-2.5-pro': {
    id: 'gemini-2.5-pro',
    name: 'Gemini 2.5 Pro',
    description: 'Fallback model (not exposed in selector).',
    provider: 'gemini',
    modelType: 'gemini',
    availableInUi: false,
    cheapEligible: false,
    billing: { estimator: 'flat', defaultCredits: 1 },
    fallback: 'gpt-5.1',
  },
  'gemini-2.5-flash': {
    id: 'gemini-2.5-flash',
    name: 'Gemini 2.5 Flash',
    description: 'Fallback model (can be free for legacy route calls).',
    provider: 'gemini',
    modelType: 'gemini',
    availableInUi: false,
    cheapEligible: false,
    billing: { estimator: 'flat', defaultCredits: 0 },
    fallback: 'gpt-5.1',
  },
} as const satisfies Record<string, AgentModelDefinition>;

type AgentModelId = keyof typeof AGENT_MODEL_REGISTRY;
const AGENT_MODEL_RECORD: Record<string, AgentModelDefinition> = AGENT_MODEL_REGISTRY as Record<string, AgentModelDefinition>;

export const STUDIO_MODEL_IDS = Object.keys(STUDIO_MODEL_REGISTRY) as StudioModelId[];
export const STUDIO_MODELS_VISIBLE_IN_SELECTOR = STUDIO_MODEL_IDS.filter((id) => STUDIO_MODEL_REGISTRY[id].showInModelSelector);
export const ALL_MODELS_IN_UI: string[] = [...STUDIO_MODELS_VISIBLE_IN_SELECTOR];
export const SYSTEM_DEFAULT_VISIBLE_MODELS: string[] = STUDIO_MODELS_VISIBLE_IN_SELECTOR.filter(
  (id) => STUDIO_MODEL_REGISTRY[id].defaultVisible,
);
export const INSTRUCTION_MODEL_IDS: string[] = STUDIO_MODEL_IDS.filter(
  (id) => STUDIO_MODEL_REGISTRY[id].instructionModel,
);
export const CHEAP_STUDIO_MODEL_IDS: string[] = STUDIO_MODEL_IDS.filter(
  (id) => STUDIO_MODEL_REGISTRY[id].cheapEligible,
);

// Most to least used listed models across all users over 60 days, as printed by `npm run studio:model-order`.
export const STUDIO_MODEL_SYSTEM_ORDER: readonly StudioModelId[] = [
  'nano-banana-2',
  'nano-banana-2.1',
  'nano-banana-pro',
  'gpt-image-2.5',
  'flux-2-klein-4b',
  'qwen-image-2.1',
  'seedvr2',
  'mai-2.6',
  'mai-2.6-flash',
  'mai-2.5-pro',
  'mai-2.5-flash',
  'nano-banana',
  'gpt-image-2',
  'seedream4.5',
  'grok-imagine-image',
  'seedance-2.0',
  'ideogram-4-edit',
  'grok-imagine-2',
  'qwen-image-3-pro',
  'flux-2-pro',
  'topaz-video-upscale',
  'magnific-image-upscale',
  'qwen-image-2-pro',
  'magnific-video-upscale',
  'topaz-image-enhance',
  'flux-3-image',
  'flux-3-klein',
  'muse-image',
  'kling-3',
  'seedance-2.5',
  'arrow-2',
  'arrow-2-telos',
  'ideogram-vector-1',
  'recraft-v4-1-vector',
  'recraft-v4-1-vector-pro',
];
const STUDIO_MODEL_RANK = new Map<string, number>(
  [...new Set([...STUDIO_MODEL_SYSTEM_ORDER, ...STUDIO_MODEL_IDS])].map((id, index) => [id, index]),
);
const studioModelRank = (id: string): number => STUDIO_MODEL_RANK.get(id) ?? Number.MAX_SAFE_INTEGER;
const STUDIO_MODEL_FAMILY_RANK = new Map(
  STUDIO_MODEL_FAMILIES.map((family) => [family, Math.min(...family.versions.map((version) => studioModelRank(version.id)))]),
);
const familyVersionIndex = (id: string): number => studioModelFamilyOf(id)?.versions.findIndex((version) => version.id === id) ?? -1;
const studioModelUsageCount = (usage: Readonly<Record<string, number>> | null | undefined, id: string): number => {
  const family = studioModelFamilyOf(id);
  return family ? family.versions.reduce((sum, version) => sum + (usage?.[version.id] ?? 0), 0) : usage?.[id] ?? 0;
};

// A family's versions share one list entry, so the family ranks as one model and keeps its own version order inside.
export const compareStudioModels = (usage?: Readonly<Record<string, number>> | null) => {
  const rank = (id: string): number => {
    const family = studioModelFamilyOf(id);
    return (family && STUDIO_MODEL_FAMILY_RANK.get(family)) ?? studioModelRank(id);
  };
  return (a: string, b: string): number =>
    studioModelUsageCount(usage, b) - studioModelUsageCount(usage, a) || rank(a) - rank(b) || familyVersionIndex(a) - familyVersionIndex(b);
};

export const DEFAULT_STUDIO_MODEL_ID: StudioModelId = 'nano-banana-2';

export const pickFavouriteStudioImageModel = (
  usage: Readonly<Record<string, number>> | null | undefined,
  selectableIds: readonly string[],
): string | undefined => {
  const ranked = selectableIds
    .filter((id) => {
      const definition = STUDIO_MODEL_REGISTRY[id as StudioModelId];
      return Boolean(definition)
        && (definition.uiCategory === 'Generate' || definition.uiCategory === 'Edit')
        && definition.capabilities.supportsPrompt
        && !definition.supportedProjectTypes
        && studioModelUsageCount(usage, id) > 0;
    })
    .sort(compareStudioModels(usage));
  for (const id of ranked) {
    const family = studioModelFamilyOf(id);
    if (!family) return id;
    const version = ranked
      .filter((candidate) => studioModelFamilyOf(candidate) === family)
      .sort((a, b) => (usage?.[b] ?? 0) - (usage?.[a] ?? 0) || familyVersionIndex(a) - familyVersionIndex(b))[0];
    if (version && ((usage?.[version] ?? 0) > 0 || family.hiddenUsageSelectsFamily)) return version;
  }
  return undefined;
};

export const AGENT_MODEL_IDS: string[] = (Object.keys(AGENT_MODEL_RECORD) as AgentModelId[]).filter(
  (id) => AGENT_MODEL_RECORD[id]?.availableInUi,
);
export const isAgentModelAllowedForContext = (
  modelId: string,
  context?: { isFaterOrgMember?: boolean | null },
): boolean => {
  const definition = AGENT_MODEL_RECORD[modelId];
  if (!definition) return false;
  if (!definition.faterOrgOnly) return true;
  return Boolean(context?.isFaterOrgMember);
};
export const filterAgentModelIdsForContext = (
  modelIds: readonly string[],
  context?: { isFaterOrgMember?: boolean | null },
): string[] => modelIds.filter((id) => isAgentModelAllowedForContext(id, context));
const CHEAP_AGENT_MODEL_IDS: string[] = AGENT_MODEL_IDS.filter(
  (id) => AGENT_MODEL_RECORD[id]?.cheapEligible,
);
const CHEAP_MODELS_ALLOWLIST: string[] = [...CHEAP_STUDIO_MODEL_IDS, ...CHEAP_AGENT_MODEL_IDS];
const CHEAP_MODELS_ALLOWLIST_SET = new Set(CHEAP_MODELS_ALLOWLIST);

const RUNTIME_MODEL_ALIAS_TO_REGISTRY_ID: Record<string, string> = {
  'black-forest-labs/flux-2-max': 'flux-2-pro',
  'black-forest-labs/flux-2-klein-4b': 'flux-2-klein-4b',
  'xai/grok-imagine-image': 'grok-imagine-image',
  'xai/grok-imagine-image/edit': 'grok-imagine-image',
  'xai/grok-imagine-image/v2.0/text-to-image': 'grok-imagine-2',
  'xai/grok-imagine-image/v2.0/edit': 'grok-imagine-2',
  'meta/muse-image/text-to-image': 'muse-image',
  'meta/muse-image/edit': 'muse-image',
  [LATEST_VIDEO_MODELS['minimax-h3-max'].textEndpoint]: 'minimax-h3-max',
  [LATEST_VIDEO_MODELS['minimax-h3-max'].imageEndpoint]: 'minimax-h3-max',
  [LATEST_VIDEO_MODELS['minimax-h3-max-turbo'].textEndpoint]: 'minimax-h3-max-turbo',
  [LATEST_VIDEO_MODELS['minimax-h3-max-turbo'].imageEndpoint]: 'minimax-h3-max-turbo',
  [LATEST_VIDEO_MODELS['wan-3.0-prime'].textEndpoint]: 'wan-3.0-prime',
  [LATEST_VIDEO_MODELS['wan-3.0-prime'].imageEndpoint]: 'wan-3.0-prime',
  [LATEST_VIDEO_MODELS['grok-imagine-video-1.5'].textEndpoint]: 'grok-imagine-video-1.5',
  [LATEST_VIDEO_MODELS['grok-imagine-video-1.5'].imageEndpoint]: 'grok-imagine-video-1.5',
  [LATEST_VIDEO_MODELS['kling-3-turbo'].textEndpoint]: 'kling-3-turbo',
  [LATEST_VIDEO_MODELS['kling-3-turbo'].imageEndpoint]: 'kling-3-turbo',
  [LATEST_VIDEO_MODELS['wan-3.0-prime'].referenceEndpoint!]: 'wan-3.0-prime',
  [LATEST_VIDEO_MODELS['flux-3-max'].textEndpoint]: 'flux-3-max',
  [LATEST_VIDEO_MODELS['flux-3-max'].imageEndpoint]: 'flux-3-max',
  [LATEST_VIDEO_MODELS['wan-3.0'].textEndpoint]: 'wan-3.0',
  [LATEST_VIDEO_MODELS['wan-3.0'].imageEndpoint]: 'wan-3.0',
  [LATEST_VIDEO_MODELS['wan-3.0'].referenceEndpoint!]: 'wan-3.0',
  [LATEST_VIDEO_MODELS['ltx-2.5-fast'].textEndpoint]: 'ltx-2.5-fast',
  [LATEST_VIDEO_MODELS['ltx-2.5-fast'].imageEndpoint]: 'ltx-2.5-fast',
  [LATEST_VIDEO_MODELS['luma-ray-3.2'].textEndpoint]: 'luma-ray-3.2',
  [LATEST_VIDEO_MODELS['luma-ray-3.2'].imageEndpoint]: 'luma-ray-3.2',
  'blackforestlabs/flux-3/text-to-image': 'flux-3-image',
  'blackforestlabs/flux-3/edit-image': 'flux-3-image',
  'blackforestlabs/flux-3/klein/text-to-image': 'flux-3-klein',
  'blackforestlabs/flux-3/klein/edit-image': 'flux-3-klein',
  'alibaba/qwen-image-3/text-to-image': 'qwen-image-3-pro',
  'alibaba/qwen-image-3/edit': 'qwen-image-3-pro',
  'MAI-Image-2.5-Flash': 'mai-2.5-flash',
  'MAI-Image-2.5-Pro': 'mai-2.5-pro',
  'mai-image-2.5-pro': 'mai-2.5-pro',
  'MAI-Image-2.6-Flash': 'mai-2.6-flash',
  'mai-image-2.6-flash': 'mai-2.6-flash',
  'MAI-Image-2.6': 'mai-2.6',
  'mai-image-2.6': 'mai-2.6',
  'mai-image-2.5-flash': 'mai-2.5-flash',
};

const normalizeRuntimeModelName = (modelName: string): string => {
  const trimmed = modelName.trim();
  if (trimmed.length === 0) return trimmed;
  const plusIndex = trimmed.indexOf('+');
  if (plusIndex > -1) {
    return trimmed.slice(0, plusIndex);
  }
  return trimmed;
};

export const resolveRegistryModelIdFromRuntimeName = (modelName: string): string | null => {
  const normalized = normalizeRuntimeModelName(modelName);
  if (!normalized) return null;

  if (STUDIO_MODEL_REGISTRY[normalized as StudioModelId]) {
    return normalized;
  }
  if (AGENT_MODEL_RECORD[normalized]) {
    return normalized;
  }
  if (RUNTIME_MODEL_ALIAS_TO_REGISTRY_ID[normalized]) {
    return RUNTIME_MODEL_ALIAS_TO_REGISTRY_ID[normalized] || null;
  }

  const leaf = normalized.split('/').pop() || normalized;
  if (RUNTIME_MODEL_ALIAS_TO_REGISTRY_ID[leaf]) {
    return RUNTIME_MODEL_ALIAS_TO_REGISTRY_ID[leaf] || null;
  }
  if (STUDIO_MODEL_REGISTRY[leaf as StudioModelId]) {
    return leaf;
  }
  if (AGENT_MODEL_RECORD[leaf]) {
    return leaf;
  }

  return null;
};

export const getStudioModelReferenceLimits = (runtimeModelName: string): { min: number; max: number } => {
  const modelId = resolveRegistryModelIdFromRuntimeName(runtimeModelName);
  const capabilities = modelId && modelId in STUDIO_MODEL_REGISTRY
    ? STUDIO_MODEL_REGISTRY[modelId as StudioModelId].capabilities
    : null;
  return capabilities?.supportsReferenceImages
    ? { min: capabilities.referenceImagesMin, max: capabilities.referenceImagesMax }
    : { min: 0, max: 0 };
};

export class ReferenceImageBudgetError extends Error {}

/** A reference within its budget keeps its size; otherwise it is scaled, ratio kept, just enough to fit. */
export const fitReferenceImageToBudget = (
  width: number,
  height: number,
  budget: NonNullable<StudioModelDefinition['capabilities']['referenceImageBudget']>,
): { width: number; height: number } => {
  const minSide = budget.minSide ?? 1;
  const scale = Math.min(Math.sqrt(budget.maxPixels / (width * height)), Math.max(1, minSide / Math.min(width, height)));
  if (scale === 1) return { width, height };
  // The epsilon absorbs float noise, so an exact 256 does not round to 255 or 257.
  const round = (value: number) => Math.max(1, scale < 1 ? Math.floor(value + 1e-9) : Math.ceil(value - 1e-9));
  const fitted = { width: round(width * scale), height: round(height * scale) };
  if (fitted.width * fitted.height > budget.maxPixels || Math.min(fitted.width, fitted.height) < minSide) {
    throw new ReferenceImageBudgetError('A reference image is too elongated for this model. Crop it to a less extreme shape and try again.');
  }
  return fitted;
};

export const isStudioModelSupportedForProjectType = (
  modelName: string,
  projectType: ProjectType | string | null | undefined,
): boolean => {
  const modelId = resolveRegistryModelIdFromRuntimeName(modelName);
  if (!modelId || !(modelId in STUDIO_MODEL_REGISTRY)) return false;
  const definition = STUDIO_MODEL_REGISTRY[modelId as StudioModelId];
  const supportedProjectTypes = definition.supportedProjectTypes;
  // A Vector project edits objects: only a model that returns SVG serves it, whatever it declares.
  if (projectType === 'VECTOR') return definition.outputFormat === 'svg';
  return !supportedProjectTypes || projectType == null || (
    typeof projectType === 'string' &&
    supportedProjectTypes.includes(projectType as ProjectType)
  );
};

export const isVectorOutputStudioModel = (modelName: string | null | undefined): boolean => {
  const modelId = modelName ? resolveRegistryModelIdFromRuntimeName(modelName) : null;
  return Boolean(modelId && modelId in STUDIO_MODEL_REGISTRY && STUDIO_MODEL_REGISTRY[modelId as StudioModelId].outputFormat === 'svg');
};

// The video models whose provider call starts from a prompt alone; every other one needs a start frame.
const TEXT_STARTABLE_VIDEO_MODELS = new Set<string>(['seedance-2.0', 'seedance-2.0-fast', 'seedance-2.5', 'gemini-omni-flash-preview', 'wan-2.2-fast', ...LATEST_VIDEO_MODEL_IDS]);
export const videoModelStartsFromText = (modelName: string): boolean =>
  TEXT_STARTABLE_VIDEO_MODELS.has(resolveRegistryModelIdFromRuntimeName(modelName) ?? modelName);
export const videoModelNeedsStartImage = (modelName: string): boolean => {
  const modelId = resolveRegistryModelIdFromRuntimeName(modelName);
  if (!modelId || !(modelId in STUDIO_MODEL_REGISTRY)) return false;
  const entry = STUDIO_MODEL_REGISTRY[modelId as StudioModelId];
  return entry.taskRoute === 'film' && entry.uiSubCategory !== 'Edit' && !TEXT_STARTABLE_VIDEO_MODELS.has(modelId);
};

export const filterStudioModelIdsForProjectType = (
  modelIds: readonly string[],
  projectType: ProjectType | string | null | undefined,
): string[] => modelIds.filter((modelId) => isStudioModelSupportedForProjectType(modelId, projectType));

export const getStudioModelProjectCompatibilityError = (
  modelName: string,
  projectType: ProjectType | string | null | undefined,
): string | null => {
  const modelId = resolveRegistryModelIdFromRuntimeName(modelName);
  if (!modelId || !(modelId in STUDIO_MODEL_REGISTRY)) return null;
  if (isStudioModelSupportedForProjectType(modelId, projectType)) return null;
  const definition = STUDIO_MODEL_REGISTRY[modelId as StudioModelId];
  if (projectType === 'VECTOR') return 'A Vector project takes a vector model. Select one from the model gallery.';
  if (definition.outputFormat === 'svg') return 'This model generates vectors and needs a Vector project.';
  return definition.uiSubCategory === 'Masked'
    ? 'This masked editing model requires a Canvas project. Select another model for this project.'
    : 'This model requires a Canvas project. Select another model for this project.';
};

export const shouldUnlockIncompatibleStudioModelPin = (
  locked: boolean,
  modelName: string,
  projectType: ProjectType | string | null | undefined,
): boolean => locked && !isStudioModelSupportedForProjectType(modelName, projectType);

/**
 * The cost-efficient models a restricted plan can actually use here. Only models this server cannot
 * run are dropped from the verdict's withheld families: billing-withheld ones stay listed as before.
 */
export const restrictedPlanStudioModelIds = (
  projectType: ProjectType | string | null | undefined,
  withheldFamilies: readonly string[],
): string[] => filterStudioModelIdsForProjectType(CHEAP_STUDIO_MODEL_IDS, projectType)
  .filter((modelId) => !isWithheldAsUnrunnable(modelId, withheldFamilies));

const isModelCheapEligible = (modelName: string): boolean => {
  const normalized = normalizeRuntimeModelName(modelName);
  if (!normalized) return false;

  if (CHEAP_MODELS_ALLOWLIST_SET.has(normalized)) return true;
  if (CHEAP_MODELS_ALLOWLIST.some((allowed) => normalized.endsWith(allowed))) return true;

  const resolvedId = resolveRegistryModelIdFromRuntimeName(normalized);
  if (!resolvedId) return false;

  if (STUDIO_MODEL_REGISTRY[resolvedId as StudioModelId]) {
    return STUDIO_MODEL_REGISTRY[resolvedId as StudioModelId].cheapEligible;
  }
  if (AGENT_MODEL_RECORD[resolvedId]) {
    return AGENT_MODEL_RECORD[resolvedId].cheapEligible;
  }
  return false;
};

export const MODEL_SELECTOR_MAPPINGS: Record<
  string,
  { name: string; category: StudioModelCategory; subCategory?: StudioModelSubCategory }
> = STUDIO_MODELS_VISIBLE_IN_SELECTOR.reduce((acc, id) => {
  const definition = STUDIO_MODEL_REGISTRY[id];
  if (definition.uiCategory) {
    acc[id] = {
      name: definition.name,
      category: definition.uiCategory,
      subCategory: definition.uiSubCategory,
    };
  }
  return acc;
}, {} as Record<string, { name: string; category: StudioModelCategory; subCategory?: StudioModelSubCategory }>);

export const MODEL_POWER_HINTS = STUDIO_MODEL_IDS.reduce<Record<string, string>>((acc, id) => {
  const hint = STUDIO_MODEL_REGISTRY[id].comparisonProfile?.positioning || STUDIO_MODEL_REGISTRY[id].powerHint;
  if (typeof hint === 'string' && hint.length > 0) {
    acc[id] = hint;
  }
  return acc;
}, {});

export const estimateStudioModelCredits = (
  modelId: string,
  params?: Record<string, unknown> | null,
  context?: CostEstimatorContext,
): number | null => {
  const definition = STUDIO_MODEL_REGISTRY[modelId as StudioModelId];
  if (!definition) return null;
  return estimateCostFromEstimator(definition.billing.estimator, params, context, definition.billing.defaultCredits);
};

// Renders the credit price of the models a caller can actually select, so the agent prompt
// quotes the registry instead of a second, hand-written price list. Iteration follows
// STUDIO_MODEL_IDS rather than the caller's array: the output must be byte-stable across
// turns or it invalidates the prompt cache. A model whose price depends on its parameters
// is described by its billing.notes; without one it is silently absent from the chapter,
// which lib/model-registry.credits.test.ts refuses.
export const describeStudioModelCredits = (modelIds: string[]): string => {
  const selected = new Set(modelIds);
  const flatByCredits = new Map<number, string[]>();
  const notedLines: string[] = [];

  for (const id of STUDIO_MODEL_IDS) {
    if (!selected.has(id)) continue;
    const { estimator, defaultCredits, notes } = STUDIO_MODEL_REGISTRY[id].billing;
    if (notes) {
      notedLines.push(`    - \`${id}\`: ${notes}`);
    } else if (estimator === 'flat') {
      flatByCredits.set(defaultCredits, [...(flatByCredits.get(defaultCredits) ?? []), id]);
    }
  }

  const lines = [...flatByCredits.entries()]
    .sort(([a], [b]) => a - b)
    .map(([credits, ids]) => `    - **${credits} credit${credits === 1 ? '' : 's'}:** ${ids.map((id) => `\`${id}\``).join(', ')}`);
  lines.push(...notedLines);
  return lines.join('\n');
};

export const getStudioModelMinimumGenerations = (modelId: string): number => {
  const definition = STUDIO_MODEL_REGISTRY[modelId as StudioModelId];
  if (!definition) return 1;
  const configuredMinimum = definition.minimumGenerations;
  if (typeof configuredMinimum !== 'number' || !Number.isFinite(configuredMinimum)) return 1;
  return Math.max(1, Math.floor(configuredMinimum));
};

export const getStudioModelMaximumGenerations = (modelId: string): number | null => {
  const configuredMaximum = STUDIO_MODEL_REGISTRY[modelId as StudioModelId]?.maximumGenerations;
  if (typeof configuredMaximum !== 'number' || !Number.isFinite(configuredMaximum)) return null;
  return Math.max(getStudioModelMinimumGenerations(modelId), Math.floor(configuredMaximum));
};

export const studioModelRequiresPrompt = (modelName: string): boolean => {
  const resolvedId = resolveRegistryModelIdFromRuntimeName(modelName);
  if (!resolvedId) return false;
  const definition = STUDIO_MODEL_REGISTRY[resolvedId as StudioModelId];
  return definition?.capabilities.requiresPrompt === true;
};

export const enforceStudioModelMinimumGenerations = (
  modelId: string,
  requestedGenerations?: number | null,
): number => {
  const minimum = getStudioModelMinimumGenerations(modelId);
  const requested =
    typeof requestedGenerations === 'number' && Number.isFinite(requestedGenerations)
      ? Math.floor(requestedGenerations)
      : minimum;
  const atLeastMinimum = Math.max(minimum, Math.max(1, requested));
  const maximum = getStudioModelMaximumGenerations(modelId);
  return maximum === null ? atLeastMinimum : Math.min(maximum, atLeastMinimum);
};

export const estimateAgentModelCredits = (modelId: string): number | null => {
  const definition = AGENT_MODEL_RECORD[modelId];
  if (!definition) return null;
  return definition.billing.defaultCredits;
};

const resolveIsRestrictedForModels = (
  accessScope: UserAccessScopeLike,
  organisationRestricted: boolean | null | undefined,
): boolean => {
  if (accessScope === 'ALL_MODELS') return false;
  if (accessScope === 'CHEAP_MODELS_ONLY') return true;
  return organisationRestricted ?? false;
};

export const isModelAllowedForAccessScope = (
  modelId: string,
  accessScope: UserAccessScopeLike,
  organisationRestricted?: boolean | null,
): boolean => {
  const restricted = resolveIsRestrictedForModels(accessScope, organisationRestricted);
  if (!restricted) return true;
  return isModelCheapEligible(modelId);
};

export const buildStudioFrontendModelAccessState = (input: {
  organisationId?: string | null;
  accessScope?: UserAccessScopeLike;
  organisationRestricted?: boolean | null;
  /**
   * Model families the caller cannot spend on, whatever their scope: the billing carve-out of
   * lib/access-suspension.ts, resolved by the caller because only it knows the clock.
   */
  withheldModelFamilies?: readonly string[];
  modelVisibility?: Record<string, ModelVisibilityPreference>;
  modelParamsById?: Record<string, Record<string, unknown> | undefined>;
}): FrontendModelAccessState[] => {
  const isRestricted = resolveIsRestrictedForModels(input.accessScope, input.organisationRestricted);
  const withheldFamilies = input.withheldModelFamilies || [];
  const visibility = input.modelVisibility || {};
  const modelParamsById = input.modelParamsById || {};

  return STUDIO_MODELS_VISIBLE_IN_SELECTOR.map((modelId) => {
    const modelDef = STUDIO_MODEL_REGISTRY[modelId];
    const pref = visibility[modelId];
    const isVisibleByDefault = modelDef.defaultVisible;
    const baseVisible = pref === 1 ? true : pref === 0 ? false : isVisibleByDefault;
    const hasAccess = (!isRestricted || modelDef.cheapEligible)
      && !isModelInFamilies(modelId, withheldFamilies);
    const isInActiveList = baseVisible && hasAccess;
    const estimatedCostCredits = estimateStudioModelCredits(modelId, modelParamsById[modelId] || null, { organisationId: input.organisationId });

    return {
      modelId,
      hasAccess,
      isVisibleByDefault,
      userVisibilityPreference: pref,
      isInActiveList,
      isCheapModel: modelDef.cheapEligible,
      estimatedCostCredits,
    };
  });
};

export const getActiveSelectableStudioModelIds = (input: {
  accessScope?: UserAccessScopeLike;
  organisationRestricted?: boolean | null;
  withheldModelFamilies?: readonly string[];
  modelVisibility?: Record<string, ModelVisibilityPreference>;
}): string[] => {
  return buildStudioFrontendModelAccessState(input)
    .filter((state) => state.isInActiveList)
    .map((state) => state.modelId);
};

/** The agent's two lists. A model this server cannot run is hidden, never "disabled": no setting would re-enable it. */
export const resolveDirectorModelLists = (
  input: Parameters<typeof buildStudioFrontendModelAccessState>[0],
): { selectableModels: string[]; disabledModels: string[] } => {
  const states = buildStudioFrontendModelAccessState(input);
  return {
    selectableModels: states.filter((state) => state.isInActiveList).map((state) => state.modelId),
    disabledModels: states
      .filter((state) => !state.isInActiveList && !isModelInFamilies(state.modelId, unrunnableModelFamilies()))
      .map((state) => state.modelId),
  };
};

const DIRECTOR_MODEL_DOC_INPAINTING_PREAMBLE = `Edit Models (Inpainting)

    These models regenerate ONLY a user-painted mask region and leave the rest of the image untouched. Prefer a maskless instruction edit for general changes, but use an inpainting model when the user has painted a mask, explicitly asks for a masked / inpaint / region-precise edit, or needs a localized change that must not touch the rest of the frame.`;

const DIRECTOR_MODEL_DOC_MASKLESS_PREAMBLE = `Edit Models (Maskless & Instruction-Driven)

    These models intelligently modify existing images based on direct instructions, without requiring a mask. This is the primary and default method for all editing tasks.
    All these models can receive the annotated canvas as extra ref image.
    They can also generate image without any base.`;

const DIRECTOR_MODEL_DOC_VIDEO_PREAMBLE = `Video Models

    All video models share the following characteristics and parameters:
    - **Input Image**: The generation is always initiated from a start image, which is the composite of all visible layers within the Generation Area (GA).
    - **\`prompt\`**: A text description of the desired motion, action, or style transformation over time.
    - **\`duration\`**: The length of the generated video in seconds. Available options are typically 5 or 10.
    - **\`seed\`**: A number for initializing the generation. A value of -1 (the default) results in a random seed. Using a specific number allows for reproducible results if all other inputs are identical.`;

const DOC_BLOCK_SEEDVR2 = (isList = false) => `    **seedvr2 [Edit/Full Area]** - SeedVR2 is a high-resolution image upscaler. It preserves the source aspect ratio and targets an output from 4 to 40 Mpx in steps of 4. It runs on fal.ai (fal-ai/seedvr/upscale/image), or exclusively on local ComfyUI when COMFYUI_SEEDVR2_ENABLED=true; a local failure is reported, never retried on fal.
    - Prompting Style: no prompt; this workflow is image-conditioned.
${isList ? `    - Input: the explicitly selected source image.` : `    - Input: the current Generation Area image.`}
    - Parameters:
        - \`inputMegaPixels\` [d: 2] [o: auto, 0.5, 1, 2, 3, 4, 5, 10, 16] // requested input resolution; auto uses the highest logical input (16 Mpx), then effective rendering uses min(requested input, output/64)
        - \`outputMegaPixels\` [d: 32] [o: 4, 8, 12, 16, 20, 24, 28, 32, 36, 40] // target output megapixels; billed 1 credit up to 16 Mpx, then +0.25 per additional 4 Mpx
        - \`seed\` [d: -1] // -1 chooses a random seed; a non-negative integer is reproducible
    - Cloud runtime (fal): the model factor is sent as upscale_factor, capped at 8 like the local workflow; a random seed (-1) is omitted, a non-negative seed is forwarded; output is webp.
      WebP caps each side at 16383 px, so a very wide or tall frame at a high output target can exceed it on fal only; lower outputMegaPixels for such frames.
    - Local runtime: COMFYUI_SEEDVR2_ENABLED=true and the SeedVR2 API workflow. Tiling parameters are fixed in the workflow and are not user controls. ComfyUI fixes denoise to 1, bicubic resizing, wavelet color correction, and the FP8 7B model.
    - Post-processing: ComfyUI performs wavelet color correction and the effective non-editable harmonize setting is On/strict; Fater applies a conditional alpha edge fade radius 64 and no preserve-higher-resolution masking.`;

const DOC_BLOCK_QWEN_IMAGE_2_1 = `    **qwen-image-2.1 [Edit/Full Area]** - Qwen Image 2.1 instruction editor with a Qwen3-VL text encoder. It runs on a local ComfyUI server where one is enabled (COMFYUI_QWEN21_ENABLED=true), else on Fater's own hosted copy of the same workflow; a failure on either is reported and never rerouted to the other. The first hosted run after an idle spell takes about two more minutes to start. Canvas projects only: not available in List.
    - Input: the current Generation Area image, required. The output keeps the input ratio; its size is set by \`resolution\` (0.5-4 MP, 32px grid, no side above 2048).
    - Mask: Ignored.
    - Ref Img: true [count: 0-9], tagged in the prompt as <image2> to <image10> (<image1> is the main image); the annotation guide takes one of the nine slots.
    - Framing: the model regenerates the whole image and can recompose it on a vague prompt; always state that framing, camera and element positions are kept unless the user asks otherwise. The result is auto-aligned onto the source to absorb the residual few-pixel drift, then its colour drift on the unchanged regions is harmonized only when the user's setting (off by default) or the generation's autoColorMatch=true turns it on. Each reference is downscaled to at most 1 Mpx before upload.
    - Transparency: on the local server alpha is preserved on the main image, the references and the PNG output, and transparent regions of the Generation Area reach the model as transparent and may stay transparent. The hosted copy keeps the references' alpha but flattens the main image to RGB, so its transparent regions do not survive.
    - cfg above 1 guides against \`negativePrompt\` (empty by default) and roughly doubles the run time; in local tests it fixed small far-background faces better but made gender/age drift more frequent. \`negativePrompt\` and \`cfgOneLastSteps\` do nothing at cfg 1.
    - \`noiseLevel\` below 1 starts from the source noised to that level instead of pure noise: 0.985 pins the layout (recasting the people of a street photo); keep 1 for edits that move or add things. It pins the layout only above the schedule's second sigma (0.9795 at 25 steps, 0.9899 at 50), so 0.985 holds at 25 steps and 40-50 steps need about 0.99.
    - The app derives \`borderLock\` and \`referenceScale\` (the main image the encoder reads, at the layers' native pixel density); never set either.
    - **Parameters:**
        - \`prompt\` [required] // Instruction driven: name what changes and what must be preserved.
        - \`resolution\` [d: 1 MP] [o: 0.5 MP, 1 MP, 2 MP, 3 MP, 4 MP] // One value sizes both the input and the output, at the source ratio, on a 32px grid, no side above 2048px (16:9 at 4 MP is 2048x1152).
        - \`steps\` [d: 25] [r: 1-50] // Fewer steps are faster, more steps refine detail.
        - \`cfg\` [d: 3] [r: 1-4] // 3 draws added people and small faces properly; 1 halves the run time but leaves them malformed.
        - \`negativePrompt\` [d: ''] // What cfg above 1 steers away from.
        - \`cfgOneLastSteps\` [d: 0] [r: 0 to steps-1] // With cfg above 1, the last N steps run at cfg 1, without the negative pass.
        - \`noiseLevel\` [d: 1] [r: 0.9-1] // First sigma of the schedule; 1 is pure noise.
        - \`seed\` [d: -1] // -1 is random; a non-negative integer is reproducible.
        - \`add_annotations_as_ref\` [d: false]`;

const DOC_BLOCK_FLUX_2_PRO = (isList = false) => `    **flux-2-pro [Edit/Full Area]** - Latest Flux model for instruction-driven edits. Use it when Flux Kontext levels of control are required but you also need higher megapixel outputs.
    - List @persona scenes: supported. Every original photo shares the eight-image capacity with edit sources; brand style is analyzed text; ordinary scenes omit the synthesized panel. Private inputs must fit the 10 MB encoded budget. Never discard identity photos to fit.
    - Hosted provider: black-forest-labs/flux-2-max. Square 4 MP output was observed at native 2000x2000, not 2048x2048; preserve native pixels instead of upscaling to an assumed 2K size.
    - Mask: Ignored.
    - Ref Img: true [count: 0-8]
${isList ? `    - Proc Area: full output image` : `    - Proc Area: Generation Area`}
    - **Parameters:**
        - \`prompt\`
        - \`resolution\` [d: 1 MP] [o: match_input_image, 0.5 MP, 1 MP, 2 MP, 4 MP]
${isList ? `` : `        - \`add_annotations_as_ref\` [d: false]`}`;

const DOC_BLOCK_NANO_BANANA_PRO = (isList = false) => `    **nano-banana-pro [Edit/Special]** - The default maskless editor for large-scale instruction-driven image edits. Powered by Gemini 3 Pro Image Preview, it delivers high-fidelity 4K/2K/1K outputs and multiple reference images.
    - List @persona scenes: supported through private native references, up to fourteen images including all originals and edit sources. Brand-folder images supply analyzed style text, not additional scene images. Ordinary scenes omit the synthesized panel; explicit /factory batches retain their reserved panel references.
    - Mask: Ignored.
    - Ref Img: true [count: 0-5]
${isList ? `    - Proc Area: full output image` : `    - Proc Area: Generation Area`}
    - **Parameters:**
        - \`prompt\` // Instruction driven.
${isList ? `        - \`imageSize\` [d: 1K] [o: 4K, 2K, 1K] // Controls output resolution at the requested output dimensions.` : `        - \`imageSize\` [d: 1K] [o: 4K, 2K, 1K] // Controls output resolution within the GA bounds.`}
        - \`enable_google_search\` [d: false] // Adds Google Search tool access for fresh facts/context when true.
${isList ? `` : `        - \`add_annotations_as_ref\` [d: false]`}`;

const DOC_BLOCK_NANO_BANANA = `    **nano-banana [Edit/Special]** - Gemini 2.5 Flash Image; use explicit image-edit instructions.
    - List @persona scenes: native private references, at most 3 total images including the source, all persona/archetype originals; brand style is analyzed text. Private encoded inputs must fit the app's 10 MB budget. Never drop originals or switch models silently.
    - Persona originals define identity; archetypes define pose, setting and product. Request one continuous scene, not a reference grid.
    - Parameters: \`prompt\`; \`aspect_ratio\` [d: auto] uses the requested supported shape.`;

const DOC_BLOCK_FLUX_KLEIN = `    **flux-2-klein-4b [Edit/Full Area]** - Fast hosted Flux image edits; prefer explicit instructions about changes and elements to preserve.
    - List @persona scenes require the hosted multi-image adapter, at most 5 total images including source, every persona/archetype original; brand style is analyzed text. Private encoded inputs must fit the app's 10 MB budget. Configured local Klein cannot preserve these references and is rejected; never silently reroute or discard photos.
    - Persona originals define identity; archetype faces are replaceable actors. Parameters: \`prompt\`, \`seed\`, \`aspect_ratio\` [d: 1:1], \`resolution\` [d: 1 MP] [o: match_input_image, 0.5 MP, 1 MP, 2 MP, 4 MP]. Preserve the user's selected resolution.`;

const DOC_BLOCK_NANO_BANANA_2 = (isList = false) => `${isList ? `    **nano-banana-2 [Edit/Special]** - Fast instruction-driven image generation and editing with native multi-reference input. Safety blocks end recovery; never switch models to bypass one.` : `    **nano-banana-2 [Edit/Special]** - The default maskless editor for small-scale instruction-driven image edits. Use it when you need faster retries, when \`nano-banana-pro\` hits a content filter, or when the user prefers a lower-resolution pass. Shares the same prompt, mask, and reference-image behavior.`}
    - List persona scenes retain all canonical original photos unless an explicit validated photo selection is supplied; retries retain that selection. All selected archetype originals remain separate within the 14-image total; brand style is analyzed text. Never silently omit photos to fit capacity. No synthesized panel or model switch.
    - Credits: 1 at 1K, 1.5 at 2K (default), 2 at 4K.
    - Mask: Ignored.
    - Ref Img: true [count: 0-14]
${isList ? `    - Proc Area: full output image` : `    - Proc Area: Generation Area`}
    - **Parameters:**
        - \`prompt\` // Instruction driven.
${isList ? `        - \`imageSize\` [d: 2K] [o: 4K, 2K, 1K] // Controls output resolution at the requested output dimensions.` : `        - \`imageSize\` [d: 2K] [o: 4K, 2K, 1K] // Controls output resolution within the GA bounds.`}
        - \`enable_google_search\` [d: false] // Adds Google Search tool access for fresh facts/context when true.
${isList ? `` : `        - \`add_annotations_as_ref\` [d: false]`}`;

const DOC_BLOCK_NANO_BANANA_21 = (isList = false) => `${isList ? `    **nano-banana-2.1 [Edit/Special]** - Google's Nano Banana 2.1, the newer Nano Banana 2: the same instruction-driven generation and editing with native multi-reference input, with sharper output, better in-image text and better consistency across turns. Safety blocks end recovery; never switch models to bypass one.` : `    **nano-banana-2.1 [Edit/Special]** - Google's Nano Banana 2.1, the newer Nano Banana 2: the same maskless instruction-driven editor as \`nano-banana-2\`, with sharper output, better in-image text and better consistency across turns. Same prompt, mask and reference-image behavior as \`nano-banana-2\`, and 0.1 credit cheaper at 2K.`}
    - It always reasons before drawing (Google's default thinking level is medium, minimal on \`nano-banana-2\`; the app sets none), so expect somewhat longer runs. Its speed figure is Nano Banana 2's until a run is measured.
    - List persona scenes retain all canonical original photos unless an explicit validated photo selection is supplied; retries retain that selection. All selected archetype originals remain separate within the 14-image total, of which Google documents at most 4 characters and 10 objects; brand style is analyzed text. Never silently omit photos to fit capacity. No synthesized panel or model switch.
    - Credits: 1 at 1K, 1.4 at 2K (default), 2 at 4K.
    - Mask: Ignored.
    - Ref Img: true [count: 0-14]
${isList ? `    - Proc Area: full output image` : `    - Proc Area: Generation Area`}
    - **Parameters:**
        - \`prompt\` // Instruction driven.
${isList ? `        - \`imageSize\` [d: 2K] [o: 4K, 2K, 1K] // Controls output resolution at the requested output dimensions.` : `        - \`imageSize\` [d: 2K] [o: 4K, 2K, 1K] // Controls output resolution within the GA bounds.`}
        - \`enable_google_search\` [d: false] // Adds Google Search tool access for fresh facts/context when true.
${isList ? `` : `        - \`add_annotations_as_ref\` [d: false]`}`;

const DOC_BLOCK_GROK_IMAGINE_2 = (isList = false) => `    **grok-imagine-2 [Edit/Full Area]** - xAI Grok Imagine 2 on fal: instruction-driven generation and maskless editing, at the same credit price as the flagship. Quality and speed against the flagship are not measured; do not promise either.
    - Credits: 1 at 1K, 1.5 at 2K (default).
    - Mask: Ignored.
    - Ref Img: true [count: 0-2]
${isList ? `    - Proc Area: full output image` : `    - Proc Area: Generation Area`}
    - At most 3 input images in total, the edit source included. Never drop a required original to fit that budget; route reference-heavy work to the flagship instead.
    - **Parameters:**
        - \`prompt\` // Instruction driven.
        - \`resolution\` [d: 2K] [o: 2K, 1K] // The only paid lever; it is the whole price difference.
        - The shape is derived, not settable: with a source image the app keeps that image's shape, and without one it snaps ${isList ? 'the requested output shape' : 'the Generation Area'} to the closest supported ratio between 1:2 and 2:1 (plus 20:9 and 9:20).
        - Aspect ratio, quality, output format, batch size and sync mode are fixed by the app and are not settable.`;

const DOC_BLOCK_MUSE_IMAGE = (isList = false) => `    **muse-image [Edit/Full Area]** - Meta Muse Image on fal: instruction-driven generation and maskless editing. Meta states that it plans the image, may search the web for real references on its own and checks its result before returning; none of this is observed. Quality and speed against the flagship are not measured; do not promise either.
    - Credits: 1.
    - Mask: Ignored.
    - Ref Img: true [count: 0-9]
${isList ? `    - Proc Area: full output image` : `    - Proc Area: Generation Area`}
    - At most 10 input images in total, the edit source included. Never drop a required original to fit that budget; route heavier reference work to the flagship instead.
    - **Parameters:**
        - \`prompt\` // Instruction driven; the only parameter${isList ? ' besides an explicit aspect_ratio' : ''}.
        - Shape: ${isList ? 'an explicitly requested aspect_ratio (width:height) wins; otherwise the app sends the source image\'s shape, or the requested output shape without one,' : 'derived, not settable: the app sends the source image\'s shape, or the Generation Area\'s without one,'} as width:height clamped between 1:16 and 16:1. Never auto.
        - The output is about 2.5 Mpx whatever the shape, per fal's documentation: no resolution tier, no 4K.
        - Output format, batch size and sync mode are fixed by the app; there is no seed, mask or negative prompt.`;

const DOC_BLOCK_FLUX_3_IMAGE = (isList = false) => `    **flux-3-image [Edit/Full Area]** - Black Forest Labs FLUX.3 image on fal: prompt-driven text-to-image and maskless main-image edits, up to 10 input images including the source. Not the \`flux-3\` video model. Quality and speed against the flagship are not measured; do not promise either.
    - Endpoints: \`blackforestlabs/flux-3/text-to-image\` without an input image, \`blackforestlabs/flux-3/edit-image\` with one; the app chooses.
    - Credits: by resolution tier, quoted in the credit list.
    - Mask: Ignored.
    - Ref Img: true [count: 0-9]
${isList ? `    - Proc Area: full output image` : `    - Proc Area: Generation Area`}
    - Input order is fixed: the source is always image 1, then the user's references in the order supplied (image 2 to image 10); without a source the references start at image 1. At most 10 input images in total; never drop a required original to fit that budget, route heavier reference work to the flagship instead.
    - **Parameters:**
        - \`prompt\` [required] // Natural prose for a generation, an instruction for an edit.
        - \`resolution\` [d: 2k] [o: 512sq, 768sq, 1k, 2k, 4k] // Equal-pixel-area tiers per BFL (768sq is a 768x768 pixel area, 1k about 1 Mpx, 2k about 4 Mpx, 4k about 16 Mpx); the dimensions follow the aspect ratio, none is forced square. The only paid lever.
        - \`aspect_ratio\` [o: auto, 21:9, 2:1, 16:9, 3:2, 7:5, 4:3, 5:4, 1:1, 4:5, 3:4, 5:7, 2:3, 9:16, 1:2] // ${isList ? 'An explicitly requested preset wins; otherwise' : 'Derived, not settable:'} with a source image the app sends \`auto\`, which per BFL keeps image 1's framing; without one it snaps ${isList ? 'the requested output shape' : 'the Generation Area'} to the closest preset.
        - Fixed by the app, not settable: prompt expansion off, safety tolerance, PNG output, sync mode. There is no seed, mask or negative prompt.
    - Known limitations: about 2 minutes at 2k, measured on a single edit; per fal's documentation 4k can take several minutes.`;

const DOC_BLOCK_FLUX_3_KLEIN = (isList = false) => `    **flux-3-klein [Edit/Full Area]** - Black Forest Labs FLUX 3 Image Klein on fal, the open-weights FLUX 3: prompt-driven text-to-image and maskless main-image edits, up to 10 input images including the source. Not \`flux-3-image\` and not the older \`flux-2-klein-4b\`.
    - Endpoints: \`blackforestlabs/flux-3/klein/text-to-image\` without an input image, \`blackforestlabs/flux-3/klein/edit-image\` with one; the app chooses.
    - Credits: by resolution tier, quoted in the credit list.
    - Mask: Ignored.
    - Ref Img: true [count: 0-9]
${isList ? `    - Proc Area: full output image` : `    - Proc Area: Generation Area`}
    - Input order is fixed: the source is always the first image, then the user's references in the order supplied; without a source the references start at the first image. At most 10 input images in total; never drop a required original to fit that budget.
    - **Parameters:**
        - \`prompt\` [required] // What changes and what stays, in plain words; the provider expands it into the full scene caption the model needs.
        - \`resolution\` [d: 2k] [o: 1k, 2k, 4k] // Pixel area of the output (about 1, 4 and 16 Mpx). The app sends an explicit width and height: ${isList ? 'the requested output shape' : 'the Generation Area'}'s shape at that area, each side a multiple of 16 between 256 and 4096. The only paid lever.
        - Fixed by the app, not settable: prompt expansion on, safety checker on, PNG output, one image. There is no seed, mask, aspect preset or negative prompt; the layout boxes fal exposes are not wired.
    - Measured: on 83 URW Concept Studio edits (2026-10-15) its blind closeness to the designer's approved result averaged 54/100, against 63 for nano-banana-2.1 and 67 for gpt-image-2.5; 58 on shopfronts, 48 on development projects. Prefer the flagship when fidelity to the brief matters.`;

const DOC_BLOCK_SEEDREAM_45 = (isList = false) => `    **seedream4.5 [Edit/Special]** - A powerful model for high-resolution editing and upscaling. Best used for full-frame generations. It has a tendency to over-modify unrequested areas; always include a list of elements to maintain in the prompt. The provider refuses prompts over 4,000 characters; in List the app drops repeated guidance to fit, so keep your own brief short.
    - List @persona scenes use every private original plus selected archetype originals within the 14-image total; brand style is analyzed text; private encoded inputs must fit the app's 10 MB budget. No synthesized panel, reference omission or silent model switch. Persona originals define identity; archetypes define pose/product/setting, never a blended face.
    - Mask: Ignored.
    - Ref Img: true [count: 0-14, including the edit source]
${isList ? `    - Proc Area: full output image` : `    - Proc Area: Generation Area`}
    - List personas/archetypes and zero-reference List scenes without an edit source use the requested target aspect ratio, not the first reference image shape. Supported target ratios: 1:1, 4:3, 3:4, 4:5, 5:4, 16:9, 9:16, 3:2, 2:3, 21:9, 9:21; other shapes are rejected before billing. Source-backed edits retain match-input geometry.
    - **Parameters:**
        - \`prompt\` // Instruction driven.
        - \`outputMegaPixels\` [d: 16] [o: 4, 16] // 4 = 2K, 16 = 4K.
${isList ? `` : `        - \`add_annotations_as_ref\` [d: false]`}`;

const DOC_BLOCK_GPT_IMAGE_2 = (isList = false) => `    **gpt-image-2 [Edit/Basic]** - Azure OpenAI GPT-image-2 image edit endpoint (main image required).
    - Mask: Ignored.
    - Ref Img: false
${isList ? `    - Proc Area: full output image (main image edit).` : `    - Proc Area: Generation Area (main image edit).`}
    - **Parameters:**
        - \`prompt\` // Main edit instruction.`;

const DOC_BLOCK_MAI_2_5_FLASH = (isList = false) => `    **mai-2.5-flash [Edit/Basic]** - Azure OpenAI MAI-Image-2.5-Flash image edit endpoint (main image required).
    - No persona or object scenes: they are refused. Use Nano Banana 2 or Flux 2 for @persona or @object requests.
    - Mask: Ignored.
    - Ref Img: false
${isList ? `    - Proc Area: full output image (main image edit). Output format is PNG (max 1,048,576 pixels, min 768px width/height).` : `    - Proc Area: Generation Area (main image edit). Output format is PNG (max 1,048,576 pixels, min 768px width/height).`}
    - **Parameters:**
        - \`prompt\` // Main edit instruction (up to 32,000 tokens context).`;

const DOC_BLOCK_QWEN_IMAGE_3_PRO = (isList = false) => `    **qwen-image-3-pro [Edit/Special]** - Qwen 3.0 on fal (alibaba/qwen-image-3). The strongest Qwen for accurate in-image text, complex layouts and photographic detail; composite benchmarks (aesthetics + prompt alignment + realism) place it above nano-banana-2.
    - Credits: 1 at 1K, 1.5 at 2K (default).
    - Mask: Ignored.
    - Ref Img: true [count: 0-2]
${isList ? `    - Proc Area: full output image` : `    - Proc Area: Generation Area`}
    - At most 3 input images in total, the edit source included, and the source is always image 1. Reference order is load-bearing: the prompt can address them as "image 2" and "image 3". Never drop a required original to fit that budget; route reference-heavy work to the flagship instead.
    - **Parameters:**
        - \`prompt\` // Instruction driven. Put text that must appear in the image in "double quotes".
        - \`negative_prompt\` [d: ""]
        - \`resolution\` [d: 2K] [o: 2K, 1K] // The only paid lever; it is the whole price difference.
        - \`seed\` [r: 0-2147483647]
        - The shape is derived, not settable: ${isList ? 'the requested output shape' : 'the Generation Area'} is fitted to the paid tier, every side kept between 384 and 2048 px, and the same shape is sent as input and asked back as output.
        - Internal behavior: the app forces \`enable_prompt_expansion\` off - the provider rewords the instruction and drifts quoted strings. Write the full instruction yourself.
${isList ? `` : `        - \`add_annotations_as_ref\` [d: false]`}`;

const DOC_BLOCK_QWEN_EDIT_MULTIANGLE = (isList = false) => `    **qwen-edit-multiangle [Edit/Special]** - fal.ai-powered camera control for orbiting around an existing image without changing its core layout.
    - Mask: Ignored.
    - Ref Img: false
${isList ? `    - Proc Area: full output image` : `    - Proc Area: Generation Area`}
    - **Parameters:**
        - \`lora_scale\` [d: 1.2] [r: 0-4, s: 0.05]
        - \`guidance_scale\` [d: 4.5] [r: 0-10, s: 0.1]
        - \`zoom\` [d: 5] [r: 0-10, s: 0.1]
        - \`horizontal_angle\` [d: 0] [r: 0-360, s: 1] // 0deg = front, 90deg = right profile, 180deg = back.
        - \`vertical_angle\` [d: 0] [r: -30-90, s: 1]
        - \`num_inference_steps\` [d: 40] [r: 1-50, s: 1]
${isList ? `` : `        - \`add_annotations_as_ref\` [d: false]`}`;

const DOC_BLOCK_QWEN_IMAGE_2_PRO = (isList = false) => `    **qwen-image-2-pro [Edit/Special]** - Replicate-hosted Qwen editor with native negative prompts and optional prompt expansion. Good for controlled full-frame edits and text-to-image fallbacks.
    - Mask: Ignored.
    - Ref Img: true [count: 0-1]
${isList ? `    - Proc Area: full output image` : `    - Proc Area: Generation Area`}
    - **Parameters:**
        - \`prompt\` // Instruction driven.
        - \`negative_prompt\` [d: ""]
        - Internal behavior: the app forces \`enable_prompt_expansion\` off; \`match_input_image\` is always enabled.
${isList ? `` : `        - \`add_annotations_as_ref\` [d: false]`}`;

const DOC_BLOCK_KLING_3 = (isList = false) => `    **kling-3 [Video]**
    - **Purpose**: Kling video generation with optional audio. Supports an optional end frame.
    - **Workflow Notes**:
${isList ? `        - Uses the explicit sourceImageUrl as start_image; source video is not sent.` : `        - Always uses the current composed workspace/timeline frame as \`start_image\`. Visible video layers contribute only their current frame; source video is not sent.`}
    - **Parameters**:
        - \`negative_prompt\`
        - \`resolution\` [d: '1080p'] [o: '720p', '1080p']
        - \`generate_audio\` [d: false]`;

const DOC_BLOCK_KLING_3_OMNI = (isList = false) => `    **kling-3-omni [Video]**
    - **Purpose**: Kling video with optional reference video for style or base editing. Supports an optional end frame.
    - **Workflow Notes**:
${isList ? `        - Default input mode is \`reference_input_mode: 'frame'\`: the app uses the explicit sourceImageUrl and sends it as \`start_image\`.` : `        - Default input mode is \`reference_input_mode: 'frame'\`: the app captures the current workspace/timeline frame and sends it as \`start_image\`.`}
${isList ? `        - Source-video processing requires an explicit video asset and is unavailable from List chat; never infer a video source from saved outputs.` : `        - Use \`reference_input_mode: 'video'\` only when the user explicitly asks to use source-video motion. If \`video_input_start_ms\`/\`video_input_end_ms\` are set, the app composes the visible timeline over that range. If neither is set, it uses the highest visible video track at the current frame.`}
        - Video reference mode is mutually exclusive with \`generate_audio\`.
    - **Parameters**:
        - \`negative_prompt\`
        - \`resolution\` [d: '1080p'] [o: '720p', '1080p']
        - \`generate_audio\` [d: false]
        - \`keep_original_sound\` [d: true]
        - \`reference_input_mode\` [d: 'frame'] [o: 'frame', 'video']
${isList ? `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // Source-video controls are unavailable from List chat.` : `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // One blank side defaults to timeline start/end.`}
        - \`video_reference_type\` [d: 'base'] [o: 'feature', 'base']`;

const DOC_BLOCK_SEEDANCE_2 = (isList = false) => `    **seedance-2.0 [Video]**
    - **Purpose**: ByteDance Seedance 2.0 video generation with optional synchronized audio, frame reference input, optional reference images, and optional reference video input.
    - **Workflow Notes**:
        - The provider often refuses an input image or video showing a realistic person (content policy; 4 of 5 attempts refused on 2026-10-03). To animate people, prefer \`kling-3\`; Seedance works from text alone or from images without people.
${isList ? `        - Default input mode is \`reference_input_mode: 'frame'\`: the app uses the explicit sourceImageUrl and appends it after user-attached image references.` : `        - Default input mode is \`reference_input_mode: 'frame'\`: the app captures the current workspace/timeline frame and appends it after user-attached image references.`}
${isList ? `        - Bind supplied image tags to explicit roles. The explicit sourceImageUrl follows the user references.` : `        - User-attached reference images keep the visible prompt numbering: \`@image1\`, \`@image2\`, etc. If you need to explicitly refer to the captured workspace frame, write \`@workspace\`; the app appends that frame after user references and converts it to the next provider \`@imageN\` before sending.`}
${isList ? `        - Preserve the explicit source subject and setting unless the user requests a change.` : `        - In frame mode, preserve the visible subject, setting, composition, materials, and branding when the user asks to use or continue the workspace frame. Do not invent a new scene when rewriting or refining a prompt that references \`@workspace\`.`}
${isList ? `        - Saved outputs are never implicit inputs.` : `        - If \`@workspace\` is not mentioned, do not add extra wording that forces the model to preserve the workspace frame; the frame can still be sent as context, but the prompt should reflect the user's intent.`}
${isList ? `        - Source-video processing requires an explicit video asset and is unavailable from List chat; never infer a video source from saved outputs.` : `        - Use \`reference_input_mode: 'video'\` only when the user explicitly asks to use source-video motion as input. If \`video_input_start_ms\`/\`video_input_end_ms\` are set, the app composes the visible timeline over that range. If neither is set, it uses the highest visible video track at the current frame.`}
${isList ? `        - Aspect ratio can be set with \`aspect_ratio\`; \`auto\` follows the explicit source image's aspect (recommended); use a listed fixed ratio only to override it.` : `        - Aspect ratio can be set with \`aspect_ratio\`; \`auto\` follows the workspace frame's aspect (recommended); use a listed fixed ratio only to override it.`}
    - **Parameters**:
        - \`duration\` [d: 5] [r: -1-15] // \`-1\` lets the model choose duration.
        - \`resolution\` [d: '720p'] [o: '480p', '720p', '1080p', '4k'] // '4k' costs 2x the 1080p rate, reserve it for final deliverables the user explicitly wants in 4K, never exploratory renders.
        - \`aspect_ratio\` [d: 'auto'] [o: 'auto', '21:9', '16:9', '4:3', '1:1', '3:4', '9:16']
        - \`generate_audio\` [d: false] // Only set true when the user explicitly asks for sound/audio.
        - \`reference_input_mode\` [d: 'frame'] [o: 'frame', 'video']
${isList ? `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // Source-video controls are unavailable from List chat.` : `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // One blank side defaults to timeline start/end.`}`;

const DOC_BLOCK_GEMINI_OMNI_FLASH = (isList = false) => `    **gemini-omni-flash-preview [Video]**
    - **Purpose**: Gemini Omni Flash 1.1 (Google's gemini-omni-1.1-flash) short-video generation/editing through the Gemini Interactions API.
    - **Workflow Notes**:
${isList ? `        - Default input mode is \`reference_input_mode: 'frame'\`: the app uses the explicit sourceImageUrl and sends it with the prompt.` : `        - Default input mode is \`reference_input_mode: 'frame'\`: the app captures the current workspace/timeline frame and sends it with the prompt.`}
        - Up to 3 attached reference images are sent as additional image inputs. Use them for product, character, style, brand, or location consistency; refer to them naturally in the prompt.
        - Set \`use_audio_input: true\` only when the user attaches an audio file and explicitly wants rhythm, mood, dialogue, or sound-driven timing to guide the video.
${isList ? `        - Source-video processing requires an explicit video asset and is unavailable from List chat; never infer a video source from saved outputs.` : `        - Use \`reference_input_mode: 'video'\` only when the user explicitly asks to use source-video motion/content as input. If \`video_input_start_ms\`/\`video_input_end_ms\` are set, the app composes the visible timeline over that range. If neither is set, it uses the highest visible video track at the current frame.`}
        - \`aspect_ratio\` can be \`auto\`, \`16:9\`, or \`9:16\`; use \`auto\` unless the user asks for landscape/portrait/social placement.
        - The model is optimized for short 720p clips. Keep prompts concise and motion-specific.
    - **Parameters**:
        - \`duration\` [d: 5] [r: 3-10] // seconds.
        - \`aspect_ratio\` [d: 'auto'] [o: 'auto', '16:9', '9:16']
        - \`reference_images\` [count: 0-3]
        - \`use_audio_input\` [d: false]
        - \`reference_input_mode\` [d: 'frame'] [o: 'frame', 'video']
${isList ? `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // Source-video controls are unavailable from List chat.` : `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // One blank side defaults to timeline start/end.`}`;

// One generator for the lib/latest-video-models.ts wave: the parameter ranges come from the same
// table the film route sends, so the doctrine cannot drift from the request.
const LATEST_VIDEO_PROMPTING: Record<LatestVideoModelId, string> = {
  'minimax-h3-max': 'Strong on people, faces and physical motion. Describe one continuous action with its start and end state; the model rewrites the prompt lightly (balanced expansion).',
  'minimax-h3-max-turbo': 'Same prompting as minimax-h3-max at about half the price and time; use it for drafts, then rerun the chosen prompt on minimax-h3-max for the final.',
  'wan-3.0-prime': 'Generates its own soundtrack only when generate_audio is true: then describe sound and ambience in the prompt. Up to 30s: write the take shot by shot across the duration. Prompt expansion adds 20-60s of latency.',
  'grok-imagine-video-1.5': 'Good with stylised, playful or graphic motion; identity of a real-looking person can drift, so prefer kling-3 or minimax-h3-max when a face must be kept. No seed.',
  'kling-3-turbo': 'Kling 3 prompting: subject, action, camera, then style. Keep it under 2500 characters. Faster than kling-3; use it to animate a person when Seedance refuses.',
  'flux-3-max': 'Premium and fast. Write one shot: subject, action over time, camera, light. Generates sound only when generate_audio is true: then describe it. An end frame (endImageUrl) makes it land on that image.',
  'wan-3.0': 'Same prompting as wan-3.0-prime at about two thirds of the price; 480p is the cheap draft tier. With reference images (up to 10) it keeps a character, product or set consistent: name what each reference is in the prompt. Generates sound only when generate_audio is true. Prompt expansion adds 20-60s of latency.',
  'ltx-2.5-fast': 'Describe one shot with its camera move and sound; durations are even numbers (6-20s, 6-10s at 1440p and 2160p). Only 16:9 or 9:16. Use 2160p for 4K deliverables.',
  'luma-ray-3.2': 'Best for seamless loops (loop: true) and morphs from a start frame to an end frame (a loop and an end frame cannot be combined). From an image the clip is 5s and cheap; from text it is priced per clip at about three times the image price, so prefer animating a frame.',
};

const DOC_BLOCK_LATEST_VIDEO = (modelId: LatestVideoModelId, isList: boolean) => {
  const spec = LATEST_VIDEO_MODELS[modelId];
  const profile = STUDIO_MODEL_REGISTRY[modelId].comparisonProfile;
  const list = (values: readonly string[]) => values.map((value) => `'${value}'`).join(', ');
  return `    **${modelId} [Video]**
    - **Purpose**: ${profile?.positioning ?? STUDIO_MODEL_REGISTRY[modelId].name}
    - **Workflow Notes**:
${isList
  ? `        - Starts from text alone, or animates the explicit sourceImageUrl when one is passed. Never claim an image is animated unless it is passed as sourceImageUrl.`
  : `        - Animates the current workspace frame when layers are visible; on an empty workspace it generates from the prompt alone.`}
        - With a start image the output follows that image's shape and aspect_ratio is ignored. From text, \`auto\` picks the ratio nearest the output frame.
${spec.supportsEndFrame ? '        - Optional end frame (SET_VIDEO_SOURCE_IMAGES, with a start image): the clip lands on that image.\n' : ''}${spec.referenceEndpoint ? `        - Up to ${spec.maxReferenceImages ?? 10} reference images (${isList ? 'referenceImageUrls' : 'the attached reference images'}) switch it to reference-to-video: it keeps those characters, products or sets consistent; a start image then becomes the first reference.\n` : ''}        - ${LATEST_VIDEO_PROMPTING[modelId]}
${(profile?.knownLimitations ?? []).map((limitation) => `        - ${limitation}`).join('\n')}
    - **Parameters**:
        - \`prompt\`: subject, action over time, camera movement, setting and light, as one continuous shot.
        - \`duration\` [d: ${spec.defaultDuration}] ${spec.durations ? `[o: ${spec.durations.join(', ')}]` : `[r: ${spec.minDuration}-${spec.maxDuration}]`} // seconds.
        - \`resolution\` [d: '${spec.defaultResolution}'] [o: ${list(spec.resolutions)}]
        - \`aspect_ratio\` [d: 'auto'] [o: 'auto', ${list(spec.aspectRatios)}]${modelId === 'luma-ray-3.2' ? '' : ' // text-to-video only.'}
${spec.supportsAudio ? "        - `generate_audio` [d: false] // set true only when the user asks for sound; included in the price.\n" : ''}${spec.supportsLoop ? "        - `loop` [d: false] // a seamless loop; not with an end frame.\n" : ''}${spec.supportsSeed ? '        - `seed` [optional]' : '        - no seed on this model.'}`;
};

const DOC_BLOCK_VEO_3_1_FAST = (isList = false) => `    **veo-3.1-fast [Video]**
${isList ? `    - **Purpose**: Google Veo 3.1 Fast image-to-video, arguably the best overall video quality, fast, with native audio and up to 4K. Animates the explicitly selected source image.` : `    - **Purpose**: Google Veo 3.1 Fast image-to-video, arguably the best overall video quality, fast, with native audio and up to 4K. Animates the current workspace frame.`}
    - **Workflow Notes**:
${isList ? `        - Image-to-video: the app uses the explicit sourceImageUrl as the start image. First-frame only (no last-frame keyframe on this endpoint).` : `        - Image-to-video: the app captures the current workspace/timeline frame as the start image. First-frame only (no last-frame keyframe on this endpoint).`}
        - Prompt the motion, camera, and scene action over time; audio is generated by default (describe sound/ambience/dialogue in the prompt when wanted).
        - Aspect \`auto\` follows the source frame; only set a fixed ratio to override.
    - **Parameters**:
        - \`prompt\`: motion + scene + optional audio description.
        - \`negative_prompt\` [optional]
        - \`duration\` [d: 8] [o: 4, 6, 8] // seconds.
        - \`resolution\` [d: '720p'] [o: '720p', '1080p', '4k']
        - \`aspect_ratio\` [d: 'auto'] [o: 'auto', '16:9', '9:16']
        - \`generate_audio\` [d: true] // native audio; set false to mute.
        - \`seed\` [optional]`;

const DOC_BLOCK_KLING_2_5_TURBO = (isList = false) => `    **kling-2.5-turbo [Video]**
${isList ? `    - **Purpose**: Kling 2.5 Turbo Pro image-to-video, top-of-leaderboard motion quality at 1080p with an optional tail (last-frame) keyframe. Animates the explicitly selected source image.` : `    - **Purpose**: Kling 2.5 Turbo Pro image-to-video, top-of-leaderboard motion quality at 1080p with an optional tail (last-frame) keyframe. Animates the current workspace frame.`}
    - **Workflow Notes**:
        - Image-to-video from the current frame. Optional \`end_frame\`: when the user attaches an end image, the clip transitions from the start frame to it.
        - Prompt motion and camera over time. No audio.
    - **Parameters**:
        - \`prompt\`: motion/camera description (max ~2500 chars).
        - \`negative_prompt\` [d: 'blur, distort, and low quality']
        - \`duration\` [d: 5] [o: 5, 10] // seconds.
        - \`aspect_ratio\` [d: '16:9'] [o: '16:9', '9:16', '1:1']
        - \`seed\` [optional]`;

const DOC_BLOCK_LTX_2_3_FAST = (isList = false) => `    **ltx-2.3-fast [Video]**
${isList ? `    - **Purpose**: Lightricks LTX-2.3 Fast image-to-video, the cheapest premium tier, with native audio, an optional end-frame keyframe, and up to 4K. Animates the explicitly selected source image.` : `    - **Purpose**: Lightricks LTX-2.3 Fast image-to-video, the cheapest premium tier, with native audio, an optional end-frame keyframe, and up to 4K. Animates the current workspace frame.`}
    - **Workflow Notes**:
        - Image-to-video from the current frame; optional \`end_frame\` for a first-to-last transition.
        - Native audio is on by default. Durations over 10s require 25fps + 1080p.
    - **Parameters**:
        - \`prompt\`: motion + scene + optional audio.
        - \`duration\` [d: 6] [r: 6-20] // seconds.
        - \`resolution\` [d: '1080p'] [o: '1080p', '1440p', '2160p']
        - \`aspect_ratio\` [d: 'auto'] [o: 'auto', '16:9', '9:16']
        - \`generate_audio\` [d: true]
        - \`seed\` [optional]`;

const DOC_BLOCK_LUMA_RAY_2_FLASH = (isList = false) => `    **luma-ray-2-flash [Video]**
${isList ? `    - **Purpose**: Luma Ray 2 Flash image-to-video, fast Luma with first+last-frame keyframes and a seamless LOOP option. Best for marketing loops and morph transitions. Animates the explicitly selected source image.` : `    - **Purpose**: Luma Ray 2 Flash image-to-video, fast Luma with first+last-frame keyframes and a seamless LOOP option. Best for marketing loops and morph transitions. Animates the current workspace frame.`}
    - **Workflow Notes**:
        - Image-to-video from the current frame; optional \`end_frame\` for a first-to-last morph. Set the \`loop\` intent in the prompt when a seamless loop is wanted.
        - Prompt motion/camera. No audio.
    - **Parameters**:
        - \`prompt\`: motion/camera description.
        - \`duration\` [d: 5] [o: 5, 9] // seconds.
        - \`resolution\` [d: '540p'] [o: '540p', '720p', '1080p']
        - \`aspect_ratio\` [d: '16:9'] [o: '16:9', '9:16', '4:3', '3:4', '21:9', '9:21']`;

const DOC_BLOCK_SEEDANCE_2_FAST = (isList = false) => `    **seedance-2.0-fast [Video]**
    - **Purpose**: ByteDance Seedance 2.0 FAST tier, the same flagship Seedance model at a fraction of the time and cost, capped at 720p. State-of-the-art quality, fast enough to iterate. Prefer this over the full \`seedance-2.0\` unless the user needs 1080p.
    - **Workflow Notes**:
        - The provider often refuses an input image or video showing a realistic person (content policy; 4 of 5 attempts refused on 2026-10-03). To animate people, prefer \`kling-3\`; Seedance works from text alone or from images without people.
${isList ? `        - Uses the Seedance prompting specification with an explicit sourceImageUrl and individually labelled reference images.` : `        - Same prompting methodology and reference/frame behaviour as \`seedance-2.0\` (see the Seedance 2.0 chapter): frame mode captures the current workspace frame; user reference images keep \`@image1..9\`; \`@workspace\` refers to the captured frame.`}
        - Capped at 480p/720p. If the user asks for 1080p, use \`seedance-2.0\` instead.
        - Optional \`end_frame\` for a first-to-last transition.
    - **Parameters**:
        - \`duration\` [d: 5] [r: -1-15] // \`-1\` lets the model choose.
        - \`resolution\` [d: '720p'] [o: '480p', '720p'] // no 1080p on the fast tier.
        - \`aspect_ratio\` [d: 'auto'] [o: 'auto', '21:9', '16:9', '4:3', '1:1', '3:4', '9:16']
        - \`generate_audio\` [d: false] // only when the user asks for sound.
        - \`seed\` [optional]`;

const DOC_BLOCK_SEEDANCE_2_5 = (isList = false) => `    **seedance-2.5 [Video]**
    - **Purpose**: ByteDance Seedance 2.5, the NEWEST Seedance. One continuous take of up to 30 seconds with locked character, set and palette, native synchronized audio and lip-sync, and up to 50 multimodal references. Prefer it over \`seedance-2.0\` whenever the deliverable is longer than 15s, needs heavy reference consistency, or needs lip-synced speech; \`seedance-2.0\` remains the choice for a 1080p/4K master.
    - **Workflow Notes**:
        - The provider often refuses an input image or video showing a realistic person (content policy; 4 of 5 attempts refused on 2026-10-03). To animate people, prefer \`kling-3\`; Seedance works from text alone or from images without people.
${isList ? `        - Uses the Seedance prompting specification with an explicit sourceImageUrl and individually labelled reference images.` : `        - Same prompting methodology and reference/frame behaviour as \`seedance-2.0\` (see the Seedance prompting chapter): frame mode captures the current workspace frame; user reference images keep \`@image1..N\`; \`@workspace\` refers to the captured frame.`}
        - Takes up to 30 reference images, 10 reference videos and 10 reference audio clips, 50 files in total. Reference videos and audio must each run 1.8s to 30.2s.
        - Capped at 480p/720p. If the user needs 1080p or 4K, use \`seedance-2.0\` instead.
        - No \`seed\` and no \`end_frame\` on this model. If the user needs a reproducible run or a first-to-last-frame transition, use \`seedance-2.0\`.
        - Structure the prompt shot by shot across the full requested duration; this model holds continuity for the whole take, so do not write it as a cut of separate clips unless the user wants cuts.
    - **Parameters**:
        - \`duration\` [d: 5] [r: -1, 4-30] // \`-1\` lets the model choose. Long takes are billed per second, so only go long when the user asks for it.
        - \`resolution\` [d: '720p'] [o: '480p', '720p'] // no 1080p/4k on this model.
        - \`aspect_ratio\` [d: 'auto'] [o: 'auto', '21:9', '16:9', '4:3', '1:1', '3:4', '9:16']
        - \`generate_audio\` [d: false] // Only set true when the user explicitly asks for sound.
        - \`reference_input_mode\` [d: 'frame'] [o: 'frame', 'video']
${isList ? `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // Source-video controls are unavailable from List chat.` : `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // One blank side defaults to timeline start/end.`}`;

const DOC_BLOCK_FLUX_3 = (isList = false) => `    **flux-3 [Video]**
${isList ? `    - **Purpose**: Black Forest Labs FLUX.3, the CHEAPEST premium video tier available here. Native synchronized audio, clips up to 20 seconds, 720p or 1080p, and an optional end frame. Animates the explicitly selected source image.` : `    - **Purpose**: Black Forest Labs FLUX.3, the CHEAPEST premium video tier available here. Native synchronized audio, clips up to 20 seconds, 720p or 1080p, and an optional end frame. Animates the current workspace frame.`}
    - **Workflow Notes**:
${isList ? `        - Image-to-video from the explicitly selected source image. If the user attaches an end image, the app switches to the first-to-last-frame endpoint automatically and the clip interpolates between the two frames.` : `        - Image-to-video from the captured workspace frame. If the user attaches an end image, the app switches to the first-to-last-frame endpoint automatically and the clip interpolates between the two frames.`}
        - No reference images beyond the start and end frame, and no seed. For multi-reference character/set consistency use \`seedance-2.5\`; for a reproducible run use \`seedance-2.0\`.
        - Prompt the motion, camera and scene action over time. Audio is generated when \`generate_audio\` is on, describe the sound or ambience in the prompt then.
        - Aspect \`auto\` follows the source frame; set a fixed ratio only to override it.
        - Cost-wise this is the default worth reaching for first on a plain frame-to-video ask: it undercuts every other premium video model per second.
    - **Parameters**:
        - \`prompt\`: motion + scene + optional audio description.
        - \`duration\` [d: 5] [r: 5-20] // seconds.
        - \`resolution\` [d: '720p'] [o: '720p', '1080p']
        - \`aspect_ratio\` [d: 'auto'] [o: 'auto', '21:9', '2:1', '16:9', '4:3', '1:1', '3:4', '9:16']
        - \`generate_audio\` [d: false] // only when the user asks for sound.
        - \`end_frame\` [optional] // attaching one switches to the first-to-last-frame endpoint.`;

const DOC_BLOCK_FLUX_3_DRAFT = (isList = false) => `    **flux-3-draft [Video]**
    - **Purpose**: The FLUX.3 DRAFT tier, the same model rendered fast and cheap at 720p.${isList ? ' Use when the user requests a draft.' : ` It is a validator, not a finisher: use it to prove that a motion or direction reads, then regenerate the accepted take with \`flux-3\`.`}
    - **Workflow Notes**:
        - Same inputs and prompting as \`flux-3\` (see its chapter): current frame in, optional end frame, same aspect and audio behaviour.
        - Always 720p. Do not deliver a draft as the final render.
    - **Parameters**:
        - \`prompt\`: motion + scene description.
        - \`duration\` [d: 5] [r: 5-20] // seconds.
        - \`aspect_ratio\` [d: 'auto'] [o: 'auto', '21:9', '2:1', '16:9', '4:3', '1:1', '3:4', '9:16']
        - \`generate_audio\` [d: false]
        - \`end_frame\` [optional]`;

const DOC_BLOCK_HAILUO_02_FAST = (isList = false) => `    **hailuo-02-fast [Video]**
${isList ? `    - **Purpose**: MiniMax Hailuo 02 (fast tier) image-to-video via fal, the CHEAPEST, FASTEST motion validator. Animates the explicitly selected source image at 512p. Use it when the user requests a low-resolution clip.` : `    - **Purpose**: MiniMax Hailuo 02 (fast tier) image-to-video via fal, the CHEAPEST, FASTEST motion validator. Animates the current workspace frame at 512p. Use it to prove that a motion/direction reads before spending the expensive Seedance/Kling render; it is a validator, not a finisher.`}
    - **Workflow Notes**:
${isList ? `        - Image-to-video only: the app uses the explicit sourceImageUrl as the start image. There is no separate frame parameter to set.` : `        - Image-to-video only: the app captures the current workspace/timeline frame as the start image. There is no separate frame parameter to set.`}
        - Prompt the MOTION and CAMERA, not the static scene already in the frame ("slow dolly in", "she turns and smiles", "leaves drift left"). Optional MiniMax camera tokens in brackets, max 3: e.g. \`[Truck left, Pan right, Zoom in]\`.
${isList ? `        - Use this model when requested; do not launch unrequested exploratory generations.` : `        - Cheap and fast, iterate freely here in the self-validation loop before escalating to a high-quality model.`}
    - **Parameters**:
        - \`prompt\`: motion/camera description.
        - \`duration\` [d: 6] [o: 6, 10] // seconds. Fixed 512p output (no resolution control).`;

const DOC_BLOCK_WAN_2_2_I2V = (isList = false) => `    **wan-2.2-i2v [Video]**
${isList ? `    - **Purpose**: Alibaba Wan 2.2 (A14B) image-to-video via fal, a cheap 720p motion preview one quality step above hailuo-02-fast, with optional last-frame keyframing. Animates the explicitly selected source image.` : `    - **Purpose**: Alibaba Wan 2.2 (A14B) image-to-video via fal, a cheap 720p motion preview one quality step above hailuo-02-fast, with optional last-frame keyframing. Animates the current workspace frame.`}
    - **Workflow Notes**:
${isList ? `        - Image-to-video: the app uses the explicit sourceImageUrl as the start image.` : `        - Image-to-video: the app captures the current workspace/timeline frame as the start image.`}
        - Optional \`end_frame\`: when the user attaches an end/last image, the app sends it so the clip transitions from the start frame to that end frame. Only used when an end frame is present.
        - Prompt the MOTION and CAMERA over time; do not re-describe the still. Wan formula: Subject + Scene + Motion (+ optional lens/lighting).
        - \`aspect_ratio: 'auto'\` (default) follows the source frame's shape (resolved app-side); set a fixed ratio only to override.
    - **Parameters**:
        - \`prompt\`: motion/camera description.
        - \`negative_prompt\` [optional] // artifacts/motion to avoid.
        - \`duration\` [d: 5] [r: 2-8] // seconds.
        - \`resolution\` [d: '720p'] [o: '480p', '580p', '720p']
        - \`aspect_ratio\` [d: 'auto'] [o: 'auto', '16:9', '9:16', '1:1', '4:3', '3:4']
        - \`seed\` [optional] // reuse a seed to lock motion once a preview passes.`;

const DOC_BLOCK_WAN_2_2_FAST = (isList = false) => `    **wan-2.2-fast [Video]**
    - **Purpose**: Distilled Wan 2.2 5B "fast-wan" TEXT-to-video via fal, the cheapest generator of all.${isList ? ' Generates from text without a source image.' : ' Sketch a motion direction from a text prompt alone (no source image) before committing to stills or an expensive render.'}
    - **Workflow Notes**:
${isList ? `        - Text-to-video only: it does NOT use the explicit source image. For image-to-video use hailuo-02-fast or wan-2.2-i2v.` : `        - Text-to-video only: it does NOT use the workspace frame. For image-to-video use hailuo-02-fast or wan-2.2-i2v.`}
        - Describe subject + scene + motion + camera concisely; short clips (<=5s).
        - \`aspect_ratio: 'auto'\` defaults to a 16:9 sketch unless a placement is requested.
    - **Parameters**:
        - \`prompt\`: full subject/scene/motion description.
        - \`negative_prompt\` [optional]
        - \`duration\` [d: 5] [r: 2-5] // seconds.
        - \`resolution\` [d: '480p'] [o: '480p', '580p', '720p'] // 480p keeps sketches cheapest.
        - \`aspect_ratio\` [d: 'auto'] [o: 'auto', '16:9', '9:16', '1:1']
        - \`seed\` [optional]`;

const DOC_BLOCK_TOPAZ_VIDEO_UPSCALE = (isList = false) => `    **topaz-video-upscale [Video/Edit]**
    - **Purpose**: Performs high-quality upscaling on an existing clip via fal.ai Topaz video enhancement.
    - **Workflow Requirements**:
${isList ? `        - Source-video processing requires an explicit video asset and is unavailable from List chat; never infer a video source from saved outputs.` : `        - Uses the highest visible video track at the current frame when no time range is provided. If \`video_input_start_ms\`/\`video_input_end_ms\` are set, the app composes the visible timeline over that range. Prompts and reference images are ignored.`}
        - Configure enhancement profile via \`model\` and scaling via \`upscale_factor\`.
    - **Parameters**:
        - \`model\` [d: 'Proteus'] [o: 'Proteus', 'Artemis HQ', 'Artemis MQ', 'Artemis LQ', 'Nyx', 'Nyx Fast', 'Nyx XL', 'Nyx HF', 'Gaia HQ', 'Gaia CG', 'Gaia 2', 'Starlight Precise 1', 'Starlight Precise 2', 'Starlight Precise 2.5', 'Starlight HQ', 'Starlight Mini', 'Starlight Sharp', 'Starlight Fast 1', 'Starlight Fast 2']
        - \`upscale_factor\` [d: 2] [r: 1-4, s: 0.1]
        - \`target_fps\` [d: 30] [r: 1-120, s: 1]
        - \`compression\` [r: 0-1, s: 0.01]
        - \`noise\` [r: 0-1, s: 0.01]
        - \`halo\` [r: 0-1, s: 0.01]
        - \`grain\` [r: 0-0.1, s: 0.005]
        - \`recover_detail\` [r: 0-1, s: 0.01]
        - \`H264_output\` [d: true] // KEEP IT ON for anything the user will watch in the app. With it off Topaz returns HEVC (hvc1), which browsers largely cannot demux: measured 2026-08-13, an h264 640x360 source came back hevc 1280x720, frame extraction found 0 frames and the card could not play it. Only turn it off when the clip is destined for an editing suite.
${isList ? `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // Source-video controls are unavailable from List chat.` : `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // One blank side defaults to timeline start/end.`}`;

const DOC_BLOCK_MIRELO_SFX = (isList = false) => `    **mirelo-sfx-1.6 [Video/Edit]**
${isList ? `    - **Purpose**: Generates synchronized sound effects for an explicit video clip; source-video processing is unavailable from List chat.` : `    - **Purpose**: Generates production-ready, frame-synchronized sound effects for an existing timeline video clip through fal endpoint \`mirelo-ai/sfx1.6/video-to-video\`.`}
    - **Workflow Requirements**:
${isList ? `        - Source-video processing requires an explicit video asset and is unavailable from List chat; never infer a video source from saved outputs.` : `        - The source is always video. If \`video_input_start_ms\`/\`video_input_end_ms\` are set, the app composes the visible timeline over that range. If neither is set, it uses the selected video track, or the highest visible video track at the cursor.`}
        - Clip duration is derived from the prepared source range and must be 1 to 60 seconds. Never set a separate \`duration\` parameter.
        - The optional prompt must describe sound only. Preserve visual content because this model adds synchronized audio rather than editing picture.
${isList ? '' : `        - On success Fater inserts the result at the selected range start, extracts an audible audio track, and leaves the carrier video hidden and muted.`}
        - Current fal pricing is $0.01 per generated second; Fater bills one credit per started source second.
    - **Parameters**:
        - \`prompt\` [d: ''] // Optional sound-only guidance, sent to fal as \`text_prompt\`.
        - \`seed\` [d: -1] // \`-1\` means random.
${isList ? `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // Source-video controls are unavailable from List chat.` : `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // One blank side defaults to timeline start/end; both blank use the selected track.`}
        - Provider-fixed: \`num_samples\` [d: 1] // Fater creates separate tasks for multiple outputs.`;

const DOC_BLOCK_MAGNIFIC_VIDEO_UPSCALE = (isList = false) => `    **magnific-video-upscale [Video/Edit]**
    - **Purpose**: Performs creative AI upscaling/enhancement on an existing clip via Magnific Video Upscaler.
    - **Workflow Requirements**:
${isList ? `        - Source-video processing requires an explicit video asset and is unavailable from List chat; never infer a video source from saved outputs.` : `        - Uses the highest visible video track at the current frame when no time range is provided. If \`video_input_start_ms\`/\`video_input_end_ms\` are set, the app composes the visible timeline over that range. Prompts and reference images are ignored.`}
        - Configure output size with \`resolution\` and enhancement style with \`creativity\`, \`sharpen\`, \`smart_grain\`, \`flavor\`, and \`fps_boost\`.
    - **Parameters**:
        - \`resolution\` [d: '2k'] [o: '720p', '1k', '2k', '4k']
        - \`creativity\` [d: 0] [r: 0-100, s: 1]
        - \`fps_boost\` [d: false]
        - \`sharpen\` [d: 0] [r: 0-100, s: 1]
        - \`smart_grain\` [d: 0] [r: 0-100, s: 1]
        - \`flavor\` [d: 'vivid'] [o: 'vivid', 'natural']
${isList ? `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // Source-video controls are unavailable from List chat.` : `        - \`video_input_start_ms\`/\`video_input_end_ms\` [optional, milliseconds] // One blank side defaults to timeline start/end.`}`;

const DOC_BLOCK_TOPAZ_IMAGE_ENHANCE = (isList = false) => `    **topaz-image-enhance [Image/Edit]**
${isList ? `    - **Purpose**: Runs Topaz image upscaling/enhancement on the explicitly selected source image via fal.ai.` : `    - **Purpose**: Runs Topaz image upscaling/enhancement on the current generation area via fal.ai.`}
    - **Workflow Requirements**:
${isList ? `        - Requires an input image from the explicitly selected source image.` : `        - Requires an input image from the workspace generation area.`}
        - Prompt and reference images are ignored.
        - Executes through \`fal-ai/topaz/upscale/image\`.
    - **Parameters**:
        - \`model\` [d: 'Standard V2'] [o: 'Low Resolution V2', 'Standard V2', 'CGI', 'High Fidelity V2', 'Text Refine', 'Recovery', 'Redefine', 'Recovery V2', 'Standard MAX', 'Wonder', 'Wonder 3']
        - \`inputMegaPixels\` [d: 5] [o: auto, 0.5, 1, 2, 5, 10, 16] // Input resolution rendered by Studio before enhancement; Auto uses 16 Mpx.
        - \`outputMegaPixels\` [d: 40] [o: 16, 30, 40] // Desired output resolution.
        - Effective \`upscale_factor\` is derived from output/input resolution and bounded to 1-4 before it is sent to Topaz. It is not the primary UI control.
        - \`crop_to_fill\` [d: false]
        - \`output_format\` [d: 'jpeg'] [o: 'jpeg', 'png']
        - \`subject_detection\` [d: 'All'] [o: 'All', 'Foreground', 'Background']
        - \`face_enhancement\` [d: true]
        - \`face_enhancement_creativity\` [r: 0-1, s: 0.01]
        - \`face_enhancement_strength\` [d: 0.8] [r: 0-1, s: 0.01]
        - \`sharpen\` [r: 0-1, s: 0.01]
        - \`denoise\` [r: 0-1, s: 0.01]
        - \`fix_compression\` [r: 0-1, s: 0.01]
        - \`strength\` [r: 0.01-1, s: 0.01] // Text Refine only
        - \`creativity\` [r: 1-6, s: 1] // Redefine only
        - \`texture\` [r: 1-5, s: 1] // Redefine only
        - \`prompt\` [max: 1024] // Redefine only
        - \`autoprompt\` [d: false] // Redefine only
        - \`detail\` [r: 0-1, s: 0.01] // Recovery V2 only
        - \`enhancement_strength\` [o: 'low', 'medium', 'high'] // Wonder 3 only
        - Legacy \`upscale_factor\`, \`topazOutputMegaPixels\`, and \`output_megapixels\` values remain accepted when the input/output resolution contract is absent.`;

const DOC_BLOCK_MAGNIFIC_IMAGE_UPSCALE = (isList = false) => `    **magnific-image-upscale [Image/Edit]**
${isList ? `    - **Purpose**: Runs Magnific Upscaler Precision V2 on the explicitly selected source image for final high-detail image upscaling.` : `    - **Purpose**: Runs Magnific Upscaler Precision V2 on the current generation area for final high-detail image upscaling.`}
    - **Behavior**:
${isList ? `        - Requires an existing source image from the explicitly selected source image.` : `        - Requires an existing source image from the current generation area.`}
        - Prompt and reference images are ignored.
        - Uses Magnific Precision V2, which may synthesize extra fine detail while preserving the source.
    - **Parameters**:
        - \`scale_factor\` [d: 2] [r: 2-16, s: 1]
        - \`flavor\` [d: 'photo'] [o: 'photo', 'photo_denoiser', 'sublime']
        - \`sharpen\` [d: 7] [r: 0-100, s: 1]
        - \`smart_grain\` [d: 7] [r: 0-100, s: 1]
        - \`ultra_detail\` [d: 30] [r: 0-100, s: 1]
        - \`filter_nsfw\` [d: false]`;

const DOC_BLOCK_IDEOGRAM_INPAINT = (isList = false) => `    **ideogram-inpaint [Edit/Masked] (Description-Driven)** - Ideogram 3.0 masked inpainting. Regenerates ONLY the region the user painted with the Mask tool and adds it back as a new layer covering just that patch, the rest of the image is left untouched. Use it for region-precise local edits ("change / replace / add X in my mask", "inpaint ...", "edit only this area"), especially when text must render cleanly.
    - Prompting Style: DESCRIPTION-DRIVEN. Ideogram does NOT obey edit instructions, describe the final result that should appear inside the mask as if it were already there. Translate the user's ask before sending it: "add / inpaint a tree" -> "a tall leafy oak tree, sun-dappled, matching the surrounding forest". Use nouns + adjectives + lighting/style; put literal text in "double quotes". (See the Instruction-Driven vs Description-Driven chapter.)
    - Mask: REQUIRED. The painted Mask-tool region IS the area that gets regenerated. If no mask is painted the run cannot proceed, ask the user to paint one with the Mask tool first; do not silently fall back to a maskless model unless they agree.
    - Ref Img: false
${isList ? `    - Proc Area: the painted mask region inside the full output image. Keep the full output image tight around the mask (plus a little surrounding context) so the rest of the frame is preserved and the patch blends in.` : `    - Proc Area: the painted mask region inside the Generation Area. Keep the Generation Area tight around the mask (plus a little surrounding context) so the rest of the frame is preserved and the patch blends in.`}
    - **Parameters:**
        - \`prompt\` // DESCRIBE what should fill the masked region, a description of the result, NOT an instruction: "a tall oak tree with sun-dappled green leaves", not "add a tree". Name the subject AND how it should match the surrounding scene (lighting, perspective, style) so it blends. Put any literal text to render in "double quotes".
        - \`magic_prompt\` [d: AUTO] [o: AUTO, ON, OFF] // ON = Ideogram expands your prompt from prompt + image (a short, region-only prompt is fine). OFF = describe the whole scene for coherence. A region-only prompt puts more emphasis on the masked content.
        - \`rendering_speed\` [d: DEFAULT] [o: FLASH, TURBO, DEFAULT, QUALITY]
        - \`style_type\` [d: AUTO] [o: AUTO, GENERAL, REALISTIC, DESIGN, FICTION]
        - \`seed\` [d: -1]`;

const DOC_BLOCK_IDEOGRAM_4_EDIT = (isList = false) => `${isList ? `    **ideogram-4-edit [Ideogram 4 / Full Area] (Description-Driven)** - Ideogram 4.0 text-to-image or full-image remix. With an explicit sourceImageUrl it remixes that image, preserving composition; without one it generates from text. Strong typography and text rendering.` : `    **ideogram-4-edit [Ideogram 4 / Full Area] (Description-Driven)** - Ideogram 4.0 text-to-image or maskless remix. If the workspace has visible source layers, it remixes the WHOLE Generation Area from your prompt while preserving the overall composition (no mask). If the workspace has no visible source image, it generates from text. Strong typography / text rendering. Use it for prompt-driven generation/full-frame restyles. Region-precise masked edits are available only on Canvas projects.`}
    - Prompting Style: DESCRIPTION-DRIVEN. Describe the desired final image, not an edit instruction. "make the sky stormy" -> "a dramatic stormy sky with dark clouds". Put literal text to render in "double quotes".
    - Mask: Ignored.
${isList ? `    - Ref Img: optional explicit source image for remix; no source means text-to-image.` : `    - Ref Img: source image optional; visible workspace content is used for remix, empty workspace uses text-to-image.`}
${isList ? `    - Proc Area: full output image (full-frame remix/generate).` : `    - Proc Area: Generation Area (full-frame remix/generate).`}
    - **Parameters:**
        - \`prompt\` // Describe the desired result. Put literal text to render in "double quotes".
        - \`image_weight\` [d: 50] [o: 1-100] // How strongly to preserve the source: higher = closer to the original, lower = more creative.
        - \`resolution\` [d: AUTO] // Optional 2K Ideogram v4 output resolution.
        - \`rendering_speed\` [d: DEFAULT] [o: TURBO, DEFAULT, QUALITY]
        - \`enable_copyright_detection\` [d: false]`;

const DOC_BLOCK_IDEOGRAM_3 = (isList = false) => `    **ideogram-3 [Generate / Text-to-Image] (Description-Driven)** - Creates a fresh image from text with strong typography. It cannot edit, remix, or use an attached/base image as provider input. You may inspect an attached image to write a better prompt, but do not pass \`sourceImageUrl\`; choose an edit-capable model instead when the user needs its pixels transformed.
    - Prompting Style: DESCRIPTION-DRIVEN. Describe the complete desired image, including subject, setting, composition, lighting, camera/style, and exact typography. Put literal text to render in "double quotes".
    - Mask: Ignored.
    - Ref Img: false.
${isList ? `    - Proc Area: creates a new image at the requested aspect ratio; no source image is sent.` : `    - Proc Area: creates a new image at the requested aspect ratio; no existing workspace pixels are sent.`}
    - **Parameters:**
        - \`prompt\` // Required description of the complete desired image.
        - \`aspect_ratio\` [d: 1x1] [o: 1x1, 16x9, 9x16, 4x3, 3x4, 3x2, 2x3]
        - \`magic_prompt\` [d: AUTO] [o: AUTO, ON, OFF]
        - \`rendering_speed\` [d: DEFAULT] [o: FLASH, TURBO, DEFAULT, QUALITY]
        - \`style_type\` [d: AUTO] [o: AUTO, GENERAL, REALISTIC, DESIGN, FICTION]
        - \`negative_prompt\` [d: ''] // Optional description of elements to exclude.
        - \`seed\` [d: -1] // -1 chooses a random seed; non-negative integers make a run repeatable.`;

const recraftVectorDocBlock = (id: 'recraft-v4-1-vector' | 'recraft-v4-1-vector-pro') => `    **${id} [Generate / Text-to-Vector] (Description-Driven)** - ${id.endsWith('pro') ? 'Recraft V4.1 Vector Pro: more geometric detail and finer paths.' : 'Recraft V4.1 Vector: a logo, icon or illustration as an editable SVG.'} Studio Vector only; the output lands as a vector layer whose shapes can be recoloured by clicking them.
    - Prompting Style: DESCRIPTION-DRIVEN. Name the subject, the style (flat, geometric, line, badge, monogram, mascot), the composition and the mood. Say "logo" or "icon" when that is wanted: it keeps the shapes few and clean. No photographic words, no gradients unless asked, no text unless the user gave the exact words.
    - Mask: Ignored. Ref Img: false. Seed: none.
    - Proc Area: the artboard sets the output size, scaled as a whole into [r: 256..4096] px per side; no aspect ratio parameter exists.
    - **Parameters:**
        - \`prompt\` // Required description of the artwork.
        - \`colors\` [d: []] // Preferred colours as #rrggbb strings; the brand's palette belongs here, not in the prose.
        - \`background_color\` [d: null] // One #rrggbb, or null to let the model choose; a logo usually wants a plain one.`;
const DOC_BLOCK_RECRAFT_V4_1_VECTOR = recraftVectorDocBlock('recraft-v4-1-vector');
const DOC_BLOCK_RECRAFT_V4_1_VECTOR_PRO = recraftVectorDocBlock('recraft-v4-1-vector-pro');
const arrowDocBlock = (id: 'arrow-2' | 'arrow-2-telos') => `    **${id} [Generate / Text-to-Vector] (Description-Driven)** - ${id === 'arrow-2' ? 'QuiverAI Arrow 2, the best-ranked text-to-SVG model: the default for logos, icons and illustrations.' : 'QuiverAI Arrow 2 Telos, the top-ranked SVG model for complex artwork; slower and about half again as expensive as Arrow 2.'} Studio Vector only; the output is clean vector paths the user recolours by clicking them.
    - Prompting Style: DESCRIPTION-DRIVEN. \`prompt\` says what to draw (subject, composition, any exact text in quotes); \`instructions\` says how it looks (flat, geometric, line weight, corner style, number of colours). Keep them apart.
    - Mask: Ignored. Ref Img: false. Seed: none.
    - Proc Area: the artboard is sent as the viewBox, scaled as a whole into [r: 256..4096] px per side.
    - Billing: reserved at the output-token bound, settled on the tokens used; more care costs more.
    - **Parameters:**
        - \`prompt\` // Required description of the artwork.
        - \`instructions\` [d: ''] // Optional style guidance.
        - \`colors\` [d: []] // #rrggbb strings; sent as an instruction to use only these colours.
        - \`reasoning_effort\` [d: medium] [o: low, medium, high, xhigh] // Care: low is fastest, xhigh the most careful.`;
const DOC_BLOCK_ARROW_2 = arrowDocBlock('arrow-2');
const DOC_BLOCK_ARROW_2_TELOS = arrowDocBlock('arrow-2-telos');
const DOC_BLOCK_IDEOGRAM_VECTOR_1 = `    **ideogram-vector-1 [Generate / Text-to-Vector] (Description-Driven)** - Ideogram Vector 1: an editable SVG with smooth gradients and sharp, correctly spelled lettering; the pick for a wordmark, a badge with text, or artwork that needs shading. Studio Vector only; the output lands as vector paths and gradients the user recolours by clicking them.
    - Prompting Style: DESCRIPTION-DRIVEN. Name the subject, the style (flat, geometric, line, badge, mascot, gradient), the composition, and any exact text in quotes.
    - Mask: Ignored. Ref Img: false. Seed: none.
    - Proc Area: the artboard's ratio is sent as the nearest of Ideogram's ratios (1:4 to 4:1); the output is fitted inside the artboard.
    - **Parameters:**
        - \`prompt\` // Required description of the artwork.
        - \`colors\` [d: []] // #rrggbb strings; sent as an instruction to use only these colours.
        - \`quality\` [d: medium] [o: low, medium, high] // ${IDEOGRAM_VECTOR_QUALITY_CREDITS.low}, ${IDEOGRAM_VECTOR_QUALITY_CREDITS.medium} or ${IDEOGRAM_VECTOR_QUALITY_CREDITS.high} credits; high for a final logo.`;

const EMPTY_DOC_BUILDER = () => '';
const maiImageDoc = (id: keyof typeof MAI_IMAGE_MODELS) => `    **${id} [Generate/Edit]** - ${MAI_IMAGE_MODELS[id].purpose} Azure public preview without an SLA.
    - Describe subject, setting, composition, lighting, materials and exact text in quotes. For edits, name the change and explicitly preserve all other geometry and content.
    - Empty source: text-to-image. One source: maskless image editing. No masks, extra references, persona or archetype conditioning. Never silently switch models.
    - PNG output. Frame ratio must be between 9:16 and 16:9; the app derives dimensions with minimum 768px per side and maximum 1,048,576 pixels. Do not promise 4K or lossless geometry.
    - Observed Azure quirk: native output can round dimensions to multiples of 16. The app keeps the native output as returned and places it in the frame.
    - Parameters: \`prompt\` [d: empty, required] // Non-empty English instruction. Dimensions are derived from the frame, not prompt prose. Seed, automatic aspect ratio and web grounding are not exposed.`;
const DOC_BLOCK_GPT_IMAGE_25 = (isList = false) => `    **gpt-image-2.5 [Edit/Full Area]** - fal-hosted GPT-Image-2.5 for prompt-driven text-to-image and main-image editing.
    - Hosting: fal; the four endpoint IDs are exactly openai/gpt-image-2.5/sunburst/text-to-image, openai/gpt-image-2.5/sunburst/edit, openai/gpt-image-2.5/flare/text-to-image and openai/gpt-image-2.5/flare/edit. Mode sunburst is the default; flare is opt-in. Archived normalized provider schema fingerprint: 8fc95df0b13a93def9b165aa8cb5ecd31c339ee2485039c72ae3a40f6e007367.
    - Text-to-image has no source. Canvas edit uses one authorized main image plus up to 10 user reference images, the annotation guide taking the last slot when annotations are on; a List turn sends up to 10 mentioned images, and the List/Factory foundation may raise the total to 16 server-resolved canonical references. All travel in the edit image_urls array, main image first. No masks, videos, audio, seed or raw provider controls.
    - Proc Area: ${isList ? 'full output image' : 'Generation Area'}; the frontend render owns the output pixel dimensions and the API uses the rendered \`targetWidth\`/\`targetHeight\` after lightweight validation. Workspace coordinates are only for frontend composition and merge placement. Require multiples of 16, at least 655,360 pixels, at most 8,294,400 pixels, a 3840px edge and a ratio between 1:3 and 3:1.
    - Canvas sources are rendered by \`renderWorkspaceArea\` with alpha disabled and uploaded as WebP; the central API does not download, decode, resize or re-encode them. The GPT adapter validates only canonical provider-accessible HTTPS URLs. Private List references use the existing asset transport without a new Sharp pipeline.
    - Provider payload is exactly prompt, image_size, background:auto, quality, num_images:1 and output_format:webp; edits add only image_urls. The app uses WebP output by default. Output consumes images[0].url.
    - **Parameters:**
        - \`prompt\` [required, max 32,000 Unicode code points] // Trimmed instruction.
        - \`mode\` [d: sunburst] [o: sunburst, flare] // Selects the endpoint family, never sent as a provider field.
        - \`quality\` [d: medium] [o: low, medium, high, xhigh, max] // Costs 1, 1.5, 2, 2.25 or 2.5 credits respectively.
        - Raw fal parameters and arbitrary parameter overrides are intentionally unavailable. Application task inputs and List foundation keys (including mainImagePrivateUrl, mainImageData, urls, referenceImagePrivateUrls, listPersonas, listObjects, listReferenceRoles, listFactoryReferences, listCreativeContext, listEditSourceOutputId${isList ? '' : ', add_annotations_as_ref'} and seed) remain internal context for resolution, retry lineage and snapshots; the adapter projects only prompt, mode and quality and sends only prepared image_urls.`;
const DOC_BLOCK_MAI_2_5_PRO = maiImageDoc('mai-2.5-pro');
const DOC_BLOCK_MAI_2_6_FLASH = maiImageDoc('mai-2.6-flash');
const DOC_BLOCK_MAI_2_6 = maiImageDoc('mai-2.6');
const STATIC_DOC_BUILDER = (text: string) => () => text;

const STUDIO_MODEL_DIRECTOR_DOC_SECTIONS: Record<StudioModelId, DirectorModelDocSection | null> = {
  'flux-2-pro': 'maskless',
  seedvr2: 'maskless',
  'qwen-image-2.1': 'maskless',
  'flux-2-klein-4b': 'maskless',
  'fater-enhance': null,
  'fater-upscale': null,
  'nano-banana': 'maskless',
  'nano-banana-pro': 'maskless',
  'nano-banana-2': 'maskless',
  'nano-banana-2.1': 'maskless',
  'seedream4.5': 'maskless',
  'gpt-image-2': 'maskless',
  'gpt-image-2.5': 'maskless',
  'mai-2.5-flash': 'maskless',
  'mai-2.5-pro': 'maskless',
  'mai-2.6-flash': 'maskless',
  'mai-2.6': 'maskless',
  'ideogram-4-edit': 'maskless',
  'ideogram-inpaint': 'inpainting',
  'ideogram-3': 'maskless',
  'qwen-image-2-pro': 'maskless',
  'qwen-image-3-pro': 'maskless',
  'qwen-edit-multiangle': 'maskless',
  'grok-imagine-image': null,
  'grok-imagine-2': 'maskless',
  'muse-image': 'maskless',
  'flux-3-image': 'maskless',
  'flux-3-klein': 'maskless',
  'kling-3': 'video',
  'kling-3-omni': 'video',
  'seedance-2.0': 'video',
  'seedance-2.0-fast': 'video',
  'seedance-2.5': 'video',
  'flux-3': 'video',
  'flux-3-draft': 'video',
  'veo-3.1-fast': 'video',
  'kling-2.5-turbo': 'video',
  'ltx-2.3-fast': 'video',
  'luma-ray-2-flash': 'video',
  'minimax-h3-max': 'video',
  'minimax-h3-max-turbo': 'video',
  'wan-3.0-prime': 'video',
  'grok-imagine-video-1.5': 'video',
  'kling-3-turbo': 'video',
  'flux-3-max': 'video',
  'wan-3.0': 'video',
  'ltx-2.5-fast': 'video',
  'luma-ray-3.2': 'video',
  'gemini-omni-flash-preview': 'video',
  'hailuo-02-fast': 'video',
  'wan-2.2-i2v': 'video',
  'wan-2.2-fast': 'video',
  'topaz-video-upscale': 'video',
  'mirelo-sfx-1.6': 'video',
  'magnific-video-upscale': 'video',
  'topaz-image-enhance': 'maskless',
  'magnific-image-upscale': 'maskless',
  'recraft-vectorize': null,
  'arrow-2': 'maskless',
  'arrow-2-telos': 'maskless',
  'arrow-2-vectorize': null,
  'ideogram-vector-1': 'maskless',
  'ideogram-vector-1-vectorize': null,
  'recraft-v4-1-vector': 'maskless',
  'recraft-v4-1-vector-pro': 'maskless',
};

const STUDIO_MODEL_DIRECTOR_DOC_BUILDERS: Record<StudioModelId, (isList?: boolean) => string> = {
  'flux-2-pro': DOC_BLOCK_FLUX_2_PRO,
  seedvr2: DOC_BLOCK_SEEDVR2,
  'qwen-image-2.1': STATIC_DOC_BUILDER(DOC_BLOCK_QWEN_IMAGE_2_1),
  'flux-2-klein-4b': STATIC_DOC_BUILDER(DOC_BLOCK_FLUX_KLEIN),
  'fater-enhance': EMPTY_DOC_BUILDER,
  'fater-upscale': EMPTY_DOC_BUILDER,
  'mai-2.5-pro': STATIC_DOC_BUILDER(DOC_BLOCK_MAI_2_5_PRO),
  'mai-2.6-flash': STATIC_DOC_BUILDER(DOC_BLOCK_MAI_2_6_FLASH),
  'mai-2.6': STATIC_DOC_BUILDER(DOC_BLOCK_MAI_2_6),
  'nano-banana': STATIC_DOC_BUILDER(DOC_BLOCK_NANO_BANANA),
  'nano-banana-pro': DOC_BLOCK_NANO_BANANA_PRO,
  'nano-banana-2': DOC_BLOCK_NANO_BANANA_2,
  'nano-banana-2.1': DOC_BLOCK_NANO_BANANA_21,
  'seedream4.5': DOC_BLOCK_SEEDREAM_45,
  'gpt-image-2': DOC_BLOCK_GPT_IMAGE_2,
  'gpt-image-2.5': DOC_BLOCK_GPT_IMAGE_25,
  'mai-2.5-flash': DOC_BLOCK_MAI_2_5_FLASH,
  'ideogram-4-edit': DOC_BLOCK_IDEOGRAM_4_EDIT,
  'ideogram-inpaint': DOC_BLOCK_IDEOGRAM_INPAINT,
  'ideogram-3': DOC_BLOCK_IDEOGRAM_3,
  'qwen-image-2-pro': DOC_BLOCK_QWEN_IMAGE_2_PRO,
  'qwen-image-3-pro': DOC_BLOCK_QWEN_IMAGE_3_PRO,
  'qwen-edit-multiangle': DOC_BLOCK_QWEN_EDIT_MULTIANGLE,
  'grok-imagine-image': EMPTY_DOC_BUILDER,
  'grok-imagine-2': DOC_BLOCK_GROK_IMAGINE_2,
  'muse-image': DOC_BLOCK_MUSE_IMAGE,
  'flux-3-image': DOC_BLOCK_FLUX_3_IMAGE,
  'flux-3-klein': DOC_BLOCK_FLUX_3_KLEIN,
  'kling-3': DOC_BLOCK_KLING_3,
  'kling-3-omni': DOC_BLOCK_KLING_3_OMNI,
  'seedance-2.0': DOC_BLOCK_SEEDANCE_2,
  'seedance-2.0-fast': DOC_BLOCK_SEEDANCE_2_FAST,
  'seedance-2.5': DOC_BLOCK_SEEDANCE_2_5,
  'flux-3': DOC_BLOCK_FLUX_3,
  'flux-3-draft': DOC_BLOCK_FLUX_3_DRAFT,
  'veo-3.1-fast': DOC_BLOCK_VEO_3_1_FAST,
  'kling-2.5-turbo': DOC_BLOCK_KLING_2_5_TURBO,
  'ltx-2.3-fast': DOC_BLOCK_LTX_2_3_FAST,
  'luma-ray-2-flash': DOC_BLOCK_LUMA_RAY_2_FLASH,
  'minimax-h3-max': (isList = false) => DOC_BLOCK_LATEST_VIDEO('minimax-h3-max', isList),
  'minimax-h3-max-turbo': (isList = false) => DOC_BLOCK_LATEST_VIDEO('minimax-h3-max-turbo', isList),
  'wan-3.0-prime': (isList = false) => DOC_BLOCK_LATEST_VIDEO('wan-3.0-prime', isList),
  'grok-imagine-video-1.5': (isList = false) => DOC_BLOCK_LATEST_VIDEO('grok-imagine-video-1.5', isList),
  'kling-3-turbo': (isList = false) => DOC_BLOCK_LATEST_VIDEO('kling-3-turbo', isList),
  'flux-3-max': (isList = false) => DOC_BLOCK_LATEST_VIDEO('flux-3-max', isList),
  'wan-3.0': (isList = false) => DOC_BLOCK_LATEST_VIDEO('wan-3.0', isList),
  'ltx-2.5-fast': (isList = false) => DOC_BLOCK_LATEST_VIDEO('ltx-2.5-fast', isList),
  'luma-ray-3.2': (isList = false) => DOC_BLOCK_LATEST_VIDEO('luma-ray-3.2', isList),
  'gemini-omni-flash-preview': DOC_BLOCK_GEMINI_OMNI_FLASH,
  'hailuo-02-fast': DOC_BLOCK_HAILUO_02_FAST,
  'wan-2.2-i2v': DOC_BLOCK_WAN_2_2_I2V,
  'wan-2.2-fast': DOC_BLOCK_WAN_2_2_FAST,
  'topaz-video-upscale': DOC_BLOCK_TOPAZ_VIDEO_UPSCALE,
  'mirelo-sfx-1.6': DOC_BLOCK_MIRELO_SFX,
  'magnific-video-upscale': DOC_BLOCK_MAGNIFIC_VIDEO_UPSCALE,
  'topaz-image-enhance': DOC_BLOCK_TOPAZ_IMAGE_ENHANCE,
  'magnific-image-upscale': DOC_BLOCK_MAGNIFIC_IMAGE_UPSCALE,
  'recraft-vectorize': EMPTY_DOC_BUILDER,
  'recraft-v4-1-vector': STATIC_DOC_BUILDER(DOC_BLOCK_RECRAFT_V4_1_VECTOR),
  'recraft-v4-1-vector-pro': STATIC_DOC_BUILDER(DOC_BLOCK_RECRAFT_V4_1_VECTOR_PRO),
  'arrow-2': STATIC_DOC_BUILDER(DOC_BLOCK_ARROW_2),
  'arrow-2-telos': STATIC_DOC_BUILDER(DOC_BLOCK_ARROW_2_TELOS),
  'arrow-2-vectorize': EMPTY_DOC_BUILDER,
  'ideogram-vector-1': STATIC_DOC_BUILDER(DOC_BLOCK_IDEOGRAM_VECTOR_1),
  'ideogram-vector-1-vectorize': EMPTY_DOC_BUILDER,
};

const DOC_ORDER_BY_SECTION: Record<DirectorModelDocSection, StudioModelId[]> = {
  inpainting: ['ideogram-inpaint'],
  maskless: [
    'arrow-2',
    'arrow-2-telos',
    'ideogram-vector-1',
    'recraft-v4-1-vector',
    'recraft-v4-1-vector-pro',
    'flux-2-pro',
    'flux-2-klein-4b',
    'seedvr2',
    'nano-banana',
    'nano-banana-pro',
    'nano-banana-2',
    'nano-banana-2.1',
    'seedream4.5',
    'gpt-image-2',
    'gpt-image-2.5',
    'mai-2.5-flash',
    'mai-2.5-pro',
    'mai-2.6-flash',
    'mai-2.6',
    'ideogram-4-edit',
    'ideogram-3',
    'qwen-edit-multiangle',
    'qwen-image-2-pro',
    'qwen-image-3-pro',
    'qwen-image-2.1',
    'grok-imagine-2',
    'muse-image',
    'flux-3-image',
    'flux-3-klein',
    'topaz-image-enhance',
    'magnific-image-upscale',
  ],
  video: [
    'kling-3',
    'kling-3-omni',
    'seedance-2.5',
    'flux-3-max',
    'wan-3.0',
    'ltx-2.5-fast',
    'luma-ray-3.2',
    'flux-3',
    'flux-3-draft',
    'seedance-2.0',
    'seedance-2.0-fast',
    'veo-3.1-fast',
    'kling-2.5-turbo',
    'ltx-2.3-fast',
    'luma-ray-2-flash',
    'minimax-h3-max',
    'minimax-h3-max-turbo',
    'wan-3.0-prime',
    'grok-imagine-video-1.5',
    'kling-3-turbo',
    'gemini-omni-flash-preview',
    'hailuo-02-fast',
    'wan-2.2-i2v',
    'wan-2.2-fast',
    'topaz-video-upscale',
    'mirelo-sfx-1.6',
    'magnific-video-upscale',
  ],
};

export const getDirectorModelDocumentationForModel = (modelId: string, isList = false): string => {
  const builder = STUDIO_MODEL_DIRECTOR_DOC_BUILDERS[modelId as StudioModelId];
  if (!builder) return '';
  return builder(isList);
};

const buildDirectorModelDocChapter = (
  section: DirectorModelDocSection,
  preamble: string,
  activeIdsSet: Set<string>, isList = false,
): string => {
  const orderedIds = DOC_ORDER_BY_SECTION[section] || [];
  const modelBlocks = orderedIds
    .filter((modelId) => activeIdsSet.has(modelId))
    .map((modelId) => getDirectorModelDocumentationForModel(modelId, isList))
    .filter((content) => typeof content === 'string' && content.trim().length > 0);

  if (modelBlocks.length === 0) return '';

  return `${preamble}\n\n${modelBlocks.join('\n\n')}`;
};

export const buildDirectorModelDocumentationChapters = (
  activeSelectableModelIds?: string[] | null, isList = false,
): DirectorModelDocumentationChapters => {
  const fallbackIds = STUDIO_MODELS_VISIBLE_IN_SELECTOR;
  const sourceIds = Array.isArray(activeSelectableModelIds)
    ? activeSelectableModelIds
    : fallbackIds;

  const activeIdsSet = new Set(
    sourceIds
      .map((id) => id.trim())
      .filter((id) => {
        const modelId = id as StudioModelId;
        const section = STUDIO_MODEL_DIRECTOR_DOC_SECTIONS[modelId];
        return Boolean(section);
      }),
  );

  return {
    inpainting: buildDirectorModelDocChapter('inpainting', DIRECTOR_MODEL_DOC_INPAINTING_PREAMBLE, activeIdsSet),
    maskless: buildDirectorModelDocChapter('maskless', isList ? 'Image generation and editing models. Inputs are explicit source images and authorized references.' : DIRECTOR_MODEL_DOC_MASKLESS_PREAMBLE, activeIdsSet, isList),
    video: buildDirectorModelDocChapter('video', isList ? 'Video models. Image-to-video uses an explicit sourceImageUrl; text-to-video has no image source.' : DIRECTOR_MODEL_DOC_VIDEO_PREAMBLE, activeIdsSet, isList),
  };
};
